import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.dashboard');
import { ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import Chart from 'chart.js/auto';
import { Service } from "src/libs/portal/season/service";
import { AlertTrackingScope, AvailabilityStatus, CropRegionContext, CroppedRegion, ImageAnalysisStatus, ImageSearchRequest, ImageSearchTarget, ImageSize, Listing, ListingFreshness, ListingFreshnessStatus, ListingOption, ListingProductUrlSource, Platform, PriceAlert, PriceAlertChannel, PriceAlertType, PriceSnapshot, PriceSnapshotRangeSummary, ProductCluster, ProductClusterType, ProductSearchIssueCode, ProductSearchRecoveryAction, ProductSearchUiState, SelectedRegionCrop, SelectedRegionCropBasis, Seller, SourceStatus, UploadImageConsentDisclosure, UserSavedItem } from "src/libs/portal/lookit/domain";
import { alertService } from "src/libs/portal/lookit/alert-service";
import { CreateCropArtifactApiResponse, cropArtifactService } from "src/libs/portal/lookit/crop-artifact-repository";
import { cropContextToApiFields, displayRegionToOriginalPixels, prepareSelectedRegionCrop } from "src/libs/portal/lookit/crop-utils";
import { priceService } from "src/libs/portal/lookit/price-service";
import {
    getPublicPriceBreakdown,
    getDailyLowestEffectivePriceSnapshots,
    normalizeListingFreshness,
    PublicPriceBreakdown
} from "src/libs/portal/lookit/price-utils";
import { LowestOfferCandidate, LowestOfferResult, lowestOfferService } from "src/libs/portal/lookit/lowest-offer-service";
import { productService } from "src/libs/portal/lookit/product-service";
import { DetectedSearchItem, ImageSearchRequestSession, InitialSearchRecommendation, LookitItemCategoryId, searchService } from "src/libs/portal/lookit/search-service";
import { imageUploadService } from "src/libs/portal/lookit/upload-service";
import { UploadOriginalImageResponse } from "src/libs/portal/lookit/upload-storage";
import { savedItemService } from "src/libs/portal/lookit/saved-item-service";
import { anonymousSessionService } from "src/libs/portal/lookit/anonymous-session";
import { defaultRegionForCategory as defaultDisplayRegionForCategory, lookitFullImageRegion } from "src/libs/portal/lookit/category-default-regions";

type LookitTabId = 'search' | 'saved' | 'alerts' | 'profile';
type UploadSource = 'camera' | 'album' | 'capture';
type LookitFlowStep = 'upload' | 'select' | 'results' | 'priceCompare';
type ResultSectionId = 'exact' | 'wholesale' | 'similar';
type ResultMatchType = 'exact' | 'wholesale' | 'similar';
type PriceTrendWindow = 30 | 60 | 90;
type AlertConditionId = 'saved_price_drop' | 'drop_5' | 'drop_10' | 'target_price' | 'lowest_30d' | 'lowest_90d' | 'restock';
type AlertEditorMode = 'price' | 'restock';
type AlertCategory = 'price' | 'restock';
type SortMode = 'recommended' | 'effective_price_asc' | 'match_confidence_desc' | 'recent_checked_desc';
type FilterSheetContext = 'results' | 'listings';
type ResultScopeFilterId = 'exactOnly' | 'includeWholesale' | 'includeSimilar';
type MarketFilterId = 'inStockOnly' | 'freeShippingOnly' | 'publicCouponOnly';
type SelectionRegionSource = 'none' | 'category_default' | 'candidate' | 'manual' | 'adjusted' | 'full_image';
type SelectionRegionHandle = 'nw' | 'ne' | 'sw' | 'se';

interface SelectionPointerState {
    pointerId: number;
    mode: 'move' | 'resize';
    handle: SelectionRegionHandle | null;
    startX: number;
    startY: number;
    startRegion: CroppedRegion;
    frameWidth: number;
    frameHeight: number;
}

interface DecodedPreviewImage {
    source: CanvasImageSource;
    width: number;
    height: number;
    close?: () => void;
}

interface PreparedPreviewImage {
    url: string;
    fileSize: number;
    imageSize: ImageSize | null;
    optimized: boolean;
}

interface LookitTab {
    id: LookitTabId;
    label: string;
    icon: string;
    badge?: string | number;
}

interface HomeEntry {
    label: string;
    title: string;
    meta: string;
    image: string;
    priceLabel: string;
    targetTab: LookitTabId;
}

interface ResultProduct {
    clusterId: string;
    image: string;
    imageAlt: string;
    title: string;
    brand: string;
    matchType: ResultMatchType;
    matchLabel: string;
    confidence: number;
    effectivePrice: number | string;
    listedPrice: number | string;
    storeCount: number;
    shippingIncluded: boolean;
    couponApplied: boolean;
    lowest: boolean;
    statusLabel: string;
    checkedAt: string;
    freshnessStatus: ListingFreshnessStatus | '';
    freshnessLabel: string;
    freshnessMetaLabel: string;
    freeShippingCondition: string;
    labels: string[];
    sourceStatus: SourceStatus;
    sourceLabel: string;
    sourceMetaLabel: string;
    priceLabel: string;
    actionLabel: string;
    saved: boolean;
}

interface ResultSection {
    id: ResultSectionId;
    title: string;
    subtitle: string;
    meta: string;
    clusterType: ProductClusterType;
    secondary: boolean;
    products: ResultProduct[];
    emptyTitle: string;
    emptyMessage: string;
    emptyActionId: ProductSearchRecoveryAction | null;
    emptyActionLabel: string;
}

interface RecoveryAction {
    id: ProductSearchRecoveryAction;
    label: string;
    description: string;
}

interface SearchNotice {
    code: ProductSearchIssueCode;
    title: string;
    message: string;
    actions: RecoveryAction[];
}

interface PriceCompareRow {
    id: string;
    title: string;
    sellerName: string;
    platformName: string;
    sellerPlatformLabel: string;
    sourceLabel: string;
    sourceMetaLabel: string;
    productUrl: string;
    linkButtonLabel: string;
    linkAriaLabel: string;
    linkSourceLabel: string;
    listedPriceLabel: string;
    instantDiscountLabel: string;
    publicCouponLabel: string;
    shippingFeeLabel: string;
    optionPriceDeltaLabel: string;
    freeShippingConditionLabel: string;
    effectivePrice: number;
    effectivePriceLabel: string;
    availabilityLabel: string;
    optionAvailabilityLabel: string;
    optionRows: ListingOptionStockRow[];
    checkedAtLabel: string;
    collectedAtLabel: string;
    nextRefreshLabel: string;
    freshnessStatus: ListingFreshnessStatus;
    freshnessLabel: string;
    freshnessMetaLabel: string;
    failureReasonLabel: string;
    publicBenefitSummary: string;
    publicBenefitLabels: string[];
    referenceBenefitSummary: string;
    referenceBenefitLabels: string[];
    comparisonLabels: string[];
    excludedFromLowest: boolean;
    isLowest: boolean;
    priceBasisLabel: string;
}

interface ListingOptionStockRow {
    id: string;
    listingId: string;
    color: string;
    size: string;
    label: string;
    availabilityStatus: AvailabilityStatus;
    statusLabel: string;
    stockLabel: string;
    restockExpectedLabel: string;
    canRequestRestock: boolean;
    restockEnabled: boolean;
    restockButtonLabel: string;
}

interface PriceCompareSummary {
    listingCountLabel: string;
    sellerPlatformLabel: string;
    lowestEffectivePriceLabel: string;
    listedPriceLabel: string;
    publicDiscountLabel: string;
    shippingLabel: string;
    freeShippingConditionLabel: string;
    stockLabel: string;
    checkedAtLabel: string;
    freshnessLabel: string;
    freshnessMetaLabel: string;
    referencePolicyLabel: string;
    labels: string[];
}

interface PriceTrendPoint {
    dateKey: string;
    label: string;
    price: number | null;
    sourceCount: number;
    checkedAt: string | null;
    collectedAt: string | null;
    isMissing: boolean;
    isRecentLowest: boolean;
}

interface PriceTrendSummary {
    windowLabel: string;
    currentLowestLabel: string;
    recentLowestLabel: string;
    latestCheckedLabel: string;
    missingLabel: string;
    hasData: boolean;
}

interface PriceDistributionRow {
    id: string;
    sellerPlatformLabel: string;
    priceLabel: string;
    widthPercent: string;
    isLowest: boolean;
}

interface SortOption {
    id: SortMode;
    label: string;
}

interface FilterChip {
    id: string;
    label: string;
}

interface ItemCategoryOption {
    id: LookitItemCategoryId;
    label: string;
    description: string;
}

interface SearchTargetSummary {
    id: string;
    categoryId: string;
    label: string;
    meta: string;
    active: boolean;
}

interface ResultFilterState {
    exactOnly: boolean;
    includeWholesale: boolean;
    includeSimilar: boolean;
    inStockOnly: boolean;
    freeShippingOnly: boolean;
    publicCouponOnly: boolean;
    platformIds: string[];
}

type SavedPriceChangeTone = 'drop' | 'rise' | 'same' | 'unknown';

interface SavedProductCard {
    id: string;
    clusterId: string;
    listingId: string | null;
    listingOptionId: string | null;
    image: string;
    imageAlt: string;
    title: string;
    brand: string;
    saveScopeLabel: string;
    savedPriceLabel: string;
    currentLowestPriceLabel: string;
    priceChangeAmountLabel: string;
    priceChangePercentLabel: string;
    priceChangeTone: SavedPriceChangeTone;
    storeCountLabel: string;
    alertStatusLabel: string;
    alertConditionLabel: string;
    alertEnabled: boolean;
    savedAtLabel: string;
    currentLowestSellerLabel: string;
    optionLabel: string;
}

interface AlertConditionOption {
    id: AlertConditionId;
    label: string;
    description: string;
    alertType: PriceAlertType;
    thresholdPercent: number | null;
    requiresTargetPrice: boolean;
}

interface AlertChannelOption {
    id: PriceAlertChannel;
    label: string;
    description: string;
}

interface AlertEditorState {
    mode: AlertEditorMode;
    savedItemId: string;
    existingAlertId: string | null;
    clusterId: string;
    listingId: string | null;
    listingOptionId: string | null;
    image: string;
    imageAlt: string;
    title: string;
    brand: string;
    contextLabel: string;
    alertCategoryLabel: string;
    basisText: string;
    baselineTitle: string;
    currentTitle: string;
    savedPrice: number;
    savedPriceLabel: string;
    currentPrice: number | null;
    currentPriceLabel: string;
    targetColor: string | null;
    targetSize: string | null;
    optionStatusLabel: string;
    conditionId: AlertConditionId;
    targetPriceInput: string;
    channels: PriceAlertChannel[];
    error: string;
}

interface AlertListItem {
    id: string;
    category: AlertCategory;
    clusterId: string;
    listingId: string | null;
    listingOptionId: string | null;
    image: string;
    imageAlt: string;
    title: string;
    brand: string;
    scopeLabel: string;
    conditionLabel: string;
    categoryLabel: string;
    thresholdLabel: string;
    baselineTitle: string;
    savedPriceLabel: string;
    currentTitle: string;
    currentPriceLabel: string;
    channelsLabel: string;
    basisLabel: string;
    statusLabel: string;
    enabled: boolean;
    lastSentLabel: string;
}

@Component({
    selector: 'wiz-page-dashboard',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.dashboard/view.scss */
:host {
  display: block;
  height: 100%;
}

.lookit-file-input {
  position: fixed;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.lookit-home {
  display: flex;
  flex-direction: column;
  gap: var(--lookit-space-3);
}

.lookit-home__intro {
  display: flex;
  flex-direction: column;
  gap: var(--lookit-space-1);
  padding: var(--lookit-space-1) 2px var(--lookit-space-2);
}

.lookit-home__eyebrow,
.lookit-memory__eyebrow {
  margin: 0;
  color: var(--lookit-color-accent-strong);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.25;
}

.lookit-home__intro h1,
.lookit-stage-header h1 {
  margin: 0;
  color: var(--lookit-color-text);
  font-size: 30px;
  font-weight: var(--lookit-font-weight-black);
  letter-spacing: 0;
  line-height: 1.08;
}

.lookit-home__copy {
  margin: 0;
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.45;
}

.lookit-upload-card {
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface-raised);
  box-shadow: var(--lookit-shadow-card);
}

.lookit-upload-card__button {
  display: grid;
  width: 100%;
  min-height: 132px;
  grid-template-columns: 62px minmax(0, 1fr);
  gap: var(--lookit-space-4);
  align-items: center;
  border-radius: var(--lookit-radius-md);
  padding: var(--lookit-space-4);
  text-align: left;
}

.lookit-upload-card__button:active {
  transform: scale(0.99);
}

.lookit-upload-card__mark {
  position: relative;
  display: grid;
  width: 62px;
  height: 62px;
  place-items: center;
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface-muted);
}

.lookit-upload-card__mark::before,
.lookit-upload-card__mark::after {
  position: absolute;
  content: "";
  border-radius: var(--lookit-radius-pill);
  background: var(--lookit-color-text);
}

.lookit-upload-card__mark::before {
  width: 28px;
  height: 2px;
}

.lookit-upload-card__mark::after {
  width: 2px;
  height: 28px;
}

.lookit-upload-card__body {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
}

.lookit-upload-card__body strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xl);
  font-weight: var(--lookit-font-weight-black);
  letter-spacing: 0;
  line-height: 1.15;
}

.lookit-upload-card__body small {
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.4;
}

.lookit-upload-card__file {
  margin: 0;
  overflow: hidden;
  border-top: 1px solid var(--lookit-border-subtle);
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.35;
  padding: var(--lookit-space-2) var(--lookit-space-4);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-upload-card__error {
  margin: 0;
  border-top: 1px solid var(--lookit-border-subtle);
  color: var(--lookit-color-price-drop);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.4;
  overflow-wrap: anywhere;
  padding: var(--lookit-space-2) var(--lookit-space-4);
}

.lookit-upload-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--lookit-space-2);
}

.lookit-privacy-note {
  margin: 0;
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.45;
}

.lookit-privacy-disclosure {
  min-width: 0;
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface-muted);
}

.lookit-privacy-disclosure summary {
  display: flex;
  min-height: 38px;
  cursor: pointer;
  list-style: none;
  align-items: center;
  justify-content: space-between;
  gap: var(--lookit-space-2);
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
  padding: 0 10px;
}

.lookit-privacy-disclosure summary::-webkit-details-marker {
  display: none;
}

.lookit-privacy-disclosure summary::after {
  width: 7px;
  height: 7px;
  flex: 0 0 auto;
  border-right: 2px solid var(--lookit-color-text-muted);
  border-bottom: 2px solid var(--lookit-color-text-muted);
  content: "";
  transform: rotate(45deg);
}

.lookit-privacy-disclosure[open] summary::after {
  transform: rotate(225deg);
}

.lookit-privacy-disclosure__body {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
  border-top: 1px solid var(--lookit-border-subtle);
  padding: 9px 10px 10px;
}

.lookit-privacy-disclosure__body p {
  margin: 0;
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.lookit-privacy-disclosure__link {
  width: max-content;
  max-width: 100%;
  color: var(--lookit-color-accent-strong);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.3;
  overflow-wrap: anywhere;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.lookit-selection,
.lookit-results,
.lookit-price-compare {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-3);
}

.lookit-upload-fallback {
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--lookit-space-2);
  align-items: center;
  border: 1px solid color-mix(in srgb, var(--lookit-color-price-drop) 30%, var(--lookit-border-subtle));
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-price-drop-soft);
  padding: var(--lookit-space-2);
}

.lookit-upload-fallback div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.lookit-upload-fallback strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-upload-fallback p {
  margin: 0;
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.lookit-upload-fallback button {
  min-height: 36px;
  border: 1px solid var(--lookit-color-price-drop);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface);
  color: var(--lookit-color-price-drop);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
  padding: 7px 9px;
  white-space: nowrap;
}

.lookit-stage-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--lookit-space-3);
  padding: var(--lookit-space-1) 2px var(--lookit-space-2);
}

.lookit-stage-header > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-1);
}

.lookit-stage-header__link {
  flex: 0 0 auto;
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.lookit-preview-shell {
  width: 100%;
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface-muted);
}

.lookit-preview-frame {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: var(--lookit-color-surface-muted);
  overscroll-behavior: contain;
  user-select: none;
  -webkit-user-select: none;
}

.lookit-preview-image {
  display: block;
  width: 100%;
  max-height: min(58vh, 430px);
  image-orientation: from-image;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
}

.lookit-selected-region {
  position: absolute;
  z-index: 3;
  border: 2px solid color-mix(in srgb, var(--lookit-color-text) 68%, transparent);
  border-radius: var(--lookit-radius-sm);
  background: color-mix(in srgb, var(--lookit-color-surface) 8%, transparent);
  box-shadow: 0 0 0 999px color-mix(in srgb, var(--lookit-color-text) 14%, transparent), 0 2px 10px color-mix(in srgb, var(--lookit-color-text) 18%, transparent);
  cursor: move;
  touch-action: none;
  transition: left 160ms cubic-bezier(0.22, 1, 0.36, 1), top 160ms cubic-bezier(0.22, 1, 0.36, 1), width 160ms cubic-bezier(0.22, 1, 0.36, 1), height 160ms cubic-bezier(0.22, 1, 0.36, 1), transform 160ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 160ms ease-out;
  user-select: none;
  -webkit-user-select: none;
  will-change: left, top, width, height, transform;
}

.lookit-selected-region.is-dragging {
  transition: none;
  user-select: none;
  -webkit-user-select: none;
  will-change: transform, width, height;
}

.lookit-selected-region__label {
  position: absolute;
  top: 6px;
  left: 6px;
  display: flex;
  max-width: calc(100% - 12px);
  min-width: 0;
  flex-direction: column;
  gap: 2px;
  pointer-events: none;
}

.lookit-selected-region__label b,
.lookit-selected-region__label small {
  display: block;
  max-width: 100%;
  overflow: hidden;
  border-radius: var(--lookit-radius-sm);
  background: color-mix(in srgb, var(--lookit-color-text) 84%, transparent);
  color: var(--lookit-color-surface);
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-selected-region__label b {
  padding: 3px 6px;
  font-size: var(--lookit-font-size-2xs);
  font-weight: var(--lookit-font-weight-black);
}

.lookit-selected-region__label small {
  width: fit-content;
  padding: 2px 5px;
  font-size: 10px;
  font-weight: var(--lookit-font-weight-bold);
}

.lookit-region-handle {
  position: absolute;
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: var(--lookit-radius-pill);
  background: transparent;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

.lookit-region-handle::after {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 14px;
  height: 14px;
  border: 1px solid var(--lookit-color-surface);
  border-radius: var(--lookit-radius-pill);
  background: var(--lookit-color-price-drop);
  box-shadow: 0 1px 4px color-mix(in srgb, var(--lookit-color-text) 26%, transparent);
  content: "";
  transform: translate(-50%, -50%);
}

.lookit-region-handle.is-nw {
  top: -20px;
  left: -20px;
  cursor: nwse-resize;
}

.lookit-region-handle.is-ne {
  top: -20px;
  right: -20px;
  cursor: nesw-resize;
}

.lookit-region-handle.is-sw {
  bottom: -20px;
  left: -20px;
  cursor: nesw-resize;
}

.lookit-region-handle.is-se {
  right: -20px;
  bottom: -20px;
  cursor: nwse-resize;
}

.lookit-manual-box {
  position: absolute;
  inset: 16% 14% 18%;
  z-index: 2;
  display: grid;
  place-items: center;
  border: 2px dashed var(--lookit-color-price-drop);
  border-radius: var(--lookit-radius-md);
  background: color-mix(in srgb, var(--lookit-color-surface) 22%, transparent);
  color: var(--lookit-color-surface);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
}

.lookit-manual-box span {
  border-radius: var(--lookit-radius-pill);
  background: color-mix(in srgb, var(--lookit-color-text) 82%, transparent);
  padding: 6px 10px;
}

.lookit-selection-panel,
.lookit-results-source {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-3);
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface);
  padding: var(--lookit-space-3);
}

.lookit-selection-panel {
  gap: var(--lookit-space-2);
}

.lookit-selection-panel__summary {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}

.lookit-selection-panel__summary small,
.lookit-results-source__copy small {
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-2xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.25;
}

.lookit-selection-panel__summary strong,
.lookit-results-source__copy strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-md);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.lookit-selection-panel__summary p,
.lookit-results-source__copy p {
  margin: 0;
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.45;
}

.lookit-selection-panel__context {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--lookit-space-2);
  margin: 0;
}

.lookit-selection-panel__context div {
  min-width: 0;
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-surface-muted);
  padding: 8px;
}

.lookit-selection-panel__context dt {
  margin: 0 0 3px;
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
}

.lookit-selection-panel__context dd {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-2xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-category-select {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-2);
}

.lookit-category-select > strong,
.lookit-search-targets > strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.25;
}

.lookit-category-select__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.lookit-category-select__grid button {
  display: grid;
  min-width: 0;
  min-height: 38px;
  place-items: center;
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-pill);
  background: var(--lookit-color-surface);
  padding: 7px 10px;
  text-align: center;
}

.lookit-category-select__grid button.is-selected {
  border-color: var(--lookit-color-text);
  background: var(--lookit-color-text);
  color: var(--lookit-color-text);
}

.lookit-category-select__grid span {
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-category-select__grid span {
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
}

.lookit-category-select__grid button.is-selected span {
  color: var(--lookit-color-surface);
}

.lookit-crop-preview {
  display: grid;
  min-width: 0;
  grid-template-columns: 64px minmax(0, 1fr);
  gap: var(--lookit-space-2);
  align-items: center;
}

.lookit-crop-preview__image {
  width: 64px;
  aspect-ratio: 1/1;
  overflow: hidden;
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-surface-muted);
  image-orientation: from-image;
  object-fit: cover;
}

.lookit-crop-preview__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}

.lookit-crop-preview__copy small {
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-2xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-crop-preview__copy strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.lookit-search-targets {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-2);
}

.lookit-search-targets__list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--lookit-space-2);
}

.lookit-search-targets__list button {
  display: flex;
  min-width: 0;
  min-height: 54px;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface-muted);
  padding: 8px;
  text-align: left;
}

.lookit-search-targets__list button.is-active {
  border-color: var(--lookit-color-accent-strong);
  background: var(--lookit-color-accent-soft);
}

.lookit-search-targets__list span,
.lookit-search-targets__list small {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-search-targets__list span {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
}

.lookit-search-targets__list small {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-bold);
}

.lookit-whole-photo-option {
  min-width: 0;
}

.lookit-whole-photo-option button {
  display: flex;
  width: 100%;
  min-height: 48px;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
  border: 1px dashed var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface-muted);
  padding: 8px 10px;
  text-align: left;
}

.lookit-whole-photo-option button.is-selected {
  border-style: solid;
  border-color: var(--lookit-color-price-drop);
  background: var(--lookit-color-price-drop-soft);
}

.lookit-whole-photo-option span,
.lookit-whole-photo-option small {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-whole-photo-option span {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
}

.lookit-whole-photo-option small {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-bold);
}

.lookit-candidate-drawer {
  min-width: 0;
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface-muted);
  padding: 0;
}

.lookit-candidate-drawer summary {
  display: flex;
  min-height: 42px;
  cursor: pointer;
  list-style: none;
  align-items: center;
  justify-content: space-between;
  gap: var(--lookit-space-2);
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
  padding: 0 10px;
}

.lookit-candidate-drawer summary::-webkit-details-marker {
  display: none;
}

.lookit-candidate-drawer summary::after {
  content: "";
  width: 7px;
  height: 7px;
  flex: 0 0 auto;
  border-right: 2px solid var(--lookit-color-text-muted);
  border-bottom: 2px solid var(--lookit-color-text-muted);
  transform: rotate(45deg);
}

.lookit-candidate-drawer[open] summary::after {
  transform: rotate(225deg);
}

.lookit-candidate-drawer summary small {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
}

.lookit-candidate-drawer .lookit-selection-panel__items {
  border-top: 1px solid var(--lookit-border-subtle);
  padding: var(--lookit-space-2);
}

.lookit-selection-panel__items {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--lookit-space-2);
}

.lookit-detected-chip {
  display: flex;
  min-width: 0;
  min-height: 58px;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  padding: 8px;
  text-align: left;
}

.lookit-detected-chip span,
.lookit-detected-chip small {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-detected-chip span {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
}

.lookit-detected-chip small {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-bold);
}

.lookit-detected-chip.is-selected {
  border-color: var(--lookit-color-accent-strong);
  background: var(--lookit-color-surface-muted);
}

.lookit-manual-guide {
  margin: 0;
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-alice-blue);
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.35;
  padding: 9px 10px;
}

.lookit-region-controls {
  min-width: 0;
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface-muted);
}

.lookit-region-controls summary {
  display: flex;
  min-height: 40px;
  cursor: pointer;
  list-style: none;
  align-items: center;
  justify-content: space-between;
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
  padding: 0 10px;
}

.lookit-region-controls summary::-webkit-details-marker {
  display: none;
}

.lookit-region-controls summary::after {
  width: 7px;
  height: 7px;
  flex: 0 0 auto;
  border-right: 2px solid var(--lookit-color-text-muted);
  border-bottom: 2px solid var(--lookit-color-text-muted);
  content: "";
  transform: rotate(45deg);
}

.lookit-region-controls[open] summary::after {
  transform: rotate(225deg);
}

.lookit-region-controls__grid {
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--lookit-space-2);
  border-top: 1px solid var(--lookit-border-subtle);
  padding: var(--lookit-space-2);
}

.lookit-region-controls__grid button {
  min-width: 0;
  min-height: 38px;
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-surface);
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
  overflow-wrap: anywhere;
  padding: 7px 8px;
}

.lookit-region-controls__grid button:last-child {
  grid-column: 1/-1;
}

.lookit-selection-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--lookit-space-2);
}

.lookit-recovery-panel,
.lookit-result-notice {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-3);
  border: 1px solid color-mix(in srgb, var(--lookit-color-accent-strong) 32%, var(--lookit-border-subtle));
  border-radius: var(--lookit-radius-md);
  background: color-mix(in srgb, var(--lookit-color-accent-soft) 24%, var(--lookit-color-surface));
  padding: var(--lookit-space-3);
}

.lookit-recovery-panel strong,
.lookit-result-notice__copy strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.25;
}

.lookit-recovery-panel p,
.lookit-result-notice__copy p {
  margin: 0;
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.lookit-recovery-panel__actions,
.lookit-result-notice__actions,
.lookit-empty-actions {
  display: grid;
  min-width: 0;
  grid-template-columns: 1fr;
  gap: var(--lookit-space-2);
}

.lookit-recovery-panel__actions button,
.lookit-result-notice__actions button,
.lookit-empty-actions button {
  display: flex;
  min-width: 0;
  min-height: 44px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 3px;
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-surface);
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
  padding: 9px 10px;
  text-align: left;
}

.lookit-recovery-panel__actions button small,
.lookit-result-notice__actions button small,
.lookit-empty-actions button small {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.lookit-results-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--lookit-space-2);
}

.lookit-results-source {
  display: grid;
  grid-template-columns: 78px minmax(0, 1fr);
  align-items: center;
}

.lookit-results-source__image {
  width: 78px;
  aspect-ratio: 1/1;
  overflow: hidden;
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface-muted);
}

.lookit-results-source__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lookit-results-source__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}

.lookit-results-source__region {
  color: var(--lookit-color-text-muted) !important;
  font-size: var(--lookit-font-size-2xs) !important;
}

.lookit-results-source__badges {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 6px;
}

.lookit-results-source__badges span {
  max-width: 100%;
  overflow: hidden;
  border-radius: var(--lookit-radius-pill);
  background: var(--lookit-color-surface-muted);
  color: var(--lookit-color-text-soft);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
  padding: 5px 7px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-results-empty {
  min-width: 0;
}

.lookit-results-empty .lookit-empty-actions {
  margin-top: var(--lookit-space-3);
}

.lookit-filter-toolbar {
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--lookit-space-2);
  align-items: stretch;
}

.lookit-filter-toolbar__sort {
  display: grid;
  min-width: 0;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 8px;
  align-items: center;
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface);
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
  padding: 8px 10px;
}

.lookit-filter-toolbar__sort span {
  white-space: nowrap;
}

.lookit-filter-toolbar__sort select {
  min-width: 0;
  width: 100%;
  border: 0;
  background: transparent;
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
  outline: none;
}

.lookit-filter-toolbar__button {
  display: flex;
  min-height: 42px;
  min-width: 82px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
  padding: 6px 10px;
}

.lookit-filter-toolbar__button small {
  max-width: 76px;
  overflow: hidden;
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-bold);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-filter-toolbar__chips {
  grid-column: 1/-1;
  display: flex;
  min-width: 0;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 2px;
  scrollbar-width: none;
}

.lookit-filter-toolbar__chips::-webkit-scrollbar {
  display: none;
}

.lookit-filter-toolbar__chips span,
.lookit-filter-toolbar__chips button {
  display: inline-flex;
  flex: 0 0 auto;
  max-width: min(46vw, 180px);
  min-height: 25px;
  align-items: center;
  border-radius: var(--lookit-radius-pill);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.1;
  padding: 0 8px;
  white-space: nowrap;
}

.lookit-filter-toolbar__chips span {
  overflow: hidden;
  background: var(--lookit-color-surface-muted);
  color: var(--lookit-color-text-soft);
  text-overflow: ellipsis;
}

.lookit-filter-toolbar__chips button {
  border: 1px solid var(--lookit-border-medium);
  color: var(--lookit-color-text-muted);
}

.lookit-result-sections {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-5);
}

.lookit-result-section {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-2);
}

.lookit-result-section + .lookit-result-section {
  border-top: 1px solid var(--lookit-border-subtle);
  padding-top: var(--lookit-space-4);
}

.lookit-result-section.is-secondary {
  border-top-style: dashed;
}

.lookit-result-section__list {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-3);
}

.lookit-result-section__empty {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
  border: 1px dashed var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.45;
  padding: var(--lookit-space-3);
}

.lookit-result-section__empty strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.25;
}

.lookit-result-section__empty p {
  margin: 0;
  overflow-wrap: anywhere;
}

.lookit-result-section__empty button {
  align-self: flex-start;
  min-height: 32px;
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-surface);
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
  padding: 7px 10px;
}

.lookit-compare-hero {
  display: grid;
  min-width: 0;
  grid-template-columns: 96px minmax(0, 1fr);
  gap: var(--lookit-space-3);
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface);
  padding: var(--lookit-space-3);
}

.lookit-compare-hero__image {
  width: 96px;
  aspect-ratio: 4/5;
  overflow: hidden;
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface-muted);
}

.lookit-compare-hero__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lookit-compare-hero__body {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-2);
}

.lookit-compare-hero__body small {
  overflow: hidden;
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-compare-hero__body strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-md);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.28;
  overflow-wrap: anywhere;
}

.lookit-compare-hero__meta {
  margin: -3px 0 0;
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.lookit-detail-save {
  min-height: 38px;
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  color: var(--lookit-color-accent-strong);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
}

.lookit-detail-save.is-saved {
  border-color: var(--lookit-color-accent-strong);
  background: var(--lookit-color-accent-soft);
}

.lookit-detail-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--lookit-space-2);
}

.lookit-detail-alert {
  min-height: 38px;
  border: 1px solid var(--lookit-color-accent-strong);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-accent-strong);
  color: var(--lookit-color-on-accent);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
}

.lookit-alert-editor {
  position: fixed;
  z-index: 40;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: var(--lookit-space-3) calc(var(--lookit-space-3) + var(--lookit-safe-area-right)) calc(var(--lookit-space-3) + var(--lookit-safe-area-bottom)) calc(var(--lookit-space-3) + var(--lookit-safe-area-left));
}

.lookit-alert-editor__scrim {
  position: absolute;
  inset: 0;
  background: color-mix(in srgb, var(--lookit-color-text) 48%, transparent);
}

.lookit-alert-editor__panel {
  position: relative;
  z-index: 1;
  display: flex;
  width: min(100%, 440px);
  max-height: min(88vh, 760px);
  max-height: min(88dvh, 100dvh - var(--lookit-safe-area-top) - var(--lookit-safe-area-bottom) - var(--lookit-space-6), 760px);
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-3);
  overflow: auto;
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface);
  padding: var(--lookit-space-4);
  box-shadow: 0 18px 54px color-mix(in srgb, var(--lookit-color-text) 24%, transparent);
}

.lookit-alert-editor__head {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--lookit-space-3);
}

.lookit-alert-editor__head > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.lookit-alert-editor__head h2 {
  margin: 0;
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xl);
  font-weight: var(--lookit-font-weight-black);
  letter-spacing: 0;
  line-height: 1.12;
}

.lookit-alert-editor__head button {
  flex: 0 0 auto;
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.lookit-alert-editor__product {
  display: grid;
  min-width: 0;
  grid-template-columns: 58px minmax(0, 1fr);
  gap: var(--lookit-space-2);
  align-items: center;
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-md);
  padding: var(--lookit-space-2);
}

.lookit-alert-editor__product img {
  width: 58px;
  aspect-ratio: 4/5;
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-surface-muted);
  object-fit: cover;
}

.lookit-alert-editor__product div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.lookit-alert-editor__product small,
.lookit-alert-editor__product span {
  overflow: hidden;
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-2xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-alert-editor__product strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.28;
  overflow-wrap: anywhere;
}

.lookit-alert-editor__basis,
.lookit-alert-editor__channel-note,
.lookit-alert-editor__error {
  margin: 0;
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.45;
}

.lookit-alert-editor__basis {
  border-left: 3px solid var(--lookit-color-accent-strong);
  background: var(--lookit-color-surface-muted);
  padding: var(--lookit-space-2) var(--lookit-space-3);
}

.lookit-alert-editor__error {
  color: var(--lookit-color-price-drop);
}

.lookit-alert-editor__prices {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--lookit-space-2);
  margin: 0;
}

.lookit-alert-editor__prices div,
.lookit-alert-card__facts div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-surface-muted);
  padding: 8px;
}

.lookit-alert-editor__prices dt,
.lookit-alert-editor__prices dd,
.lookit-alert-card__facts dt,
.lookit-alert-card__facts dd {
  margin: 0;
  min-width: 0;
}

.lookit-alert-editor__prices dt,
.lookit-alert-card__facts dt {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-alert-editor__prices dd,
.lookit-alert-card__facts dd {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.lookit-alert-editor__group {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-2);
}

.lookit-alert-editor__group > strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.25;
}

.lookit-alert-editor__conditions,
.lookit-alert-editor__channels {
  display: grid;
  min-width: 0;
  gap: var(--lookit-space-2);
}

.lookit-alert-editor__conditions {
  grid-template-columns: 1fr;
}

.lookit-alert-editor__channels {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.lookit-alert-editor__conditions button,
.lookit-alert-editor__channels button {
  display: flex;
  min-width: 0;
  min-height: 52px;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  padding: 8px;
  text-align: left;
}

.lookit-alert-editor__conditions button.is-selected,
.lookit-alert-editor__channels button.is-selected {
  border-color: var(--lookit-color-accent-strong);
  background: var(--lookit-color-accent-soft);
}

.lookit-alert-editor__conditions span,
.lookit-alert-editor__channels span {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-alert-editor__conditions small,
.lookit-alert-editor__channels small {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.25;
}

.lookit-alert-editor__target {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
}

.lookit-alert-editor__target span {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-alert-editor__target input {
  width: 100%;
  min-height: var(--lookit-touch-button);
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  padding: 0 var(--lookit-space-3);
}

.lookit-alert-editor__actions {
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 0.8fr) minmax(0, 1fr);
  gap: var(--lookit-space-2);
}

.lookit-alert-editor__actions button {
  min-height: var(--lookit-touch-button);
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
  padding: 0 var(--lookit-space-2);
}

.lookit-alert-editor__remove {
  border-color: color-mix(in srgb, var(--lookit-color-price-drop) 40%, var(--lookit-border-medium)) !important;
  color: var(--lookit-color-price-drop) !important;
}

.lookit-alert-editor__save {
  border-color: var(--lookit-color-accent-strong) !important;
  background: var(--lookit-color-accent-strong);
  color: var(--lookit-color-on-accent) !important;
}

.lookit-alert-editor__save:disabled {
  opacity: 0.48;
}

.lookit-filter-sheet {
  position: fixed;
  z-index: 42;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: var(--lookit-space-3) calc(var(--lookit-space-3) + var(--lookit-safe-area-right)) calc(var(--lookit-space-3) + var(--lookit-safe-area-bottom)) calc(var(--lookit-space-3) + var(--lookit-safe-area-left));
}

.lookit-filter-sheet__scrim {
  position: absolute;
  inset: 0;
  background: color-mix(in srgb, var(--lookit-color-text) 42%, transparent);
}

.lookit-filter-sheet__panel {
  position: relative;
  z-index: 1;
  display: flex;
  width: min(100%, 440px);
  max-height: min(78vh, 640px);
  max-height: min(78dvh, 100dvh - var(--lookit-safe-area-top) - var(--lookit-safe-area-bottom) - var(--lookit-space-6), 640px);
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-3);
  overflow: auto;
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-lg) var(--lookit-radius-lg) var(--lookit-radius-md) var(--lookit-radius-md);
  background: var(--lookit-color-surface);
  box-shadow: var(--lookit-shadow-floating, 0 18px 48px rgba(15, 23, 42, 0.18));
  padding: var(--lookit-space-4);
}

.lookit-filter-sheet__head {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--lookit-space-3);
}

.lookit-filter-sheet__head div,
.lookit-filter-sheet__group {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.lookit-filter-sheet__head div {
  gap: 3px;
}

.lookit-filter-sheet__head small {
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-filter-sheet__head h2 {
  margin: 0;
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-lg);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
}

.lookit-filter-sheet__head button {
  flex: 0 0 auto;
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-filter-sheet__group {
  gap: var(--lookit-space-2);
}

.lookit-filter-sheet__group > strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-filter-sheet__buttons,
.lookit-filter-sheet__platforms {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 7px;
}

.lookit-filter-sheet__buttons button,
.lookit-filter-sheet__platforms button {
  display: inline-flex;
  max-width: 100%;
  min-height: 34px;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-pill);
  background: var(--lookit-color-surface-muted);
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.1;
  padding: 0 10px;
  white-space: nowrap;
}

.lookit-filter-sheet__buttons button.is-selected,
.lookit-filter-sheet__platforms button.is-selected {
  border-color: var(--lookit-color-accent-strong);
  background: var(--lookit-color-accent-soft);
  color: var(--lookit-color-accent-strong);
}

.lookit-filter-sheet__actions {
  display: grid;
  grid-template-columns: minmax(0, 0.82fr) minmax(0, 1fr);
  gap: var(--lookit-space-2);
  padding-top: var(--lookit-space-1);
}

.lookit-filter-sheet__actions button {
  min-height: var(--lookit-touch-button);
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
}

.lookit-filter-sheet__apply {
  border-color: var(--lookit-color-text) !important;
  background: var(--lookit-color-text);
  color: var(--lookit-color-surface) !important;
}

.lookit-lowest-summary {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-3);
  border: 1px solid color-mix(in srgb, var(--lookit-color-price-drop) 36%, var(--lookit-color-surface));
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-price-drop-soft);
  padding: var(--lookit-space-3);
}

.lookit-lowest-summary__main {
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 4px var(--lookit-space-2);
  align-items: baseline;
}

.lookit-lowest-summary__main span,
.lookit-lowest-summary__main small {
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.25;
}

.lookit-lowest-summary__main strong {
  color: var(--lookit-color-price-drop);
  font-size: 24px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.08;
  text-align: right;
  white-space: nowrap;
}

.lookit-lowest-summary__main small {
  grid-column: 1/-1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-lowest-summary__labels,
.lookit-price-row__badges {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 6px;
}

.lookit-lowest-summary__labels span,
.lookit-price-row__badge {
  display: inline-flex;
  max-width: 100%;
  min-height: 22px;
  align-items: center;
  border-radius: var(--lookit-radius-pill);
  background: var(--lookit-color-surface);
  color: var(--lookit-color-text-soft);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
  padding: 0 7px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-price-row__badge.is-accent {
  background: var(--lookit-color-accent-soft);
  color: var(--lookit-color-accent-strong);
}

.lookit-price-row__badge.is-warning {
  background: color-mix(in srgb, var(--lookit-color-lowest) 18%, var(--lookit-color-surface));
  color: var(--lookit-color-lowest);
}

.lookit-price-row__badge.is-danger {
  background: var(--lookit-color-price-drop-soft);
  color: var(--lookit-color-price-drop);
}

.lookit-lowest-summary__facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--lookit-space-2);
  margin: 0;
}

.lookit-lowest-summary__facts div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
  border-radius: var(--lookit-radius-sm);
  background: color-mix(in srgb, var(--lookit-color-surface) 80%, transparent);
  padding: 8px;
}

.lookit-lowest-summary__facts dt,
.lookit-lowest-summary__facts dd {
  margin: 0;
  min-width: 0;
}

.lookit-lowest-summary__facts dt {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-lowest-summary__facts dd {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.28;
  overflow-wrap: anywhere;
}

.lookit-lowest-summary__policy {
  margin: 0;
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.45;
}

.lookit-price-note {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
  border-left: 3px solid var(--lookit-color-accent-strong);
  background: var(--lookit-color-surface-muted);
  padding: var(--lookit-space-3);
}

.lookit-price-note strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.3;
}

.lookit-price-note p {
  margin: 0;
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.45;
}

.lookit-price-trend {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-3);
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface);
  padding: var(--lookit-space-3);
}

.lookit-price-trend__head {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--lookit-space-3);
}

.lookit-price-trend__head > div:first-child {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.lookit-price-trend__head strong,
.lookit-price-distribution__head strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-md);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-price-trend__head span,
.lookit-price-distribution__head span {
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.25;
}

.lookit-price-trend__segments {
  display: grid;
  flex: 0 0 auto;
  grid-template-columns: repeat(3, minmax(44px, 1fr));
  gap: 4px;
  border-radius: var(--lookit-radius-pill);
  background: var(--lookit-color-surface-muted, #f4f4f5);
  padding: 3px;
}

.lookit-price-trend__segments button {
  min-height: 30px;
  border-radius: var(--lookit-radius-pill);
  color: var(--lookit-color-text-soft, #52525b);
  font-size: 11px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.1;
  padding: 0 8px;
  white-space: nowrap;
}

.lookit-price-trend__segments button.is-active {
  background: var(--lookit-color-text, #111111);
  color: var(--lookit-color-surface, #ffffff);
}

.lookit-price-trend__summary {
  display: grid;
  min-width: 0;
  grid-template-columns: 1fr;
  gap: var(--lookit-space-2);
}

.lookit-price-trend__summary div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-surface-muted);
  padding: 8px;
}

.lookit-price-trend__summary small {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-price-trend__summary b {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.lookit-price-trend__canvas {
  position: relative;
  width: 100%;
  height: 218px;
  min-width: 0;
  overflow: hidden;
  border-radius: var(--lookit-radius-sm);
  background: linear-gradient(180deg, var(--lookit-color-surface, #ffffff) 0%, var(--lookit-color-surface-muted, #f4f4f5) 100%);
  padding: 4px 2px 0;
}

.lookit-price-trend__canvas canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

.lookit-price-trend__empty {
  display: flex;
  min-height: 176px;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  border: 1px dashed var(--lookit-border-medium);
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-surface-muted, #f4f4f5);
  padding: var(--lookit-space-3);
}

.lookit-price-trend__empty strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.3;
}

.lookit-price-trend__empty p,
.lookit-price-trend__missing,
.lookit-price-trend__caption {
  margin: 0;
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.45;
}

.lookit-price-trend__caption {
  color: var(--lookit-color-text-soft);
}

.lookit-price-distribution {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-2);
  border-top: 1px solid var(--lookit-border-subtle);
  padding-top: var(--lookit-space-3);
}

.lookit-price-distribution__head {
  display: flex;
  min-width: 0;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--lookit-space-2);
}

.lookit-price-distribution__rows {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-2);
}

.lookit-price-distribution__row {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
}

.lookit-price-distribution__meta {
  display: flex;
  min-width: 0;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--lookit-space-2);
}

.lookit-price-distribution__meta span,
.lookit-price-distribution__meta b {
  min-width: 0;
  overflow: hidden;
  font-size: var(--lookit-font-size-xs);
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-price-distribution__meta span {
  color: var(--lookit-color-text-soft);
  font-weight: var(--lookit-font-weight-bold);
}

.lookit-price-distribution__meta b {
  flex: 0 0 auto;
  color: var(--lookit-color-text);
  font-weight: var(--lookit-font-weight-black);
}

.lookit-price-distribution__track {
  width: 100%;
  height: 8px;
  overflow: hidden;
  border-radius: var(--lookit-radius-pill);
  background: var(--lookit-color-surface-muted, #f4f4f5);
}

.lookit-price-distribution__bar {
  height: 100%;
  min-width: 18%;
  border-radius: inherit;
  background: var(--lookit-color-accent-strong, #667c8c);
}

.lookit-price-distribution__row.is-lowest .lookit-price-distribution__meta b {
  color: var(--lookit-color-price-drop);
}

.lookit-price-distribution__row.is-lowest .lookit-price-distribution__bar {
  background: var(--lookit-color-price-drop, #667c8c);
}

@media (min-width: 520px) {
  .lookit-price-trend__summary {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
.lookit-price-list {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-4);
}

.lookit-price-list__header {
  display: flex;
  min-width: 0;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--lookit-space-2);
}

.lookit-price-list__header div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.lookit-price-list__header strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-md);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-price-list__header span,
.lookit-price-list__header em {
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-xs);
  font-style: normal;
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.2;
}

.lookit-price-list__header em {
  flex: 0 0 auto;
}

.lookit-price-row {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-4);
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface);
  padding: var(--lookit-space-4);
}

.lookit-price-row.is-lowest {
  border-color: var(--lookit-color-accent-strong);
}

.lookit-price-row.is-excluded {
  border-color: color-mix(in srgb, var(--lookit-color-text-muted) 28%, var(--lookit-border-subtle));
  background: color-mix(in srgb, var(--lookit-color-surface-muted) 72%, var(--lookit-color-surface));
}

.lookit-price-row.is-excluded .lookit-price-row__price b {
  color: var(--lookit-color-text-soft);
}

.lookit-price-row__head {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--lookit-space-3);
}

.lookit-price-row__seller {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}

.lookit-price-row__seller span {
  overflow: hidden;
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-price-row__seller small,
.lookit-price-row__seller em {
  overflow: hidden;
  font-size: 10px;
  font-style: normal;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-price-row__seller small {
  color: var(--lookit-color-accent-strong);
}

.lookit-price-row__seller em {
  max-width: 100%;
  color: var(--lookit-color-text-muted);
}

.lookit-price-row__seller strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.lookit-price-row__price {
  display: flex;
  flex: 0 0 auto;
  min-width: 112px;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  text-align: right;
}

.lookit-price-row__price small {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-price-row__price b {
  color: var(--lookit-color-price-drop);
  font-size: var(--lookit-font-size-md);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
  overflow-wrap: anywhere;
  text-align: inherit;
}

.lookit-price-row__price em {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-style: normal;
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.2;
  white-space: nowrap;
}

.lookit-price-row__breakdown {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 0;
}

.lookit-price-row__breakdown div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-surface-muted);
  padding: 10px;
}

.lookit-price-row__breakdown dt,
.lookit-price-row__breakdown dd {
  margin: 0;
  min-width: 0;
}

.lookit-price-row__breakdown dt {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-price-row__breakdown dd {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.lookit-price-row__meta {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 7px;
}

.lookit-price-row__meta span {
  max-width: 100%;
  overflow: hidden;
  border-radius: var(--lookit-radius-pill);
  background: var(--lookit-color-surface-muted);
  color: var(--lookit-color-text-soft);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.2;
  padding: 5px 7px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-option-stock {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-2);
  border: 0;
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-surface-muted);
  padding: 10px;
}

.lookit-option-stock__head {
  display: flex;
  min-width: 0;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--lookit-space-2);
}

.lookit-option-stock__head strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-option-stock__head span {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.2;
  white-space: nowrap;
}

.lookit-option-stock__list {
  display: grid;
  min-width: 0;
  grid-template-columns: 1fr;
  gap: var(--lookit-space-2);
}

.lookit-option-stock__item {
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 6px var(--lookit-space-2);
  align-items: center;
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-surface);
  padding: 8px;
}

.lookit-option-stock__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.lookit-option-stock__copy strong,
.lookit-option-stock__copy small {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-option-stock__copy strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-option-stock__copy small {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.2;
}

.lookit-option-stock__status {
  display: inline-flex;
  min-height: 24px;
  align-items: center;
  justify-content: center;
  border-radius: var(--lookit-radius-pill);
  background: var(--lookit-color-surface);
  color: var(--lookit-color-text-soft);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.1;
  padding: 0 8px;
  white-space: nowrap;
}

.lookit-option-stock__item.is-in-stock .lookit-option-stock__status {
  background: var(--lookit-color-accent-soft);
  color: var(--lookit-color-accent-strong);
}

.lookit-option-stock__item.is-low-stock .lookit-option-stock__status,
.lookit-option-stock__item.is-restock .lookit-option-stock__status {
  background: color-mix(in srgb, var(--lookit-color-lowest) 18%, var(--lookit-color-surface));
  color: var(--lookit-color-lowest);
}

.lookit-option-stock__item.is-sold-out .lookit-option-stock__status {
  background: var(--lookit-color-price-drop-soft);
  color: var(--lookit-color-price-drop);
}

.lookit-option-stock__button {
  grid-column: 1/-1;
  min-height: 34px;
  border: 1px solid var(--lookit-color-accent-strong);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface);
  color: var(--lookit-color-accent-strong);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
  padding: 0 var(--lookit-space-2);
}

.lookit-option-stock__button.is-enabled {
  background: var(--lookit-color-accent-soft);
}

.lookit-price-row__basis,
.lookit-price-row__reference {
  margin: 0;
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.lookit-price-row__reference {
  color: var(--lookit-color-text-muted);
}

.lookit-price-row__benefits {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.lookit-price-row__benefits div {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 5px;
  align-content: flex-start;
  border: 0;
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-surface-muted);
  padding: 10px;
}

.lookit-price-row__benefits small {
  flex: 0 0 100%;
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-price-row__benefits span {
  display: inline-flex;
  max-width: 100%;
  min-height: 21px;
  align-items: center;
  border-radius: var(--lookit-radius-pill);
  background: var(--lookit-color-surface);
  color: var(--lookit-color-text-soft);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.15;
  padding: 0 7px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-price-row__button {
  min-height: var(--lookit-touch-button);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-text);
  color: var(--lookit-color-surface);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
  padding: 8px var(--lookit-space-2);
  overflow-wrap: anywhere;
}

.lookit-price-row__actions {
  display: grid;
  grid-template-columns: minmax(0, 0.82fr) minmax(0, 1fr);
  gap: var(--lookit-space-2);
}

.lookit-price-row__save {
  min-height: var(--lookit-touch-button);
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  color: var(--lookit-color-accent-strong);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
  padding: 8px var(--lookit-space-2);
  overflow-wrap: anywhere;
}

.lookit-price-row__save.is-saved {
  border-color: var(--lookit-color-accent-strong);
  background: var(--lookit-color-accent-soft);
}

.lookit-saved,
.lookit-alerts,
.lookit-tab-empty {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-3);
}

.lookit-saved-summary,
.lookit-alert-summary {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-2);
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface-muted);
  padding: var(--lookit-space-3);
}

.lookit-saved-summary div,
.lookit-alert-summary div {
  display: flex;
  min-width: 0;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--lookit-space-2);
}

.lookit-saved-summary small,
.lookit-alert-summary small {
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-saved-summary strong,
.lookit-alert-summary strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-lg);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-saved-summary p,
.lookit-alert-summary p {
  margin: 0;
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.45;
}

.lookit-saved-list {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-3);
}

.lookit-alert-list {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-3);
}

.lookit-alert-card {
  display: grid;
  min-width: 0;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: var(--lookit-space-3);
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface);
  padding: var(--lookit-space-3);
}

.lookit-alert-card.is-disabled {
  opacity: 0.72;
}

.lookit-alert-card.is-restock {
  border-color: color-mix(in srgb, var(--lookit-color-accent-strong) 34%, var(--lookit-border-subtle));
}

.lookit-alert-card__media {
  width: 72px;
  aspect-ratio: 4/5;
  overflow: hidden;
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface-muted);
}

.lookit-alert-card__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lookit-alert-card__body {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--lookit-space-2);
}

.lookit-alert-card__head {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--lookit-space-2);
}

.lookit-alert-card__head div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.lookit-alert-card__head span,
.lookit-alert-card__head small {
  overflow: hidden;
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-2xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-alert-card__head strong {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.28;
  overflow-wrap: anywhere;
}

.lookit-alert-card__head em {
  flex: 0 0 auto;
  border-radius: var(--lookit-radius-pill);
  background: var(--lookit-color-surface-muted);
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-style: normal;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
  padding: 5px 7px;
  white-space: nowrap;
}

.lookit-alert-card__head em.is-enabled {
  background: var(--lookit-color-accent-soft);
  color: var(--lookit-color-accent-strong);
}

.lookit-alert-card__facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--lookit-space-2);
  margin: 0;
}

.lookit-alert-card__meta {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 6px;
}

.lookit-alert-card__meta span {
  max-width: 100%;
  overflow: hidden;
  border-radius: var(--lookit-radius-pill);
  background: var(--lookit-color-surface-muted);
  color: var(--lookit-color-text-soft);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.2;
  padding: 5px 7px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-alert-card__meta .lookit-alert-card__type {
  background: var(--lookit-color-accent-soft);
  color: var(--lookit-color-accent-strong);
  font-weight: var(--lookit-font-weight-black);
}

.lookit-alert-card__meta .lookit-alert-card__type.is-restock {
  background: var(--lookit-color-price-drop-soft);
  color: var(--lookit-color-price-drop);
}

.lookit-alert-card__actions {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--lookit-space-2);
}

.lookit-alert-card__actions button {
  min-height: 36px;
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
  padding: 7px 8px;
  overflow-wrap: anywhere;
}

.lookit-alert-card__actions button.is-enabled {
  border-color: var(--lookit-color-accent-strong);
  background: var(--lookit-color-accent-soft);
  color: var(--lookit-color-accent-strong);
}

.lookit-saved-card {
  display: grid;
  min-width: 0;
  grid-template-columns: 86px minmax(0, 1fr);
  gap: var(--lookit-space-3);
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface);
  padding: var(--lookit-space-3);
}

.lookit-saved-card.is-drop {
  border-color: color-mix(in srgb, var(--lookit-color-price-drop) 36%, var(--lookit-border-subtle));
}

.lookit-saved-card.is-rise {
  border-color: color-mix(in srgb, var(--lookit-color-lowest) 42%, var(--lookit-border-subtle));
}

.lookit-saved-card__media {
  display: block;
  width: 86px;
  aspect-ratio: 4/5;
  overflow: hidden;
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface-muted);
}

.lookit-saved-card__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lookit-saved-card__body,
.lookit-saved-card__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.lookit-saved-card__body {
  gap: var(--lookit-space-2);
}

.lookit-saved-card__head {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--lookit-space-2);
}

.lookit-saved-card__copy {
  gap: 3px;
}

.lookit-saved-card__copy span,
.lookit-saved-card__copy small {
  overflow: hidden;
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-2xs);
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-saved-card__copy button {
  min-width: 0;
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.28;
  overflow-wrap: anywhere;
  text-align: left;
}

.lookit-saved-card__alert {
  flex: 0 0 auto;
  border-radius: var(--lookit-radius-pill);
  background: var(--lookit-color-surface-muted);
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
  padding: 5px 7px;
  white-space: nowrap;
}

.lookit-saved-card__alert.is-enabled {
  background: var(--lookit-color-accent-soft);
  color: var(--lookit-color-accent-strong);
}

.lookit-saved-card__prices {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--lookit-space-2);
  margin: 0;
}

.lookit-saved-card__prices div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-surface-muted);
  padding: 8px;
}

.lookit-saved-card__prices dt,
.lookit-saved-card__prices dd {
  margin: 0;
  min-width: 0;
}

.lookit-saved-card__prices dt {
  color: var(--lookit-color-text-muted);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-saved-card__prices dd {
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.lookit-saved-card__change.is-drop dd {
  color: var(--lookit-color-price-drop);
}

.lookit-saved-card__change.is-rise dd {
  color: var(--lookit-color-lowest);
}

.lookit-saved-card__change dd {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.lookit-saved-card__change small {
  color: inherit;
  font-size: 10px;
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
}

.lookit-saved-card__meta {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 6px;
}

.lookit-saved-card__meta span {
  max-width: 100%;
  overflow: hidden;
  border-radius: var(--lookit-radius-pill);
  background: var(--lookit-color-surface-muted);
  color: var(--lookit-color-text-soft);
  font-size: 10px;
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.2;
  padding: 5px 7px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-saved-card__actions {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--lookit-space-2);
}

.lookit-saved-card__actions button {
  min-height: 36px;
  border: 1px solid var(--lookit-border-medium);
  border-radius: var(--lookit-radius-md);
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.15;
  padding: 7px 8px;
  overflow-wrap: anywhere;
}

.lookit-saved-card__actions button.is-enabled {
  border-color: var(--lookit-color-accent-strong);
  background: var(--lookit-color-accent-soft);
  color: var(--lookit-color-accent-strong);
}

.lookit-memory {
  display: flex;
  flex-direction: column;
  gap: var(--lookit-space-3);
  padding-top: var(--lookit-space-5);
}

.lookit-memory__header {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: var(--lookit-space-3);
}

.lookit-memory__header h2 {
  margin: 2px 0 0;
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-lg);
  font-weight: var(--lookit-font-weight-black);
  letter-spacing: 0;
  line-height: 1.2;
}

.lookit-memory__link {
  flex: 0 0 auto;
  color: var(--lookit-color-text-soft);
  font-size: var(--lookit-font-size-xs);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.lookit-memory__list {
  display: flex;
  flex-direction: column;
  gap: var(--lookit-space-2);
}

.lookit-memory-card {
  display: grid;
  min-height: 76px;
  grid-template-columns: 58px minmax(0, 1fr) auto;
  gap: var(--lookit-space-3);
  align-items: center;
  border: 1px solid var(--lookit-border-subtle);
  border-radius: var(--lookit-radius-md);
  background: var(--lookit-color-surface);
  padding: var(--lookit-space-2);
  text-align: left;
}

.lookit-memory-card:active {
  transform: scale(0.99);
}

.lookit-memory-card__image {
  width: 58px;
  height: 58px;
  border-radius: var(--lookit-radius-sm);
  background: var(--lookit-color-surface-muted);
  object-fit: cover;
}

.lookit-memory-card__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.lookit-memory-card__copy small,
.lookit-memory-card__copy em {
  overflow: hidden;
  color: var(--lookit-color-text-muted);
  font-size: var(--lookit-font-size-2xs);
  font-style: normal;
  font-weight: var(--lookit-font-weight-bold);
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-memory-card__copy strong {
  overflow: hidden;
  color: var(--lookit-color-text);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lookit-memory-card b {
  max-width: 82px;
  overflow: hidden;
  color: var(--lookit-color-price-drop);
  font-size: var(--lookit-font-size-sm);
  font-weight: var(--lookit-font-weight-black);
  line-height: 1.2;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 430px) {
  .lookit-price-list__header,
  .lookit-price-row__head {
    align-items: flex-start;
    flex-direction: column;
  }
  .lookit-price-row__price {
    min-width: 0;
    align-items: flex-start;
    text-align: left;
  }
  .lookit-price-row__breakdown {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .lookit-saved-card__prices,
  .lookit-alert-card__facts {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 360px) {
  .lookit-home__intro h1,
  .lookit-stage-header h1 {
    font-size: 28px;
  }
  .lookit-upload-card__button {
    min-height: 118px;
    grid-template-columns: 54px minmax(0, 1fr);
    gap: var(--lookit-space-3);
    padding: var(--lookit-space-3);
  }
  .lookit-upload-card__mark {
    width: 54px;
    height: 54px;
  }
  .lookit-upload-card__body strong {
    font-size: var(--lookit-font-size-lg);
  }
  .lookit-upload-actions,
  .lookit-upload-fallback,
  .lookit-selection-actions,
  .lookit-results-actions,
  .lookit-selection-panel__items,
  .lookit-search-targets__list {
    grid-template-columns: 1fr;
  }
  .lookit-stage-header {
    align-items: flex-start;
    flex-direction: column;
  }
  .lookit-preview-image {
    max-height: min(54vh, 360px);
  }
  .lookit-results-source {
    grid-template-columns: 64px minmax(0, 1fr);
  }
  .lookit-results-source__image {
    width: 64px;
  }
  .lookit-filter-toolbar {
    grid-template-columns: minmax(0, 1fr) 76px;
  }
  .lookit-filter-toolbar__button {
    min-width: 0;
    padding: 6px 8px;
  }
  .lookit-filter-toolbar__button small {
    max-width: 58px;
  }
  .lookit-compare-hero {
    grid-template-columns: 82px minmax(0, 1fr);
    gap: var(--lookit-space-2);
    padding: var(--lookit-space-2);
  }
  .lookit-compare-hero__image {
    width: 82px;
  }
  .lookit-detail-actions,
  .lookit-alert-editor__prices,
  .lookit-alert-editor__channels,
  .lookit-alert-editor__actions {
    grid-template-columns: 1fr;
  }
  .lookit-alert-editor {
    padding: var(--lookit-space-2) calc(var(--lookit-space-2) + var(--lookit-safe-area-right)) calc(var(--lookit-space-2) + var(--lookit-safe-area-bottom)) calc(var(--lookit-space-2) + var(--lookit-safe-area-left));
  }
  .lookit-alert-editor__panel {
    max-height: 92vh;
    max-height: min(92dvh, 100dvh - var(--lookit-safe-area-top) - var(--lookit-safe-area-bottom) - var(--lookit-space-4));
    padding: var(--lookit-space-3);
  }
  .lookit-filter-sheet {
    padding: var(--lookit-space-2) calc(var(--lookit-space-2) + var(--lookit-safe-area-right)) calc(var(--lookit-space-2) + var(--lookit-safe-area-bottom)) calc(var(--lookit-space-2) + var(--lookit-safe-area-left));
  }
  .lookit-filter-sheet__panel {
    max-height: 88vh;
    max-height: min(88dvh, 100dvh - var(--lookit-safe-area-top) - var(--lookit-safe-area-bottom) - var(--lookit-space-4));
    padding: var(--lookit-space-3);
  }
  .lookit-filter-sheet__actions {
    grid-template-columns: 1fr;
  }
  .lookit-price-trend__head,
  .lookit-price-distribution__head {
    align-items: stretch;
    flex-direction: column;
  }
  .lookit-price-trend__segments {
    width: 100%;
  }
  .lookit-price-trend__canvas {
    height: 190px;
  }
  .lookit-price-distribution__meta {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }
  .lookit-price-distribution__meta b {
    flex: 0 0 auto;
  }
  .lookit-price-row__head {
    flex-direction: column;
  }
  .lookit-price-row__price {
    align-items: flex-start;
    text-align: left;
  }
  .lookit-price-row__breakdown {
    grid-template-columns: 1fr;
  }
  .lookit-price-row__actions {
    grid-template-columns: 1fr;
  }
  .lookit-option-stock__head,
  .lookit-option-stock__item {
    align-items: stretch;
  }
  .lookit-option-stock__head {
    flex-direction: column;
    gap: 4px;
  }
  .lookit-option-stock__item {
    grid-template-columns: 1fr;
  }
  .lookit-option-stock__status {
    width: max-content;
  }
  .lookit-option-stock__button {
    grid-column: auto;
  }
  .lookit-lowest-summary__main {
    grid-template-columns: 1fr;
  }
  .lookit-lowest-summary__main strong {
    text-align: left;
  }
  .lookit-lowest-summary__facts,
  .lookit-price-row__benefits {
    grid-template-columns: 1fr;
  }
  .lookit-saved-card {
    grid-template-columns: 72px minmax(0, 1fr);
    gap: var(--lookit-space-2);
    padding: var(--lookit-space-2);
  }
  .lookit-saved-card__media {
    width: 72px;
  }
  .lookit-saved-card__head {
    align-items: stretch;
    flex-direction: column;
  }
  .lookit-saved-card__alert {
    width: max-content;
  }
  .lookit-saved-card__prices,
  .lookit-saved-card__actions {
    grid-template-columns: 1fr;
  }
  .lookit-alert-card {
    grid-template-columns: 62px minmax(0, 1fr);
    gap: var(--lookit-space-2);
    padding: var(--lookit-space-2);
  }
  .lookit-alert-card__media {
    width: 62px;
  }
  .lookit-alert-card__head {
    align-items: stretch;
    flex-direction: column;
  }
  .lookit-alert-card__head em {
    width: max-content;
  }
  .lookit-alert-card__facts,
  .lookit-alert-card__actions {
    grid-template-columns: 1fr;
  }
  .lookit-memory-card {
    grid-template-columns: 52px minmax(0, 1fr);
  }
  .lookit-memory-card__image {
    width: 52px;
    height: 52px;
  }
  .lookit-memory-card b {
    grid-column: 2;
    max-width: 100%;
    text-align: left;
  }
}`],
})
export class PageDashboardComponent implements OnInit, OnDestroy {
    @ViewChild('priceTrendCanvas') private priceTrendCanvas?: ElementRef<HTMLCanvasElement>;
    @ViewChild('previewImageElement') private previewImageElementRef?: ElementRef<HTMLImageElement>;
    @ViewChild('selectionRegionElement') private selectionRegionElementRef?: ElementRef<HTMLElement>;

    private readonly albumImageAccept = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';
    private readonly cameraImageAccept = 'image/*';

    public activeTab: LookitTabId = 'search';
    public selectedFileName = '';
    public photoInputAccept = this.albumImageAccept;
    public photoInputCapture: string | null = null;
    public uploadState = '사진 대기 중';
    public uploadFailureMessage = '';
    public imageAnalysisStatus: ImageAnalysisStatus = 'detected';
    public imageAnalysisMessage = '';
    public searchUiState: ProductSearchUiState = { status: 'idle', issues: [] };
    public alertBadge: string | number = '';
    public flowStep: LookitFlowStep = 'upload';
    public previewImageUrl = '';
    public detectedItems: DetectedSearchItem[] = [];
    public selectedDetectedItemId = '';
    public detectedCategoryId: LookitItemCategoryId | string | null = null;
    public suggestedCategoryId: LookitItemCategoryId | string | null = null;
    public selectedCategoryId: LookitItemCategoryId | '' = '';
    public searchTargets: ImageSearchTarget[] = [];
    public activeSearchTargetId = '';
    public manualSelectionRequested = false;
    public selectedRegion: CroppedRegion | null = null;
    public selectionRegionSource: SelectionRegionSource = 'none';
    public cropPreviewUrl = '';
    public localCropId = '';
    public isSelectionDragging = false;
    public selectedResultProduct: ResultProduct | null = null;
    public savedItems: UserSavedItem[] = [];
    public savedProductCards: SavedProductCard[] = [];
    public alertListItems: AlertListItem[] = [];
    public alertEditor: AlertEditorState | null = null;
    public priceCompareRows: PriceCompareRow[] = [];
    public priceCompareSummary: PriceCompareSummary | null = null;
    public priceTrendWindows: PriceTrendWindow[] = [30, 60, 90];
    public selectedPriceTrendWindow: PriceTrendWindow = 30;
    public priceTrendPoints: PriceTrendPoint[] = [];
    public priceTrendSummary: PriceTrendSummary = {
        windowLabel: '최근 30일',
        currentLowestLabel: '현재 최저가 확인 필요',
        recentLowestLabel: '가격 확인 데이터 없음',
        latestCheckedLabel: '스냅샷 없음',
        missingLabel: '확인된 날짜만 표시합니다.',
        hasData: false
    };
    public priceDistributionRows: PriceDistributionRow[] = [];
    public priceTrendAriaLabel = '최근 30일 날짜별 최저 실구매가 추이';
    public resultSortMode: SortMode = 'recommended';
    public listingSortMode: SortMode = 'effective_price_asc';
    public filterSheetOpen = false;
    public filterSheetContext: FilterSheetContext = 'results';
    public resultFilters: ResultFilterState = this.defaultResultFilters();
    public readonly uploadPrivacyDisclosure: UploadImageConsentDisclosure = {
        externalAiTransferEnabled: false,
        externalAiConsentRequired: false,
        policyLink: '/privacy/image-upload',
        retentionSummary: '현재는 사진을 기기 안에서 미리보기로만 처리하고 외부 AI 전송은 꺼져 있습니다.',
        deletionSummary: '실제 검색 연동 시 선택한 옷 영역 crop, 민감 영역 후보, embedding, 검색 기록은 분리해 관리하며 사용자가 삭제할 수 있어야 합니다.',
        externalTransferSummary: '민감 영역 자동 감지는 실패할 수 있습니다. 외부 AI 분석을 켜는 경우 얼굴, 계정명, DM, 위치 후보를 확인·블러한 뒤 전송 대상과 보관 기간을 별도 안내합니다.',
        legalReviewRequiredItems: ['외부 AI 전송', '보관 기간', '학습 사용 여부', '삭제 가능 범위']
    };

    private productClusters: ProductCluster[] = [];
    private listings: Listing[] = [];
    private priceSnapshots: PriceSnapshot[] = [];
    private priceTrendRangeSummary: PriceSnapshotRangeSummary | null = null;
    private platforms: Platform[] = [];
    private sellers: Seller[] = [];
    private readonly supportedImageTypes = ['image/jpeg', 'image/png', 'image/webp'];
    private readonly maxUploadImageBytes = 10 * 1024 * 1024;
    private readonly maxPreviewImageLongEdge = 1600;
    private readonly maxPreviewImageBytes = 2.5 * 1024 * 1024;
    private readonly previewImageMimeType = 'image/webp';
    private readonly previewImageQuality = 0.82;
    private readonly anonymousSession = anonymousSessionService.resolve();
    private readonly anonymousSessionId = this.anonymousSession.anonymous_session_id;
    private readonly currentUserId = anonymousSessionService.userId(this.anonymousSessionId);
    private readonly dayMs = 24 * 60 * 60 * 1000;
    private readonly missingGapDays = 14;
    private readonly defaultSearchTargetCategoryIds: LookitItemCategoryId[] = ['top', 'bottom', 'shoes'];
    private readonly recoveryActionCatalog: Record<ProductSearchRecoveryAction, RecoveryAction> = {
        adjust_region: {
            id: 'adjust_region',
            label: '영역 다시 맞추기',
            description: '찾고 싶은 옷만 들어오도록 선택 영역을 조정합니다.'
        },
        change_category: {
            id: 'change_category',
            label: '카테고리 바꾸기',
            description: '상의, 하의, 신발 등 검색 기준을 다시 선택합니다.'
        },
        search_full_image: {
            id: 'search_full_image',
            label: '전체 사진으로 검색',
            description: '영역이 애매할 때 전체 이미지 기준으로 다시 찾습니다.'
        },
        view_similar_designs: {
            id: 'view_similar_designs',
            label: '유사 디자인 보기',
            description: '동일 상품이 아닌 참고 디자인 후보만 분리해서 봅니다.'
        },
        upload_new_photo: {
            id: 'upload_new_photo',
            label: '새 사진 업로드',
            description: '더 선명하거나 다른 각도의 사진으로 다시 시작합니다.'
        }
    };
    private priceAlerts: PriceAlert[] = [];
    private currentImageSearchRequest: ImageSearchRequestSession | null = null;
    private recentImageSearchRequest: ImageSearchRequest | null = null;
    private resultClusterIds: string[] | null = null;
    private resultClusterTypes: Record<string, ProductClusterType> | null = null;
    private priceTrendChart: Chart | null = null;
    private priceTrendChartSignature = '';
    private priceTrendRenderHandle: number | null = null;
    private priceTrendRenderHandleType: 'animationFrame' | 'timeout' | null = null;
    private selectionPointerState: SelectionPointerState | null = null;
    private pendingSelectionRegion: CroppedRegion | null = null;
    private latestSelectionRegion: CroppedRegion | null = null;
    private selectionAnimationFrame: number | null = null;
    private originalImageSize: ImageSize | null = null;
    private uploadedImageSize: ImageSize | null = null;
    private displayedImageSize: ImageSize | null = null;
    private normalizedImageId = '';
    private cropImageId = '';
    private cropRegionOriginal: CroppedRegion | null = null;
    private selectedCrop: SelectedRegionCrop | null = null;

    public tabs: LookitTab[] = [];
    public homeEntries: HomeEntry[] = [];
    public resultSections: ResultSection[] = [];
    public readonly sortOptions: SortOption[] = [
        { id: 'recommended', label: '추천순' },
        { id: 'effective_price_asc', label: '실구매가 낮은 순' },
        { id: 'match_confidence_desc', label: '동일 상품 신뢰도 높은 순' },
        { id: 'recent_checked_desc', label: '최근 확인순' }
    ];
    public readonly itemCategoryOptions: ItemCategoryOption[] = [
        { id: 'top', label: '상의', description: '어깨 아래/허리 위 영역' },
        { id: 'outer', label: '아우터', description: '겉옷/레이어 영역' },
        { id: 'bottom', label: '하의', description: '허리 아래/신발 전 영역' },
        { id: 'dress', label: '원피스', description: '상체 아래부터 긴 라인' },
        { id: 'shoes', label: '신발', description: '발 주변 영역' },
        { id: 'bag', label: '가방', description: '측면 추천, 직접 보정 권장' },
        { id: 'accessory', label: '액세서리', description: '모자/주얼리/소품 영역' },
        { id: 'unknown', label: '확인 필요', description: '직접 확인할 영역' },
        { id: 'other', label: '기타', description: '직접 맞출 영역' }
    ];
    public readonly primaryItemCategoryIds: LookitItemCategoryId[] = ['top', 'bottom', 'dress', 'shoes', 'bag', 'other'];
    public readonly alertConditionOptions: AlertConditionOption[] = [
        {
            id: 'saved_price_drop',
            label: '저장 당시보다 가격 하락',
            description: '저장 시점 실구매가보다 낮아지면 알림',
            alertType: 'price_drop',
            thresholdPercent: null,
            requiresTargetPrice: false
        },
        {
            id: 'drop_5',
            label: '5% 이상 가격 하락',
            description: '저장가 대비 5% 이상 낮아지면 알림',
            alertType: 'price_drop',
            thresholdPercent: 5,
            requiresTargetPrice: false
        },
        {
            id: 'drop_10',
            label: '10% 이상 가격 하락',
            description: '저장가 대비 10% 이상 낮아지면 알림',
            alertType: 'price_drop',
            thresholdPercent: 10,
            requiresTargetPrice: false
        },
        {
            id: 'target_price',
            label: '내가 정한 금액 이하',
            description: '직접 정한 실구매가 이하가 되면 알림',
            alertType: 'target_price',
            thresholdPercent: null,
            requiresTargetPrice: true
        },
        {
            id: 'lowest_30d',
            label: '최근 30일 최저가 갱신',
            description: '최근 30일 실구매가 최저 지점이 갱신되면 알림',
            alertType: 'lowest_30d',
            thresholdPercent: null,
            requiresTargetPrice: false
        },
        {
            id: 'lowest_90d',
            label: '최근 90일 최저가 갱신',
            description: '최근 90일 실구매가 최저 지점이 갱신되면 알림',
            alertType: 'lowest_90d',
            thresholdPercent: null,
            requiresTargetPrice: false
        },
        {
            id: 'restock',
            label: '품절 옵션 재입고',
            description: '선택한 색상/사이즈가 구매 가능해지면 알림',
            alertType: 'restock',
            thresholdPercent: null,
            requiresTargetPrice: false
        }
    ];
    public readonly alertChannelOptions: AlertChannelOption[] = [
        { id: 'in_app', label: '앱 내', description: '알림 화면 목록' },
        { id: 'browser_push', label: '브라우저', description: '브라우저 알림 연동 예정' },
        { id: 'app_push', label: '앱 푸시', description: '모바일 앱 푸시 연동 예정' },
        { id: 'email', label: '이메일', description: '이메일 발송 연동 예정' }
    ];

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        imageUploadService.useRuntimeImageUploadService();
        searchService.useRuntimeImageAnalysisService();
        await this.loadLookitServiceState();
        this.refreshLookitData();
        await this.service.render();
    }

    public async pickImage(source: UploadSource) {
        this.activeTab = 'search';
        this.uploadState = this.uploadStatus(source);
        this.uploadFailureMessage = '';

        const input = this.getPhotoInput();
        if (!input) {
            this.uploadState = '파일 선택 불가';
            await this.service.render();
            return;
        }

        this.configurePhotoInput(input, source);
        input.value = '';
        input.click();
        await this.service.render();
    }

    public async handleFileInput(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files.length > 0 ? input.files[0] : null;

        if (!file) {
            this.uploadState = this.previewImageUrl ? '옷 영역 선택 중' : '사진 대기 중';
            await this.service.render();
            return;
        }

        if (!this.isSupportedImageFile(file)) {
            input.value = '';
            this.selectedFileName = '';
            this.uploadState = '다른 이미지 필요';
            this.uploadFailureMessage = this.unsupportedUploadFileMessage();
            await this.service.render();
            return;
        }

        if (file.size > this.maxUploadImageBytes) {
            input.value = '';
            this.selectedFileName = '';
            this.uploadState = '이미지 용량 초과';
            this.uploadFailureMessage = this.fileTooLargeMessage();
            await this.service.render();
            return;
        }

        this.clearCropPreparation({ resetImageContext: true });
        this.uploadFailureMessage = '';
        this.selectedFileName = this.selectedUploadFileLabel(file);
        this.uploadState = this.previewPreparingState(file);
        await this.service.render();
        await this.waitForNextFrame();

        const previewAsset = await this.preparePreviewImageAsset(file);
        if (!previewAsset.imageSize) {
            URL.revokeObjectURL(previewAsset.url);
            input.value = '';
            this.selectedFileName = '';
            this.uploadState = '이미지 확인 필요';
            this.uploadFailureMessage = this.invalidUploadImageMessage();
            await this.service.render();
            return;
        }

        this.setLocalPreviewUrl(previewAsset.url);
        this.originalImageSize = previewAsset.imageSize;
        const uploadResult = await this.uploadOriginalImageIfEnabled(file, previewAsset);
        this.normalizedImageId = uploadResult?.normalized_image_id || '';
        this.uploadedImageSize = uploadResult
            ? {
                width: uploadResult.normalized_width || uploadResult.original_width,
                height: uploadResult.normalized_height || uploadResult.original_height
            }
            : null;
        const requestImageSize = uploadResult
            ? this.uploadedImageSize
            : previewAsset.imageSize;
        this.originalImageSize = requestImageSize;
        this.flowStep = 'select';
        this.currentImageSearchRequest = await searchService.createImageSearchRequest({
            userId: this.currentUserId,
            uploadImageId: uploadResult?.upload_image_id || null,
            imageUrl: this.previewImageUrl,
            fileName: uploadResult ? undefined : this.selectedFileName,
            fileSize: uploadResult?.byte_size || previewAsset.fileSize,
            imageSize: requestImageSize,
            privacyFlags: {
                allowExternalAnalysis: false,
                requiresOnDeviceProcessing: !uploadResult,
                retainUploadedImage: Boolean(uploadResult)
            }
        });
        this.detectedItems = this.currentImageSearchRequest.detected_items;
        this.searchUiState = this.currentImageSearchRequest.ui_state || { status: 'ready', issues: [] };
        this.updateImageAnalysisState(this.currentImageSearchRequest.image_analysis || null);
        this.selectedDetectedItemId = '';
        this.detectedCategoryId = this.currentImageSearchRequest.detected_category_id || null;
        this.suggestedCategoryId = this.currentImageSearchRequest.suggested_category_id || null;
        this.selectedCategoryId = '';
        this.searchTargets = this.ensureSearchTargets(this.currentImageSearchRequest.search_targets || [], null);
        this.activeSearchTargetId = '';
        this.manualSelectionRequested = false;
        this.selectedRegion = null;
        this.selectionRegionSource = 'none';
        this.selectedResultProduct = null;
        this.resultClusterIds = null;
        this.resultClusterTypes = null;
        this.currentImageSearchRequest = {
            ...this.currentImageSearchRequest,
            detected_category_id: this.detectedCategoryId,
            suggested_category_id: this.suggestedCategoryId,
            cropped_region: null,
            selected_category_id: null,
            selected_candidate_id: null,
            selected_item_index: null,
            search_targets: this.searchTargets,
            active_search_target_id: null
        };
        const initialRecommendation = searchService.getInitialSearchRecommendation(this.currentImageSearchRequest);
        if (!this.applyInitialSearchRecommendation(initialRecommendation)) {
            this.uploadState = this.imageAnalysisUploadState();
        }

        await this.service.render();
    }

    public async handlePreviewImageLoad(event: Event) {
        const image = event.target as HTMLImageElement | null;
        if (image) {
            this.originalImageSize = this.uploadedImageSize || this.imageNaturalSize(image);
            this.displayedImageSize = this.imageDisplayedSize(image);
        }

        this.refreshSelectedCropPreparation();
        await this.service.render();
    }

    public async selectDetectedItem(item: DetectedSearchItem) {
        const categoryId = this.itemCategoryIdFromCategory(item.category);
        if (categoryId && categoryId !== this.selectedCategoryId) {
            this.activateSearchTarget(categoryId);
        }

        if (this.currentImageSearchRequest) {
            this.currentImageSearchRequest = await searchService.selectDetectedItem({
                request: this.currentImageSearchRequest,
                itemIndex: item.item_index
            });
        }
        this.selectedDetectedItemId = item.id;
        if (this.currentImageSearchRequest) {
            this.activeSearchTargetId = this.currentImageSearchRequest.active_search_target_id || this.activeSearchTargetId;
            this.currentImageSearchRequest = {
                ...this.currentImageSearchRequest,
                selected_candidate_id: item.id,
                selected_item_index: item.item_index
            };
        }
        this.suggestedCategoryId = item.suggestedCategoryId || categoryId || this.suggestedCategoryId;
        this.manualSelectionRequested = true;
        this.applySelectedRegion(item.cropped_region, 'candidate');
        this.uploadState = this.selectedItemCategoryLabel ? `${this.selectedItemCategoryLabel} 추천 영역 확인 필요` : '추천 영역 확인 필요';
        await this.service.render();
    }

    public async selectItemCategory(categoryId: string) {
        if (!this.isItemCategoryId(categoryId)) return;

        const preserveSelectedRegion = this.shouldPreserveRegionOnCategoryChange();
        this.activateSearchTarget(categoryId, { preserveSelectedRegion });
        this.manualSelectionRequested = true;
        this.uploadState = this.categorySelectionStateLabel(categoryId, preserveSelectedRegion);
        await this.service.render();
    }

    public async enterManualSelection() {
        this.selectedDetectedItemId = '';
        this.manualSelectionRequested = true;
        this.applySelectedRegion(this.selectedRegion || this.defaultRegionForCategory(this.selectedCategoryId || 'other'), 'manual');
        this.uploadState = this.selectedCategoryId ? `${this.selectedItemCategoryLabel} 사용자 지정 영역` : '사용자 지정 영역 · 카테고리 선택 필요';
        await this.service.render();
    }

    public async selectFullImageSearch() {
        this.selectedDetectedItemId = '';
        this.manualSelectionRequested = true;
        this.applySelectedRegion(lookitFullImageRegion, 'full_image');
        if (this.currentImageSearchRequest) {
            this.currentImageSearchRequest = {
                ...this.currentImageSearchRequest,
                selected_candidate_id: null,
                selected_item_index: null
            };
        }
        this.uploadState = this.selectedCategoryId ? '전체 사진 기준 검색' : '전체 사진 기준 · 카테고리 선택 필요';
        await this.service.render();
    }

    public async goToResults() {
        if (!this.canOpenResults) return;

        this.destroyPriceTrendChart();
        const currentRequest = this.currentImageSearchRequest;
        const selectedRegion = this.selectedRegion;
        if (!currentRequest || !selectedRegion || !this.selectedCategoryId) return;

        this.refreshSelectedCropPreparation();
        this.uploadFailureMessage = '';
        this.uploadState = '선택 영역 crop 생성 중';
        await this.service.render();

        const cropReady = await this.ensureCropArtifactForSearch();
        if (!cropReady) {
            await this.service.render();
            return;
        }

        this.uploadState = '선택 영역 분석 중';
        await this.service.render();

        const analysisResponse = await searchService.analyzeConfirmedCrop({
            request: currentRequest,
            selectedRegion,
            cropImageId: this.cropImageId || this.selectedCrop?.crop_image_id || null,
            cropRegionOriginal: this.cropRegionOriginal,
            selectedCrop: this.selectedCrop,
            originalImageSize: this.originalImageSize,
            displayedImageSize: this.displayedImageSize,
            selectedCategoryId: this.selectedCategoryId,
            regionSource: this.selectionRegionSource,
            selectedCandidateId: this.selectedDetectedItem?.id || currentRequest.selected_candidate_id || null,
            userConfirmed: true,
            privacyFlags: {
                allowExternalAnalysis: false,
                requiresOnDeviceProcessing: !currentRequest.upload_image_id,
                retainUploadedImage: Boolean(currentRequest.upload_image_id)
            }
        });
        this.updateImageAnalysisState(analysisResponse);
        if (this.currentImageSearchRequest) {
            this.currentImageSearchRequest = {
                ...this.currentImageSearchRequest,
                image_analysis: analysisResponse,
                detected_items: analysisResponse.detectedItems.length > 0
                    ? analysisResponse.detectedItems as DetectedSearchItem[]
                    : this.currentImageSearchRequest.detected_items,
                suggested_category_id: analysisResponse.suggestedCategoryId || this.suggestedCategoryId,
                search_status: 'matching'
            };
        }

        this.uploadState = '선택 영역 상품 검색 중';
        await this.service.render();

        const cropContext = this.currentCropContext();
        const searchResults = await searchService.getSearchResults({
            request_id: currentRequest.id,
            upload_image_id: currentRequest.upload_image_id,
            local_mock_image_id: currentRequest.local_mock_image_id,
            search_target_id: this.activeSearchTargetId,
            selected_item_index: this.selectedDetectedItem?.item_index ?? currentRequest.selected_item_index,
            selected_category_id: this.selectedCategoryId,
            suggested_category_id: this.suggestedCategoryId,
            detected_category_id: this.detectedCategoryId,
            selected_candidate_id: this.selectedDetectedItem?.id || currentRequest.selected_candidate_id || null,
            selected_item_label: this.selectedDetectedItem?.label || null,
            selected_region: selectedRegion,
            region_source: this.selectionRegionSource,
            adjustment_status: this.adjustmentStatusForRegionSource(this.selectionRegionSource),
            ...cropContext,
            selected_crop: this.selectedCrop
        });
        this.originalImageSize = searchResults.original_image_size || this.originalImageSize;
        this.searchUiState = searchResults.ui_state;
        this.displayedImageSize = searchResults.displayed_image_size || this.displayedImageSize;
        this.cropRegionOriginal = searchResults.crop_region_original || this.cropRegionOriginal;
        this.cropPreviewUrl = searchResults.crop_preview_url || this.cropPreviewUrl;
        this.localCropId = searchResults.local_crop_id || this.localCropId;
        this.selectedCrop = searchResults.selected_crop || this.selectedCrop;
        if (this.currentImageSearchRequest) {
            this.currentImageSearchRequest = {
                ...this.currentImageSearchRequest,
                cropped_region: searchResults.selected_region,
                ...this.currentCropContext(),
                selected_item_index: searchResults.selected_item_index,
                selected_candidate_id: searchResults.selected_candidate_id,
                selected_category_id: searchResults.selected_category_id,
                suggested_category_id: searchResults.suggested_category_id ?? this.suggestedCategoryId,
                detected_category_id: searchResults.detected_category_id ?? this.detectedCategoryId,
                search_targets: this.upsertSearchTarget(searchResults.selected_search_target),
                active_search_target_id: searchResults.selected_search_target.id,
                search_status: searchResults.ui_state.status === 'platform_error' || searchResults.ui_state.status === 'no_listing_available' ? 'failed' : 'completed',
                ui_state: searchResults.ui_state
            };
        }
        this.productClusters = this.mergeById(this.productClusters, searchResults.product_clusters);
        this.resultClusterIds = searchResults.product_clusters.map((cluster) => cluster.id);
        this.resultClusterTypes = searchResults.groups.reduce<Record<string, ProductClusterType>>((types, group) => {
            group.product_clusters.forEach((cluster) => {
                types[cluster.id] = group.cluster_type;
            });
            return types;
        }, {});
        this.refreshLookitData();
        this.flowStep = 'results';
        this.activeTab = 'search';
        this.selectedResultProduct = null;
        this.uploadState = `${this.selectedItemCategoryLabel} 결과 준비됨`;
        await this.service.render();
    }

    public async backToSelection() {
        this.destroyPriceTrendChart();
        this.selectedResultProduct = null;
        if (!this.previewImageUrl) {
            this.flowStep = 'upload';
            this.uploadState = '사진 대기 중';
        } else {
            this.flowStep = 'select';
            this.uploadState = '옷 영역 선택 중';
        }

        await this.service.render();
    }

    public async backToResults() {
        this.destroyPriceTrendChart();
        this.flowStep = 'results';
        this.activeTab = 'search';
        this.selectedResultProduct = null;
        this.uploadState = '선택 영역 결과 준비됨';
        await this.service.render();
    }

    public async openPriceCompare(product: ResultProduct | any) {
        const clusterId = product && (product.clusterId || product.id);
        if (!clusterId) return;

        await this.loadProductClusterDetail(clusterId, this.selectedPriceTrendWindow);
        const cluster = this.findCluster(clusterId);
        const target = this.findResultProduct(clusterId) || (cluster ? this.toResultProduct(cluster) : null);
        if (!target) return;

        this.selectedResultProduct = target;
        this.flowStep = 'priceCompare';
        this.activeTab = 'search';
        this.uploadState = '가격 비교 확인 중';
        this.refreshPriceInsights();
        await this.service.render();
        this.schedulePriceTrendChartRender();
    }

    public async selectPriceTrendWindow(windowDays: PriceTrendWindow) {
        if (this.selectedPriceTrendWindow === windowDays) return;

        this.selectedPriceTrendWindow = windowDays;
        await this.loadPriceTrendSnapshotsForSelectedProduct();
        this.refreshPriceInsights();
        await this.service.render();
        this.schedulePriceTrendChartRender();
    }

    public async selectResultSort(event: Event) {
        const value = (event.target as HTMLSelectElement).value;
        if (!this.isSortMode(value)) return;

        this.resultSortMode = value;
        this.refreshLookitData();
        await this.service.render();
    }

    public async selectListingSort(event: Event) {
        const value = (event.target as HTMLSelectElement).value;
        if (!this.isSortMode(value)) return;

        this.listingSortMode = value;
        this.refreshPriceInsights();
        await this.service.render();
        this.schedulePriceTrendChartRender();
    }

    public async openFilterSheet(context: FilterSheetContext) {
        this.filterSheetContext = context;
        this.filterSheetOpen = true;
        await this.service.render();
    }

    public async closeFilterSheet() {
        this.filterSheetOpen = false;
        await this.service.render();
    }

    public async toggleResultScopeFilter(filterId: ResultScopeFilterId) {
        if (filterId === 'exactOnly') {
            const exactOnly = !this.resultFilters.exactOnly;
            this.resultFilters = {
                ...this.resultFilters,
                exactOnly,
                includeWholesale: exactOnly ? false : true,
                includeSimilar: exactOnly ? false : true
            };
        } else {
            this.resultFilters = {
                ...this.resultFilters,
                exactOnly: false,
                [filterId]: !this.resultFilters[filterId]
            };
        }

        this.refreshLookitData();
        await this.service.render();
    }

    public async toggleMarketFilter(filterId: MarketFilterId) {
        this.resultFilters = {
            ...this.resultFilters,
            [filterId]: !this.resultFilters[filterId]
        };

        this.refreshLookitData();
        await this.service.render();
        this.schedulePriceTrendChartRender();
    }

    public async togglePlatformFilter(platformId: string) {
        const exists = this.resultFilters.platformIds.includes(platformId);
        this.resultFilters = {
            ...this.resultFilters,
            platformIds: exists
                ? this.resultFilters.platformIds.filter((id) => id !== platformId)
                : [...this.resultFilters.platformIds, platformId]
        };

        this.refreshLookitData();
        await this.service.render();
        this.schedulePriceTrendChartRender();
    }

    public async resetFilters() {
        this.resultFilters = this.defaultResultFilters();
        this.resultSortMode = 'recommended';
        this.listingSortMode = 'effective_price_asc';
        this.refreshLookitData();
        await this.service.render();
        this.schedulePriceTrendChartRender();
    }

    public openListing(row: PriceCompareRow) {
        if (!row.productUrl || typeof window === 'undefined') return;
        window.open(row.productUrl, '_blank', 'noopener,noreferrer');
    }

    public async toggleRestockAlert(row: PriceCompareRow, option: ListingOptionStockRow) {
        if (!this.selectedResultProduct || !option.canRequestRestock) return;

        const clusterId = this.selectedResultProduct.clusterId;

        if (option.restockEnabled) {
            const removedAlerts = this.removeAlertsForSaved(clusterId, row.id, option.id);
            await this.removeSavedItemByIdentity(clusterId, row.id, option.id);
            if (removedAlerts) await this.persistPriceAlerts();
            this.uploadState = '재입고 알림 해제됨';
        } else {
            const listingOption = this.findListingOption(row.id, option.id);
            const savedItem = this.ensureSavedItem(clusterId, row.id, listingOption || option);
            if (!savedItem) return;

            const nextAlert = this.createRestockAlert(savedItem);
            if (!nextAlert) return;

            this.upsertPriceAlert(nextAlert);
            await this.upsertSavedItem(savedItem);
            await this.updateSavedItemAlertPreferenceFromAlert(nextAlert);
            await this.persistPriceAlerts();
            this.uploadState = '재입고 알림 설정됨';
        }

        this.refreshLookitData();
        await this.service.render();
    }

    public async toggleSaveFromProduct(product: ResultProduct | any) {
        const clusterId = product && (product.clusterId || product.id);
        if (!clusterId) return;

        if (this.isProductSaved(clusterId)) {
            const removedAlerts = this.removeAlertsForSaved(clusterId, null);
            await this.removeSavedItemByIdentity(clusterId, null);
            if (removedAlerts) await this.persistPriceAlerts();
            this.uploadState = '저장 해제됨';
        } else {
            const savedItem = this.createSavedItem(clusterId, null);
            if (!savedItem) return;

            await this.upsertSavedItem(savedItem);
            this.uploadState = '저장됨';
        }

        this.refreshLookitData();
        await this.service.render();
    }

    public async toggleSaveFromListing(row: PriceCompareRow) {
        if (!this.selectedResultProduct) return;

        const clusterId = this.selectedResultProduct.clusterId;
        const listingId = row.id;

        if (this.isListingSaved(clusterId, listingId)) {
            const removedAlerts = this.removeAlertsForSaved(clusterId, listingId);
            await this.removeSavedItemByIdentity(clusterId, listingId);
            if (removedAlerts) await this.persistPriceAlerts();
            this.uploadState = '판매처 저장 해제됨';
        } else {
            const savedItem = this.createSavedItem(clusterId, listingId);
            if (!savedItem) return;

            await this.upsertSavedItem(savedItem);
            this.uploadState = '판매처 상품 저장됨';
        }

        this.refreshLookitData();
        await this.service.render();
    }

    public async openSelectedProductAlertSettings() {
        if (!this.selectedResultProduct) return;

        const savedItem = this.ensureSavedItem(this.selectedResultProduct.clusterId, null);
        if (!savedItem) return;

        await this.upsertSavedItem(savedItem);
        this.refreshLookitData();
        this.openAlertEditor(savedItem);
        this.uploadState = '알림 설정';
        await this.service.render();
    }

    public async openSavedAlertSettings(card: SavedProductCard) {
        const savedItem = this.findSavedItem(card.clusterId, card.listingId, card.listingOptionId);
        if (!savedItem) return;

        this.openAlertEditor(savedItem);
        this.uploadState = '알림 설정';
        await this.service.render();
    }

    public async openAlertListSettings(item: AlertListItem) {
        const savedItem = this.findSavedItem(item.clusterId, item.listingId, item.listingOptionId);
        if (!savedItem) return;

        this.openAlertEditor(savedItem);
        this.uploadState = '알림 수정';
        await this.service.render();
    }

    public async selectAlertCondition(conditionId: AlertConditionId) {
        if (!this.alertEditor) return;

        this.alertEditor.conditionId = conditionId;
        this.alertEditor.error = '';

        if (conditionId === 'target_price' && !this.alertEditor.targetPriceInput) {
            const basePrice = this.alertEditor.currentPrice || this.alertEditor.savedPrice;
            this.alertEditor.targetPriceInput = basePrice > 1000 ? `${Math.max(1000, basePrice - 1000)}` : '';
        }

        await this.service.render();
    }

    public async handleAlertTargetPriceInput(event: Event) {
        if (!this.alertEditor) return;

        const input = event.target as HTMLInputElement;
        this.alertEditor.targetPriceInput = (input.value || '').replace(/[^0-9]/g, '');
        this.alertEditor.error = '';
        await this.service.render();
    }

    public async toggleAlertChannel(channelId: PriceAlertChannel) {
        if (!this.alertEditor) return;

        const exists = this.alertEditor.channels.includes(channelId);
        const nextChannels = exists
            ? this.alertEditor.channels.filter((item) => item !== channelId)
            : [...this.alertEditor.channels, channelId];

        this.alertEditor.channels = nextChannels;
        this.alertEditor.error = '';
        await this.service.render();
    }

    public async saveAlertSettings() {
        if (!this.alertEditor) return;

        const nextAlert = this.createPriceAlertFromEditor(this.alertEditor);
        if (!nextAlert) {
            await this.service.render();
            return;
        }

        this.upsertPriceAlert(nextAlert);
        await this.updateSavedItemAlertPreferenceFromAlert(nextAlert);

        await this.persistPriceAlerts();
        this.refreshLookitData();
        this.alertEditor = null;
        this.uploadState = '알림 저장됨';
        await this.service.render();
    }

    public async removeAlertSettings() {
        if (!this.alertEditor) return;

        const removed = this.removeAlertsForSaved(this.alertEditor.clusterId, this.alertEditor.listingId, this.alertEditor.listingOptionId);
        if (removed) {
            await this.updateSavedItemAlertPreferenceByIdentity(
                this.alertEditor.clusterId,
                this.alertEditor.listingId,
                this.alertEditor.listingOptionId,
                null,
                this.alertEditor.savedItemId
            );
            await this.persistPriceAlerts();
        }

        this.refreshLookitData();
        this.alertEditor = null;
        this.uploadState = '알림 해제됨';
        await this.service.render();
    }

    public async toggleAlertEnabled(item: AlertListItem) {
        const now = new Date().toISOString();
        this.priceAlerts = this.priceAlerts.map((alert) => {
            if (alert.id !== item.id) return alert;
            return {
                ...alert,
                enabled: !alert.enabled,
                updated_at: now
            };
        });

        const updatedAlert = this.priceAlerts.find((alert) => alert.id === item.id) || null;
        if (updatedAlert) await this.updateSavedItemAlertPreferenceFromAlert(updatedAlert);
        await this.persistPriceAlerts();
        this.refreshLookitData();
        this.uploadState = item.enabled ? '알림 꺼짐' : '알림 켜짐';
        await this.service.render();
    }

    public async removeAlertListItem(item: AlertListItem) {
        const removed = this.removeAlertsForSaved(item.clusterId, item.listingId, item.listingOptionId);
        if (removed) {
            await this.updateSavedItemAlertPreferenceByIdentity(
                item.clusterId,
                item.listingId,
                item.listingOptionId,
                null
            );
            await this.persistPriceAlerts();
        }

        this.refreshLookitData();
        this.uploadState = '알림 해제됨';
        await this.service.render();
    }

    public async closeAlertEditor() {
        this.alertEditor = null;
        await this.service.render();
    }

    public async removeSavedCard(card: SavedProductCard) {
        const removedAlerts = this.removeAlertsForSaved(card.clusterId, card.listingId, card.listingOptionId);
        await this.removeSavedItemByIdentity(card.clusterId, card.listingId, card.listingOptionId);
        if (removedAlerts) await this.persistPriceAlerts();
        this.refreshLookitData();
        this.uploadState = '저장 해제됨';
        await this.service.render();
    }

    public async openSavedCard(card: SavedProductCard) {
        await this.openPriceCompare({ clusterId: card.clusterId });
    }

    public async clearUpload() {
        this.destroyPriceTrendChart();
        this.revokePreviewUrl();
        this.previewImageUrl = '';
        this.selectedFileName = '';
        this.activeTab = 'search';
        this.flowStep = 'upload';
        this.detectedItems = [];
        this.selectedDetectedItemId = '';
        this.imageAnalysisStatus = 'detected';
        this.imageAnalysisMessage = '';
        this.detectedCategoryId = null;
        this.suggestedCategoryId = null;
        this.selectedCategoryId = '';
        this.searchTargets = [];
        this.activeSearchTargetId = '';
        this.manualSelectionRequested = false;
        this.selectedRegion = null;
        this.selectionRegionSource = 'none';
        this.clearCropPreparation({ resetImageContext: true });
        this.selectionPointerState = null;
        this.pendingSelectionRegion = null;
        this.latestSelectionRegion = null;
        this.isSelectionDragging = false;
        this.cancelSelectionFrame();
        this.setSelectionElementDragging(false);
        this.selectedResultProduct = null;
        this.alertEditor = null;
        this.filterSheetOpen = false;
        this.currentImageSearchRequest = null;
        this.searchUiState = { status: 'idle', issues: [] };
        this.resultClusterIds = null;
        this.resultClusterTypes = null;
        this.uploadFailureMessage = '';
        this.uploadState = '사진 대기 중';

        const input = this.getPhotoInput();
        if (input) input.value = '';

        await this.service.render();
    }

    public async handleRecoveryAction(actionId: ProductSearchRecoveryAction) {
        if (actionId === 'upload_new_photo') {
            await this.clearUpload();
            return;
        }

        if (actionId === 'adjust_region') {
            this.destroyPriceTrendChart();
            this.selectedResultProduct = null;
            this.flowStep = this.previewImageUrl ? 'select' : 'upload';
            this.activeTab = 'search';
            this.uploadState = this.previewImageUrl ? '영역을 다시 맞춰주세요' : '사진 대기 중';
            await this.service.render();
            return;
        }

        if (actionId === 'change_category') {
            this.destroyPriceTrendChart();
            this.selectedResultProduct = null;
            this.flowStep = this.previewImageUrl ? 'select' : 'upload';
            this.activeTab = 'search';
            this.selectedCategoryId = '';
            this.activeSearchTargetId = '';
            this.uploadState = this.previewImageUrl ? '카테고리를 다시 선택해 주세요' : '사진 대기 중';
            if (this.currentImageSearchRequest) {
                this.currentImageSearchRequest = {
                    ...this.currentImageSearchRequest,
                    selected_category_id: null,
                    active_search_target_id: null
                };
            }
            await this.service.render();
            return;
        }

        if (actionId === 'search_full_image') {
            if (!this.previewImageUrl) {
                await this.clearUpload();
                return;
            }
            if (!this.selectedCategoryId) {
                this.activateSearchTarget('other');
            }
            await this.selectFullImageSearch();
            if (this.canOpenResults) await this.goToResults();
            return;
        }

        if (actionId === 'view_similar_designs') {
            this.resultFilters = {
                ...this.resultFilters,
                exactOnly: false,
                includeWholesale: false,
                includeSimilar: true
            };
            this.flowStep = 'results';
            this.activeTab = 'search';
            this.selectedResultProduct = null;
            this.uploadState = '유사 디자인 참고 후보';
            this.refreshLookitData();
            await this.service.render();
        }
    }

    public async goHome() {
        await this.clearUpload();
    }

    public async selectTab(tab: LookitTabId | string) {
        if (!this.isTabId(tab)) return;
        this.activeTab = tab;

        if (tab !== 'search') {
            this.destroyPriceTrendChart();
        } else if (this.flowStep === 'priceCompare') {
            this.schedulePriceTrendChartRender();
        }

        await this.service.render();
    }

    public async openHomeEntry(entry: HomeEntry) {
        this.activeTab = entry.targetTab;
        this.uploadState = `${entry.title} 열기`;
        await this.service.render();
    }

    public ngOnDestroy() {
        this.destroyPriceTrendChart();
        this.cancelSelectionFrame();
        this.revokePreviewUrl();
    }

    public get hasSelectionRegion(): boolean {
        return Boolean(this.previewImageUrl && this.selectedRegion);
    }

    public get hasCropPreview(): boolean {
        return Boolean(this.cropPreviewUrl && this.selectedRegion);
    }

    public get cropPreviewLabel(): string {
        if (this.selectionRegionSource === 'manual' || this.selectionRegionSource === 'adjusted') return '직접 조정한 영역';
        if (this.selectionRegionSource === 'full_image') return '전체 사진 기준';
        return '추천 영역';
    }

    public get cropPreviewAlt(): string {
        return `${this.cropPreviewLabel} 미리보기`;
    }

    public get selectedRegionBasisLabel(): string {
        if (this.selectedRegion && !this.selectedCategoryId && this.selectionRegionSource === 'candidate') return '추천 영역 · 카테고리 확인 필요';
        return this.selectedItemCategoryLabel ? `${this.selectedItemCategoryLabel} 영역 검색 기준` : '카테고리 선택 필요';
    }

    public get selectedRegionModeLabel(): string {
        if (this.selectionRegionSource === 'full_image') return '전체 사진 기준';
        if (this.selectionRegionSource === 'candidate') return '추천 영역';
        if (this.selectionRegionSource === 'category_default') return this.selectedItemCategoryLabel ? `${this.selectedItemCategoryLabel} 기본 영역` : '카테고리 기본 영역';
        if (this.selectionRegionSource === 'manual' || this.selectionRegionSource === 'adjusted') return '사용자 지정 영역';
        return '영역 미선택';
    }

    public get selectedRegionStatusLabel(): string {
        if (!this.selectedRegion) return '선택 전';
        const activeTarget = this.searchTargets.find((target) => target.id === this.activeSearchTargetId);
        if (activeTarget?.adjustmentStatus === 'manual' || activeTarget?.adjustmentStatus === 'adjusted') return '사용자 지정';
        if (activeTarget?.adjustmentStatus === 'candidate') return '확인 필요';
        if (activeTarget?.adjustmentStatus === 'full_image') return '전체 기준';
        if (this.selectionRegionSource === 'adjusted') return '사용자 지정';
        return '조정 가능';
    }

    public get selectedRegionCoordinateLabel(): string {
        if (!this.selectedRegion) return '선택 영역 없음';
        if (this.selectionRegionSource === 'full_image') return '전체 사진 100%';
        return `x ${this.formatRegionNumber(this.selectedRegion.x)}%, y ${this.formatRegionNumber(this.selectedRegion.y)}%, ${this.formatRegionNumber(this.selectedRegion.width)}% x ${this.formatRegionNumber(this.selectedRegion.height)}%`;
    }

    public get selectedRegionInstruction(): string {
        if (!this.selectedRegion) return this.imageAnalysisMessage || '추천 후보가 없어요. 카테고리를 고르고 직접 영역을 맞춰주세요.';
        if (this.imageAnalysisStatus === 'low_confidence') return '자동 감지 신뢰도가 낮습니다. 영역과 카테고리를 확인한 뒤 필요하면 직접 조정하세요.';
        if (this.imageAnalysisStatus === 'multiple_items_confused') return '여러 후보가 겹쳐 보입니다. 찾고 싶은 옷에 맞게 영역을 확정해 주세요.';
        if (!this.selectedCategoryId && this.selectionRegionSource === 'candidate') return '추천 영역을 찾았어요. 카테고리를 확인해 주세요.';
        if (!this.selectedCategoryId) return '카테고리를 확인해야 검색할 수 있습니다.';
        if (this.selectionRegionSource === 'candidate') return '추천 영역을 찾았어요. 필요하면 직접 조정하세요.';
        if (this.selectedCategoryId === 'bag' && this.selectionRegionSource === 'category_default') {
            return '가방 위치는 사진마다 달라 기본 영역을 필요에 맞게 움직여주세요.';
        }
        return '영역을 움직여 찾고 싶은 옷에 맞춰주세요. 이후 카테고리를 바꿔도 영역은 유지됩니다.';
    }

    public get hasSelectionRecoveryActions(): boolean {
        return this.selectionRecoveryActions.length > 0;
    }

    public get selectionRecoveryActions(): RecoveryAction[] {
        if (this.imageAnalysisStatus === 'no_item_detected') {
            return this.recoveryActions(['adjust_region', 'change_category', 'search_full_image', 'upload_new_photo']);
        }
        if (this.imageAnalysisStatus === 'low_confidence') {
            return this.recoveryActions(['adjust_region', 'change_category', 'search_full_image']);
        }
        if (this.imageAnalysisStatus === 'multiple_items_confused') {
            return this.recoveryActions(['adjust_region', 'change_category', 'search_full_image']);
        }
        if (this.imageAnalysisStatus === 'unsupported_image' || this.imageAnalysisStatus === 'non_fashion_image') {
            return this.recoveryActions(['upload_new_photo', 'adjust_region']);
        }
        return [];
    }

    public get selectionIssueTitle(): string {
        if (this.imageAnalysisStatus === 'no_item_detected') return '옷 영역을 자동으로 찾지 못했습니다';
        if (this.imageAnalysisStatus === 'low_confidence') return '자동 감지 신뢰도가 낮습니다';
        if (this.imageAnalysisStatus === 'multiple_items_confused') return '여러 옷 후보가 겹쳐 있습니다';
        if (this.imageAnalysisStatus === 'unsupported_image') return '지원하지 않는 이미지입니다';
        if (this.imageAnalysisStatus === 'non_fashion_image') return '패션 상품으로 판단하기 어렵습니다';
        return '영역 확인 필요';
    }

    public get selectionIssueMessage(): string {
        return this.imageAnalysisMessage || '같은 상품처럼 보이는 임의 결과를 만들지 않고, 사용자가 확정한 영역과 카테고리로만 검색합니다.';
    }

    public get searchActionLabel(): string {
        if (!this.selectedCategoryId) return '카테고리를 확인해 주세요';
        if (!this.hasSelectionRegion) return '영역을 맞춰주세요';
        if (this.selectionRegionSource === 'full_image') return '전체 사진 기준 검색';
        return this.selectedItemCategoryLabel ? `${this.selectedItemCategoryLabel}로 검색` : '이 영역으로 검색';
    }

    public startSelectionMove(event: PointerEvent) {
        if (!this.selectedRegion) return;
        this.beginSelectionPointer(event, 'move', null);
    }

    public startSelectionResize(event: PointerEvent, handle: SelectionRegionHandle) {
        if (!this.selectedRegion) return;
        this.beginSelectionPointer(event, 'resize', handle);
    }

    public handleSelectionPointerMove(event: PointerEvent) {
        const state = this.selectionPointerState;
        if (!state || state.pointerId !== event.pointerId) return;

        event.preventDefault();
        event.stopPropagation();
        const deltaX = state.frameWidth > 0 ? ((event.clientX - state.startX) / state.frameWidth) * 100 : 0;
        const deltaY = state.frameHeight > 0 ? ((event.clientY - state.startY) / state.frameHeight) * 100 : 0;
        const nextRegion = state.mode === 'resize'
            ? this.resizeRegion(state.startRegion, deltaX, deltaY, state.handle)
            : this.moveRegion(state.startRegion, deltaX, deltaY);

        this.pendingSelectionRegion = nextRegion;
        this.latestSelectionRegion = nextRegion;
        this.scheduleSelectionFrame();
    }

    public async finishSelectionPointer(event: PointerEvent) {
        const state = this.selectionPointerState;
        if (!state || state.pointerId !== event.pointerId) return;

        this.selectionPointerState = null;
        this.cancelSelectionFrame();
        event.preventDefault();
        event.stopPropagation();

        const nextRegion = this.latestSelectionRegion || this.pendingSelectionRegion || state.startRegion;
        this.syncSelectionElementToPercent(nextRegion);
        this.releasePointerCapture(event);
        this.pendingSelectionRegion = null;
        this.latestSelectionRegion = null;
        this.isSelectionDragging = false;
        this.setSelectionElementDragging(false);
        this.applySelectedRegion(nextRegion, 'adjusted');
        this.uploadState = this.selectedCategoryId ? `${this.selectedItemCategoryLabel} 사용자 지정 영역` : '사용자 지정 영역 · 카테고리 선택 필요';
        await this.service.render();
        this.clearSelectionElementInlineStyles();
    }

    public async nudgeSelection(direction: 'up' | 'down' | 'left' | 'right') {
        if (!this.selectedRegion) {
            this.applySelectedRegion(this.defaultRegionForCategory(this.selectedCategoryId || 'other'), 'manual');
        }

        if (!this.selectedRegion) return;

        const step = 3;
        const deltaX = direction === 'left' ? -step : direction === 'right' ? step : 0;
        const deltaY = direction === 'up' ? -step : direction === 'down' ? step : 0;
        this.applySelectedRegion(this.moveRegion(this.selectedRegion, deltaX, deltaY), 'adjusted');
        this.uploadState = this.selectedCategoryId ? `${this.selectedItemCategoryLabel} 사용자 지정 영역` : '사용자 지정 영역 · 카테고리 선택 필요';
        await this.service.render();
    }

    public async scaleSelection(scale: 'up' | 'down') {
        if (!this.selectedRegion) {
            this.applySelectedRegion(this.defaultRegionForCategory(this.selectedCategoryId || 'other'), 'manual');
        }

        if (!this.selectedRegion) return;

        const nextRegion = this.scaleRegion(this.selectedRegion, scale === 'up' ? 1.12 : 0.88);
        this.applySelectedRegion(nextRegion, 'adjusted');
        this.uploadState = this.selectedCategoryId ? `${this.selectedItemCategoryLabel} 사용자 지정 영역` : '사용자 지정 영역 · 카테고리 선택 필요';
        await this.service.render();
    }

    public async resetSelectionRegion() {
        this.selectedDetectedItemId = '';
        this.manualSelectionRequested = true;
        if (!this.selectedCategoryId) {
            this.applySelectedRegion(this.defaultRegionForCategory('other'), 'manual');
            this.uploadState = '영역을 맞춰주세요 · 카테고리 선택 필요';
            await this.service.render();
            return;
        }

        this.applySelectedRegion(this.defaultRegionForCategory(this.selectedCategoryId), 'category_default');
        this.uploadState = this.categorySearchStateLabel(this.selectedCategoryId);
        await this.service.render();
    }

    public get selectedDetectedItem(): DetectedSearchItem | null {
        return this.detectedItems.find((item) => item.id === this.selectedDetectedItemId) || null;
    }

    public get selectedChoiceLabel(): string {
        const categoryLabel = this.selectedItemCategoryLabel;
        if (this.selectionRegionSource === 'full_image') return '전체 사진으로 검색';
        if (this.selectedRegion && categoryLabel) return `${categoryLabel} · ${this.selectedRegionModeLabel}`;
        if (this.selectedRegion) return `${this.selectedRegionModeLabel} · 카테고리 확인 필요`;
        if (categoryLabel) return `${categoryLabel} 영역을 맞춰주세요`;
        return '카테고리 선택 필요';
    }

    public get selectedChoiceSummary(): string {
        const categoryLabel = this.selectedItemCategoryLabel;
        if (this.selectionRegionSource === 'full_image') return '전체 사진으로 검색합니다.';
        if (this.selectedRegion && categoryLabel && this.isUserControlledRegionSource(this.selectionRegionSource)) {
            return `사용자 지정 영역을 유지한 채 ${categoryLabel} 분류로 검색합니다.`;
        }
        if (this.selectedRegion && categoryLabel && this.selectionRegionSource === 'candidate') {
            return `${categoryLabel} 추천 영역입니다. 확인 후 직접 조정할 수 있습니다.`;
        }
        if (this.selectedRegion && this.selectionRegionSource === 'candidate') {
            return '추천 영역을 찾았어요. 카테고리를 확인하면 검색할 수 있습니다.';
        }
        if (this.selectedRegion && categoryLabel) return `${categoryLabel} 기본 영역으로 시작했습니다. 직접 조정할 수 있습니다.`;
        if (categoryLabel) return `${categoryLabel} 영역을 맞춘 뒤 검색할 수 있습니다.`;
        return '카테고리를 선택해야 결과를 볼 수 있습니다.';
    }

    public get selectedChoiceHelpText(): string {
        if (this.selectedRegion) {
            return this.selectedRegionInstruction;
        }

        return this.imageAnalysisMessage || '카테고리를 선택하면 기본 영역으로 시작합니다. 직접 조정한 뒤에는 카테고리만 바뀝니다.';
    }

    public get visibleItemCategoryOptions(): ItemCategoryOption[] {
        return this.itemCategoryOptions.filter((option) => this.primaryItemCategoryIds.includes(option.id));
    }

    public get searchTargetSummaries(): SearchTargetSummary[] {
        return this.searchTargets.map((target) => {
            const categoryLabel = this.itemCategoryLabel(target.selectedCategory || '') || target.label.replace(/\s*타깃$/, '');
            const regionLabel = this.searchTargetRegionLabel(target);
            const statusLabel = this.searchTargetAdjustmentLabel(target.adjustmentStatus);

            return {
                id: target.id,
                categoryId: target.selectedCategory || 'other',
                label: `${categoryLabel} 타깃`,
                meta: [regionLabel, statusLabel].filter(Boolean).join(' · '),
                active: target.id === this.activeSearchTargetId
            };
        });
    }

    public get resultBasisLabel(): string {
        const categoryLabel = this.selectedSearchContextLabel();
        return `${categoryLabel} 기준 결과`;
    }

    public get resultRegionBasisLabel(): string {
        if (this.selectionRegionSource === 'manual' || this.selectionRegionSource === 'adjusted') return '사용자 지정 영역';
        if (this.selectionRegionSource === 'full_image') return '전체 사진 기준';
        if (this.selectionRegionSource === 'candidate') return '추천 영역';
        if (this.selectionRegionSource === 'category_default') return '카테고리 기본 영역';
        return '선택 영역 기준';
    }

    public get resultBasisSummary(): string {
        return this.resultRegionBasisLabel;
    }

    public get selectedItemCategoryLabel(): string {
        return this.itemCategoryLabel(this.selectedCategoryId);
    }

    public get canOpenResults(): boolean {
        return Boolean(this.previewImageUrl && this.selectedCategoryId && this.selectedRegion);
    }

    public get hasResultProducts(): boolean {
        return this.resultSections.some((section) => section.products.length > 0);
    }

    public get hasOnlySimilarResults(): boolean {
        const exactCount = this.resultProductCount('exact');
        const wholesaleCount = this.resultProductCount('wholesale');
        const similarCount = this.resultProductCount('similar');
        return similarCount > 0 && exactCount === 0 && wholesaleCount === 0;
    }

    public get resultNotice(): SearchNotice | null {
        if (this.hasOnlySimilarResults) {
            return {
                code: 'no_exact_match',
                title: '동일 상품으로 볼 후보가 없습니다',
                message: '아래 후보는 같은 상품이 아니라 디자인이 비슷한 참고 상품입니다. 실구매 전 상품명, 소재, 상세 이미지를 따로 확인해 주세요.',
                actions: this.recoveryActions(['view_similar_designs', 'adjust_region', 'change_category'])
            };
        }

        const issue = this.primarySearchIssue();
        if (!issue && this.hasResultProducts) return null;

        const code = issue?.code || this.searchUiState.status as ProductSearchIssueCode;
        const actions = this.recoveryActions(issue?.recommended_actions || this.defaultRecoveryActionIds(code));

        if (code === 'no_listing_available') {
            return {
                code,
                title: '판매 가능한 상품 후보가 없습니다',
                message: issue?.message || '동일 상품이나 유사 상품처럼 보이는 임의 결과를 채우지 않았습니다. 영역, 카테고리, 사진을 바꿔 다시 검색해 주세요.',
                actions
            };
        }

        if (code === 'platform_error') {
            return {
                code,
                title: '상품 검색 연동에 실패했습니다',
                message: issue?.message || '플랫폼 조회가 실패해 결과를 확정할 수 없습니다. 실패를 숨기기 위해 무관한 상품을 표시하지 않습니다.',
                actions
            };
        }

        if (code === 'no_exact_match') {
            return {
                code,
                title: '동일 상품 근거가 부족합니다',
                message: issue?.message || '동일 상품 또는 같은 도매택으로 묶을 근거가 부족해 유사 디자인과 분리했습니다.',
                actions
            };
        }

        if (code === 'no_item_detected' || code === 'low_confidence') {
            return {
                code,
                title: code === 'no_item_detected' ? '옷 영역을 찾지 못했습니다' : '검색 신뢰도가 낮습니다',
                message: issue?.message || this.imageAnalysisMessage || '사용자가 확정한 영역과 카테고리로 다시 검색해 주세요.',
                actions
            };
        }

        if (!this.hasResultProducts && this.hasActiveMarketFilters()) {
            return {
                code: 'no_listing_available',
                title: '필터에 맞는 후보가 없습니다',
                message: '현재 필터 조건에서는 표시할 후보가 없습니다. 필터를 줄이거나 유사 디자인을 참고해 주세요.',
                actions: this.recoveryActions(['view_similar_designs', 'adjust_region', 'change_category'])
            };
        }

        if (!this.hasResultProducts) {
            return {
                code: 'search_failed',
                title: '검색 결과가 없습니다',
                message: '동일 상품, 도매택 추정, 유사 디자인 후보를 찾지 못했습니다. 다른 영역을 선택하거나 새 사진을 올려주세요.',
                actions
            };
        }

        return null;
    }

    public get resultEmptyTitle(): string {
        return this.resultNotice?.title || '검색 결과가 없습니다';
    }

    public get resultEmptyMessage(): string {
        return this.resultNotice?.message || '다른 영역을 선택하거나 새 사진을 올려주세요.';
    }

    public get resultRecoveryActions(): RecoveryAction[] {
        return this.resultNotice?.actions || this.recoveryActions(['adjust_region', 'change_category', 'upload_new_photo']);
    }

    public get resultFilterChips(): FilterChip[] {
        const chips = this.sharedFilterChips();

        if (this.resultFilters.exactOnly) {
            chips.unshift({ id: 'exactOnly', label: '동일 상품만' });
        } else {
            if (!this.resultFilters.includeWholesale) chips.unshift({ id: 'excludeWholesale', label: '도매택 제외' });
            if (!this.resultFilters.includeSimilar) chips.unshift({ id: 'excludeSimilar', label: '유사 제외' });
        }

        return chips;
    }

    public get listingFilterChips(): FilterChip[] {
        return this.sharedFilterChips();
    }

    public get resultFilterCountLabel(): string {
        const count = this.resultSections.reduce((sum, section) => sum + section.products.length, 0);
        return `${count}개 후보`;
    }

    public get listingFilterCountLabel(): string {
        return `${this.priceCompareRows.length}개 판매처`;
    }

    public get listingSortLabel(): string {
        return this.sortLabel(this.listingSortMode);
    }

    public get activeFilterCount(): number {
        return this.resultFilterChips.length;
    }

    public get filterSheetTitle(): string {
        return this.filterSheetContext === 'results' ? '검색 결과 필터' : '판매처 필터';
    }

    public get filterSheetSummary(): string {
        return this.filterSheetContext === 'results' ? this.resultFilterCountLabel : this.listingFilterCountLabel;
    }

    public get platformFilterOptions(): Platform[] {
        const priority = [
            'platform-musinsa',
            'platform-29cm',
            'platform-wconcept',
            'platform-zigzag',
            'platform-ably',
            'platform-naver-smartstore',
            'platform-hago',
            'platform-personal-mall'
        ];

        return [...this.platforms].sort((a, b) => {
            const aIndex = priority.indexOf(a.id);
            const bIndex = priority.indexOf(b.id);
            if (aIndex !== bIndex) return (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) - (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex);
            return a.name.localeCompare(b.name, 'ko');
        });
    }

    public get hasSavedItems(): boolean {
        return this.savedProductCards.length > 0;
    }

    public get hasAlertItems(): boolean {
        return this.alertListItems.length > 0;
    }

    public get savedCountLabel(): string {
        return `${this.savedProductCards.length}개 저장`;
    }

    public get alertCountLabel(): string {
        const priceCount = this.alertListItems.filter((item) => item.category === 'price').length;
        const restockCount = this.alertListItems.filter((item) => item.category === 'restock').length;
        return `가격 ${priceCount}개 · 재입고 ${restockCount}개`;
    }

    public get visibleAlertConditionOptions(): AlertConditionOption[] {
        if (this.alertEditor?.mode === 'restock') {
            return this.alertConditionOptions.filter((option) => option.alertType === 'restock');
        }

        return this.alertConditionOptions.filter((option) => option.alertType !== 'restock');
    }

    public get canSaveAlertSettings(): boolean {
        if (!this.alertEditor) return false;

        const condition = this.findAlertConditionOption(this.alertEditor.conditionId);
        if (!condition) return false;
        if (this.alertEditor.channels.length === 0) return false;
        if (!condition.requiresTargetPrice) return true;

        return this.parseAlertTargetPrice(this.alertEditor.targetPriceInput) > 0;
    }

    public get alertSaveButtonLabel(): string {
        return this.alertEditor?.existingAlertId ? '수정 저장' : '알림 저장';
    }

    public get selectedProductSaveLabel(): string {
        if (!this.selectedResultProduct) return '저장';
        return this.isProductSaved(this.selectedResultProduct.clusterId) ? '저장됨' : '저장';
    }

    public get isSelectedProductSaved(): boolean {
        return Boolean(this.selectedResultProduct && this.isProductSaved(this.selectedResultProduct.clusterId));
    }

    public get selectedCluster(): ProductCluster | null {
        if (!this.selectedResultProduct) return null;
        return this.findCluster(this.selectedResultProduct.clusterId) || null;
    }

    public get selectedClusterMetaLabel(): string {
        const cluster = this.selectedCluster;
        if (!cluster) return '브랜드/도매택 확인 필요';

        const labels = [
            cluster.brand_name ? `브랜드 ${cluster.brand_name}` : '',
            cluster.wholesale_tag_name ? `도매택 ${cluster.wholesale_tag_name}` : ''
        ].filter(Boolean);

        return labels.length > 0 ? labels.join(' · ') : '브랜드/도매택 확인 필요';
    }

    public get selectedClusterSpecLabel(): string {
        const cluster = this.selectedCluster;
        if (!cluster) return '';

        return [
            cluster.category,
            cluster.colors.join(', '),
            cluster.materials.join(', ')
        ].filter(Boolean).join(' · ');
    }

    private createPriceCompareSummary(): PriceCompareSummary | null {
        if (!this.selectedResultProduct) return null;

        const clusterListings = this.getMarketFilteredListingsForCluster(this.selectedResultProduct.clusterId);
        const lowestOffer = this.lowestOfferForListings(clusterListings, this.selectedResultProduct.clusterId);
        const lowestCandidate = lowestOffer.lowestCandidate;
        const lowestListing = lowestCandidate?.listing || null;
        if (!lowestCandidate || !lowestListing) return null;

        const breakdown = lowestCandidate.breakdown;
        const freshness = lowestCandidate.freshness || this.listingFreshness(lowestListing);
        const effectivePrice = lowestCandidate.publicEffectivePrice;
        const publicDiscount = breakdown.instantDiscount + breakdown.publicCoupon;
        const labels = this.lowestOfferChipLabels(lowestCandidate);
        const shippingUnknown = breakdown.unknownComponents.includes('shipping_fee');
        const couponUnknown = breakdown.unknownComponents.includes('public_coupon');
        const freeShippingUnknown = breakdown.unknownComponents.includes('free_shipping_condition');

        return {
            listingCountLabel: `${clusterListings.length}개 판매처 비교`,
            sellerPlatformLabel: this.platformAndSellerLabel(lowestListing),
            lowestEffectivePriceLabel: this.formatWon(effectivePrice),
            listedPriceLabel: `상품가 ${this.formatWon(breakdown.basePrice)}`,
            publicDiscountLabel: couponUnknown ? '공개 혜택 확인 필요' : publicDiscount > 0 ? `공개 혜택 -${this.formatWon(publicDiscount)}` : '공개 혜택 없음',
            shippingLabel: shippingUnknown ? '배송비 확인 필요' : breakdown.shippingFee > 0 ? `배송비 포함 +${this.formatWon(breakdown.shippingFee)}` : '배송비 포함 · 무료배송',
            freeShippingConditionLabel: freeShippingUnknown ? '무료배송 조건 확인 필요' : lowestListing.free_shipping_condition || '무료배송 조건 없음',
            stockLabel: this.clusterStockSummary(clusterListings),
            checkedAtLabel: lowestCandidate.checkedAt ? `가격 확인 시점 ${this.formatCheckedAt(lowestCandidate.checkedAt)}` : '가격 확인 시점 없음',
            freshnessLabel: this.freshnessBadgeLabel(freshness),
            freshnessMetaLabel: this.freshnessMetaLabel(freshness),
            referencePolicyLabel: breakdown.needsVerification
                ? '배송비, 쿠폰, 재고 중 확인되지 않은 항목이 있어 실구매가 확정값으로 보지 않습니다. 구매 전 판매처에서 조건을 확인해 주세요.'
                : freshness.status === 'stale'
                ? '확인 필요 상태의 최저가는 구매 전 판매처에서 재확인이 필요합니다. 개인 적립금, 개인 쿠폰, 멤버십 할인은 참고 혜택으로만 표시합니다.'
                : '마지막 확인 기준입니다. 개인 적립금, 개인 쿠폰, 멤버십 할인은 참고 혜택으로만 표시합니다.',
            labels: Array.from(new Set(labels.map((label) => this.safeStatusText(label)))).slice(0, 5)
        };
    }

    public get priceCompareNotice(): SearchNotice | null {
        if (!this.selectedResultProduct) return null;

        const rows = this.priceCompareRows;
        if (rows.length === 0) {
            return {
                code: 'no_listing_available',
                title: '연결된 판매처가 없습니다',
                message: '이 상품군에 현재 비교 가능한 판매처가 없습니다. 동일 상품처럼 보이도록 임의 판매처를 채우지 않았습니다.',
                actions: this.recoveryActions(['view_similar_designs', 'adjust_region', 'upload_new_photo'])
            };
        }

        const failedRows = rows.filter((row) => row.freshnessStatus === 'failed' || row.freshnessStatus === 'api_pending');
        if (failedRows.length > 0) {
            return {
                code: 'platform_error',
                title: '일부 판매처 가격 확인에 실패했습니다',
                message: '확인 실패 또는 연동 대기 판매처는 최저가 판단에서 제외하고 참고 상태로만 표시합니다.',
                actions: this.recoveryActions(['view_similar_designs', 'upload_new_photo'])
            };
        }

        const comparableRows = rows.filter((row) => !row.excludedFromLowest);
        const allComparableRowsAreStale = comparableRows.length > 0 && comparableRows.every((row) => row.freshnessStatus === 'stale' || row.freshnessStatus === 'expired');
        if (comparableRows.length === 0 || allComparableRowsAreStale || !this.priceCompareSummary) {
            return {
                code: 'stale_price',
                title: '가격 확인이 오래되었습니다',
                message: '확인 필요 상태의 판매처는 최저가 근거에 경고를 함께 표시합니다. 구매 전 판매처에서 최신 가격을 확인해 주세요.',
                actions: this.recoveryActions(['view_similar_designs', 'upload_new_photo'])
            };
        }

        return null;
    }

    public get priceCompareEmptyTitle(): string {
        return this.priceCompareNotice?.title || '가격 비교 정보가 없습니다';
    }

    public get priceCompareEmptyMessage(): string {
        return this.priceCompareNotice?.message || '이 상품군에 연결된 판매처 데이터가 없습니다.';
    }

    public get priceCompareEmptyActions(): RecoveryAction[] {
        return this.priceCompareNotice?.actions || this.recoveryActions(['view_similar_designs', 'adjust_region']);
    }

    public trackById(index: number, item: { id?: string | number | null } | null): string | number {
        return item?.id ?? index;
    }

    public trackByLabel(index: number, item: string | { label?: string | null } | null): string | number {
        if (typeof item === 'string') return item;
        return item?.label || index;
    }

    public trackBySection(index: number, section: ResultSection | null): string | number {
        return section?.id || index;
    }

    public trackByResultProduct(index: number, product: ResultProduct | null): string | number {
        return product?.clusterId || index;
    }

    public trackByPriceRow(index: number, row: PriceCompareRow | null): string | number {
        return row?.id || index;
    }

    public trackByOptionRow(index: number, row: ListingOptionStockRow | null): string | number {
        return row?.id || index;
    }

    public trackByHomeEntry(index: number, item: HomeEntry | null): string | number {
        return item ? `${item.title}:${item.targetTab}` : index;
    }

    public trackBySavedCard(index: number, item: SavedProductCard | null): string | number {
        return item?.id || item?.clusterId || index;
    }

    public trackByAlertItem(index: number, item: AlertListItem | null): string | number {
        return item?.id || index;
    }

    public trackByTrendWindow(_index: number, windowDays: PriceTrendWindow): number {
        return windowDays;
    }

    public percent(value: number): string {
        const normalizedValue = Math.abs(value) <= 1 ? value * 100 : value;
        const safeValue = Math.max(0, Math.min(100, normalizedValue));
        return `${safeValue}%`;
    }

    public scoreLabel(value: number): string {
        return `${Math.round(value * 100)}%`;
    }

    public detectionChipMeta(item: DetectedSearchItem): string {
        const categoryLabel = this.itemCategoryIdFromCategory(item.category)
            ? this.itemCategoryLabel(this.itemCategoryIdFromCategory(item.category))
            : '참고 위치';
        return `${categoryLabel} 추천 영역 · 직접 조정 가능`;
    }

    public formatWon(value: number): string {
        return `${new Intl.NumberFormat('ko-KR').format(value)}원`;
    }

    public priceTrendWindowLabel(windowDays: PriceTrendWindow): string {
        return `${windowDays}일`;
    }

    public isTargetPriceCondition(conditionId: AlertConditionId): boolean {
        return conditionId === 'target_price';
    }

    public isAlertChannelSelected(channelId: PriceAlertChannel): boolean {
        return Boolean(this.alertEditor && this.alertEditor.channels.includes(channelId));
    }

    public listingSaveLabel(row: PriceCompareRow): string {
        if (!this.selectedResultProduct) return '판매처 저장';
        return this.isListingSaved(this.selectedResultProduct.clusterId, row.id) ? '판매처 저장됨' : '판매처 저장';
    }

    public isMarketFilterSelected(filterId: MarketFilterId): boolean {
        return Boolean(this.resultFilters[filterId]);
    }

    public isPlatformFilterSelected(platformId: string): boolean {
        return this.resultFilters.platformIds.includes(platformId);
    }

    public isListingSaved(clusterId: string, listingId: string | null): boolean {
        return this.savedItems.some((item) => this.savedKey(item.product_cluster_id, item.listing_id, item.listing_option_id || null) === this.savedKey(clusterId, listingId));
    }

    private beginSelectionPointer(event: PointerEvent, mode: SelectionPointerState['mode'], handle: SelectionRegionHandle | null) {
        if (!this.selectedRegion) return;

        const frame = this.findSelectionFrame(event);
        const frameRect = frame?.getBoundingClientRect();
        if (!frameRect || frameRect.width <= 0 || frameRect.height <= 0) return;

        event.preventDefault();
        event.stopPropagation();
        this.capturePointer(event);
        this.isSelectionDragging = true;
        this.pendingSelectionRegion = { ...this.selectedRegion };
        this.latestSelectionRegion = { ...this.selectedRegion };
        this.setSelectionElementDragging(true);
        this.selectionPointerState = {
            pointerId: event.pointerId,
            mode,
            handle,
            startX: event.clientX,
            startY: event.clientY,
            startRegion: { ...this.selectedRegion },
            frameWidth: frameRect.width,
            frameHeight: frameRect.height
        };
    }

    private scheduleSelectionFrame() {
        if (this.selectionAnimationFrame !== null || typeof window === 'undefined') return;

        this.selectionAnimationFrame = window.requestAnimationFrame(() => {
            this.selectionAnimationFrame = null;

            const state = this.selectionPointerState;
            const region = this.pendingSelectionRegion;
            if (!state || !region) return;

            this.paintSelectionRegion(region, state.frameWidth, state.frameHeight);
        });
    }

    private cancelSelectionFrame() {
        if (this.selectionAnimationFrame === null || typeof window === 'undefined') return;

        window.cancelAnimationFrame(this.selectionAnimationFrame);
        this.selectionAnimationFrame = null;
    }

    private paintSelectionRegion(region: CroppedRegion, frameWidth: number, frameHeight: number) {
        const element = this.selectionRegionElementRef?.nativeElement;
        if (!element) return;

        element.style.left = '0px';
        element.style.top = '0px';
        element.style.width = `${(region.width / 100) * frameWidth}px`;
        element.style.height = `${(region.height / 100) * frameHeight}px`;
        element.style.transform = `translate3d(${(region.x / 100) * frameWidth}px, ${(region.y / 100) * frameHeight}px, 0)`;
    }

    private syncSelectionElementToPercent(region: CroppedRegion) {
        const element = this.selectionRegionElementRef?.nativeElement;
        if (!element) return;

        element.style.left = this.percent(region.x);
        element.style.top = this.percent(region.y);
        element.style.width = this.percent(region.width);
        element.style.height = this.percent(region.height);
        element.style.transform = 'translate3d(0, 0, 0)';
    }

    private clearSelectionElementInlineStyles() {
        const element = this.selectionRegionElementRef?.nativeElement;
        if (!element) return;

        element.style.removeProperty('transform');
    }

    private setSelectionElementDragging(isDragging: boolean) {
        const element = this.selectionRegionElementRef?.nativeElement;
        if (!element) return;

        element.classList.toggle('is-dragging', isDragging);
    }

    private capturePointer(event: PointerEvent) {
        const element = this.selectionRegionElement(event);
        if (!element || !element.setPointerCapture) return;

        try {
            element.setPointerCapture(event.pointerId);
        } catch (error) {
            // Pointer capture can fail if the pointer was already released.
        }
    }

    private releasePointerCapture(event: PointerEvent) {
        const element = this.selectionRegionElement(event);
        if (!element || !element.releasePointerCapture) return;

        try {
            element.releasePointerCapture(event.pointerId);
        } catch (error) {
            // Pointer capture can fail if the pointer was already released.
        }
    }

    private selectionRegionElement(event: PointerEvent): HTMLElement | null {
        return (event.currentTarget as HTMLElement | null)?.closest?.('.lookit-selected-region') as HTMLElement | null;
    }

    private findSelectionFrame(event: PointerEvent): HTMLElement | null {
        const target = event.currentTarget as HTMLElement | null;
        return target?.closest?.('.lookit-preview-frame') as HTMLElement | null;
    }

    private isUserControlledRegionSource(source: SelectionRegionSource): boolean {
        return source === 'manual' || source === 'adjusted' || source === 'full_image';
    }

    private shouldPreserveRegionOnCategoryChange(): boolean {
        return Boolean(
            this.selectedRegion &&
            (this.isUserControlledRegionSource(this.selectionRegionSource) || this.selectionRegionSource === 'candidate')
        );
    }

    private applyInitialSearchRecommendation(recommendation: InitialSearchRecommendation | null): boolean {
        if (!recommendation) return false;

        const selectedCategoryId = recommendation.selectedCategoryId && this.isItemCategoryId(recommendation.selectedCategoryId)
            ? recommendation.selectedCategoryId
            : '';
        const suggestedCategoryId = recommendation.suggestedCategoryId || this.suggestedCategoryId || null;
        const normalizedRegion = this.normalizeRegion(recommendation.selectedRegion);
        const searchTargetId = recommendation.searchTargetId || (selectedCategoryId ? this.searchTargetIdForCategory(selectedCategoryId) : `target-recommended-${recommendation.selectedCandidateId || Date.now()}`);
        const targetCategoryId = selectedCategoryId || null;
        const targetCategoryLabel = this.itemCategoryLabel(selectedCategoryId || suggestedCategoryId || '') || '추천';
        const nextTarget: ImageSearchTarget = {
            id: searchTargetId,
            label: `${targetCategoryLabel} 타깃`,
            selectedCategory: targetCategoryId,
            selectedRegion: { ...normalizedRegion },
            regionSource: recommendation.regionSource,
            adjustmentStatus: recommendation.adjustmentStatus,
            suggestedCategoryId,
            suggestedCategoryConfidence: recommendation.categoryConfidence,
            selectedCandidateId: recommendation.selectedCandidateId,
            selectedItemIndex: recommendation.selectedItemIndex,
            updatedAt: new Date().toISOString()
        };

        this.suggestedCategoryId = suggestedCategoryId;
        this.selectedCategoryId = selectedCategoryId;
        this.selectedDetectedItemId = recommendation.selectedCandidateId || '';
        this.selectedRegion = normalizedRegion;
        this.selectionRegionSource = recommendation.regionSource as SelectionRegionSource;
        this.activeSearchTargetId = searchTargetId;
        this.manualSelectionRequested = false;
        this.searchTargets = this.upsertSearchTarget(nextTarget);
        this.syncCurrentImageSearchRequestSelection(nextTarget);
        this.refreshSelectedCropPreparation();

        if (this.currentImageSearchRequest) {
            this.currentImageSearchRequest = {
                ...this.currentImageSearchRequest,
                search_status: selectedCategoryId ? 'matching' : 'awaiting_user_confirmation'
            };
        }

        this.uploadState = this.initialRecommendationUploadState(recommendation, selectedCategoryId);
        return true;
    }

    private initialRecommendationUploadState(recommendation: InitialSearchRecommendation, selectedCategoryId: LookitItemCategoryId | ''): string {
        const categoryLabel = this.itemCategoryLabel(selectedCategoryId);
        if (this.imageAnalysisStatus === 'low_confidence') return '추천 영역 확인 필요';
        if (categoryLabel && !recommendation.needsCategoryConfirmation) return `추천 영역을 찾았어요 · ${categoryLabel}로 검색 가능`;
        return '추천 영역을 찾았어요 · 카테고리를 확인해 주세요';
    }

    private updateImageAnalysisState(response: ImageSearchRequest['image_analysis'] | null) {
        this.imageAnalysisStatus = response?.status || 'detected';
        this.imageAnalysisMessage = this.imageAnalysisGuidance(this.imageAnalysisStatus);
    }

    private imageAnalysisUploadState(): string {
        if (this.imageAnalysisStatus === 'no_item_detected') return '영역을 직접 맞춰주세요';
        if (this.imageAnalysisStatus === 'low_confidence') return '추천 영역 확인 필요';
        if (this.imageAnalysisStatus === 'multiple_items_confused') return '찾을 옷 영역을 선택해 주세요';
        if (this.imageAnalysisStatus === 'unsupported_image') return '지원 이미지 확인 필요';
        if (this.imageAnalysisStatus === 'non_fashion_image') return '옷 사진 확인 필요';
        return '카테고리와 영역을 선택해 주세요';
    }

    private imageAnalysisGuidance(status: ImageAnalysisStatus): string {
        if (status === 'no_item_detected') return '자동으로 잡힌 옷 후보가 없습니다. 카테고리를 고르고 직접 영역을 맞춰주세요.';
        if (status === 'low_confidence') return '자동 감지가 불확실합니다. 추천 영역과 카테고리를 확인한 뒤 필요하면 직접 조정하세요.';
        if (status === 'multiple_items_confused') return '여러 옷 후보가 겹쳐 보입니다. 찾고 싶은 옷 하나에 맞게 영역을 직접 확정해 주세요.';
        if (status === 'unsupported_image') return '지원하지 않는 이미지입니다. jpg, png, webp 이미지로 다시 시도하거나 직접 영역을 선택해 주세요.';
        if (status === 'non_fashion_image') return '패션 상품으로 판단하기 어렵습니다. 옷이 잘 보이는 사진으로 다시 시도하거나 직접 영역을 선택해 주세요.';
        return '';
    }

    private ensureSearchTargets(targets: ImageSearchTarget[], activeCategoryId: string | null | undefined): ImageSearchTarget[] {
        const byId = new Map<string, ImageSearchTarget>();
        targets.forEach((target) => byId.set(target.id, target));

        const categoryIds = new Set<string>(this.defaultSearchTargetCategoryIds);
        if (activeCategoryId) categoryIds.add(activeCategoryId);

        categoryIds.forEach((categoryId) => {
            const targetId = this.searchTargetIdForCategory(categoryId);
            if (!byId.has(targetId)) {
                byId.set(targetId, this.createSearchTarget(categoryId));
            }
        });

        return Array.from(byId.values());
    }

    private createSearchTarget(categoryId: string, region?: CroppedRegion | null, source: SelectionRegionSource = 'category_default'): ImageSearchTarget {
        const selectedRegion = region === undefined ? this.defaultRegionForCategory(categoryId) : region;
        const regionSource = selectedRegion ? source : 'none';

        return {
            id: this.searchTargetIdForCategory(categoryId),
            label: `${this.itemCategoryLabel(categoryId) || '검색'} 타깃`,
            selectedCategory: categoryId,
            selectedRegion: selectedRegion ? { ...selectedRegion } : null,
            regionSource,
            adjustmentStatus: this.adjustmentStatusForRegionSource(regionSource),
            suggestedCategoryId: null,
            suggestedCategoryConfidence: null,
            selectedCandidateId: null,
            selectedItemIndex: null,
            updatedAt: new Date().toISOString()
        };
    }

    private searchTargetIdForCategory(categoryId: string): string {
        return `target-${categoryId}`;
    }

    private activateSearchTarget(categoryId: string, options: { preserveSelectedRegion?: boolean } = {}) {
        const targetId = this.searchTargetIdForCategory(categoryId);
        const preservedRegion = this.selectedRegion ? { ...this.selectedRegion } : null;
        const preservedRegionSource = this.selectionRegionSource;
        this.searchTargets = this.ensureSearchTargets(this.searchTargets, categoryId);
        const target = this.searchTargets.find((item) => item.id === targetId) || this.createSearchTarget(categoryId);

        this.activeSearchTargetId = target.id;
        this.selectedCategoryId = this.isItemCategoryId(categoryId) ? categoryId : 'other';

        if (options.preserveSelectedRegion && preservedRegion) {
            const preservedDetectedItem = this.selectedDetectedItem;
            const isSameSuggestedCategory = preservedDetectedItem
                ? this.itemCategoryIdFromCategory(preservedDetectedItem.suggestedCategoryId || preservedDetectedItem.category) === categoryId
                : false;
            const preservedCandidateId = preservedRegionSource === 'candidate' && isSameSuggestedCategory ? this.selectedDetectedItemId : null;
            const preservedItemIndex = preservedRegionSource === 'candidate' && isSameSuggestedCategory ? preservedDetectedItem?.item_index ?? null : null;
            this.selectedDetectedItemId = preservedCandidateId || '';
            this.selectedRegion = preservedRegion;
            this.selectionRegionSource = preservedRegionSource === 'candidate' || this.isUserControlledRegionSource(preservedRegionSource)
                ? preservedRegionSource
                : 'manual';
            this.syncActiveSearchTarget({
                selectedRegion: { ...preservedRegion },
                regionSource: this.selectionRegionSource,
                adjustmentStatus: this.adjustmentStatusForRegionSource(this.selectionRegionSource),
                suggestedCategoryId: this.suggestedCategoryId,
                selectedCandidateId: preservedCandidateId,
                selectedItemIndex: preservedItemIndex
            });
            this.refreshSelectedCropPreparation();
            return;
        }

        this.selectedDetectedItemId = target.selectedCandidateId || '';
        this.selectedRegion = target.selectedRegion ? { ...target.selectedRegion } : null;
        this.selectionRegionSource = (target.regionSource || 'none') as SelectionRegionSource;

        if (!this.selectedRegion) {
            this.applySelectedRegion(this.defaultRegionForCategory(categoryId), 'category_default');
            return;
        }

        this.syncActiveSearchTarget();
        this.refreshSelectedCropPreparation();
    }

    private syncActiveSearchTarget(overrides: Partial<ImageSearchTarget> = {}) {
        if (!this.selectedCategoryId) return;

        const targetId = this.activeSearchTargetId || this.searchTargetIdForCategory(this.selectedCategoryId);
        const selectedItem = this.selectedDetectedItem;
        const nextTarget: ImageSearchTarget = {
            id: targetId,
            label: `${this.selectedItemCategoryLabel || '검색'} 타깃`,
            selectedCategory: this.selectedCategoryId,
            selectedRegion: this.selectedRegion ? { ...this.selectedRegion } : null,
            regionSource: this.selectionRegionSource,
            adjustmentStatus: this.adjustmentStatusForRegionSource(this.selectionRegionSource),
            suggestedCategoryId: selectedItem?.suggestedCategoryId || this.suggestedCategoryId || null,
            suggestedCategoryConfidence: selectedItem?.suggestedCategoryConfidence ?? selectedItem?.categoryConfidence ?? null,
            selectedCandidateId: this.selectedDetectedItemId || null,
            selectedItemIndex: selectedItem?.item_index ?? null,
            updatedAt: new Date().toISOString(),
            ...overrides
        };

        this.activeSearchTargetId = nextTarget.id;
        this.searchTargets = this.upsertSearchTarget(nextTarget);
        this.syncCurrentImageSearchRequestSelection(nextTarget);
    }

    private upsertSearchTarget(target: ImageSearchTarget): ImageSearchTarget[] {
        const exists = this.searchTargets.some((item) => item.id === target.id);
        const nextTargets = exists
            ? this.searchTargets.map((item) => item.id === target.id ? { ...item, ...target } : item)
            : [...this.searchTargets, target];

        this.searchTargets = nextTargets;
        return nextTargets;
    }

    private syncCurrentImageSearchRequestSelection(target?: ImageSearchTarget) {
        if (!this.currentImageSearchRequest) return;

        const activeTarget = target || this.searchTargets.find((item) => item.id === this.activeSearchTargetId) || null;
        this.currentImageSearchRequest = {
            ...this.currentImageSearchRequest,
            detected_category_id: this.detectedCategoryId,
            suggested_category_id: this.suggestedCategoryId,
            cropped_region: this.selectedRegion,
            ...this.currentCropContext(),
            selected_item_index: activeTarget?.selectedItemIndex ?? null,
            selected_candidate_id: activeTarget?.selectedCandidateId ?? null,
            selected_category_id: this.selectedCategoryId || null,
            search_targets: this.searchTargets,
            active_search_target_id: this.activeSearchTargetId || null
        };
    }

    private adjustmentStatusForRegionSource(regionSource: SelectionRegionSource): ImageSearchTarget['adjustmentStatus'] {
        if (regionSource === 'category_default') return 'defaulted';
        if (regionSource === 'candidate') return 'candidate';
        if (regionSource === 'manual') return 'manual';
        if (regionSource === 'adjusted') return 'adjusted';
        if (regionSource === 'full_image') return 'full_image';
        return 'unselected';
    }

    private searchTargetRegionLabel(target: ImageSearchTarget): string {
        if (target.regionSource === 'full_image') return '전체 사진 기준';
        if (target.regionSource === 'candidate') return '추천 영역';
        if (target.regionSource === 'manual' || target.regionSource === 'adjusted') return '사용자 지정 영역';
        if (target.regionSource === 'category_default') return '카테고리 기본 영역';
        return '영역 미선택';
    }

    private searchTargetAdjustmentLabel(status: ImageSearchTarget['adjustmentStatus']): string {
        if (status === 'adjusted' || status === 'manual') return '사용자 지정';
        if (status === 'candidate') return '확인 필요';
        if (status === 'full_image') return '전체';
        if (status === 'defaulted') return '기본 영역';
        return '선택 전';
    }

    private applySelectedRegion(region: CroppedRegion, source: SelectionRegionSource) {
        const normalizedRegion = this.normalizeRegion(region);
        this.selectedRegion = normalizedRegion;
        this.selectionRegionSource = source;

        if (source === 'manual' || source === 'adjusted') {
            this.manualSelectionRequested = true;
        }

        if (this.currentImageSearchRequest) {
            this.currentImageSearchRequest = {
                ...this.currentImageSearchRequest,
                cropped_region: normalizedRegion,
                search_status: 'matching'
            };
        }

        this.syncActiveSearchTarget();
        this.refreshSelectedCropPreparation();
    }

    private refreshSelectedCropPreparation() {
        if (!this.selectedRegion || !this.previewImageUrl) {
            this.clearCropPreparation();
            return;
        }

        const image = this.previewImageElementRef?.nativeElement || null;
        if (!image || image.naturalWidth <= 0 || image.naturalHeight <= 0) {
            return;
        }

        const previewImageSize = this.imageNaturalSize(image);
        const originalImageSize = this.uploadedImageSize || this.originalImageSize || previewImageSize;
        const displayedImageSize = this.imageDisplayedSize(image);
        const cropRegionOriginal = displayRegionToOriginalPixels(this.selectedRegion, displayedImageSize, originalImageSize);
        const previewCropRegionOriginal = displayRegionToOriginalPixels(this.selectedRegion, displayedImageSize, previewImageSize);
        const localCropId = this.createLocalCropId(this.currentImageSearchRequest?.id || 'local', this.selectedCategoryId || 'unknown', cropRegionOriginal);
        const cropPreviewUrl = this.createCanvasCropPreview(image, previewCropRegionOriginal);
        const preparedCrop = prepareSelectedRegionCrop({
            selectedRegion: this.selectedRegion,
            originalImageSize,
            displayedImageSize,
            cropImageId: localCropId,
            cropRegionOriginal,
            cropPreviewUrl,
            localCropId,
            cropBasis: this.cropBasisForRegionSource(this.selectionRegionSource),
            mimeType: 'image/webp'
        });

        this.originalImageSize = preparedCrop.originalImageSize;
        this.displayedImageSize = preparedCrop.displayedImageSize;
        this.cropRegionOriginal = preparedCrop.cropRegionOriginal;
        this.cropPreviewUrl = preparedCrop.cropPreviewUrl || '';
        this.localCropId = preparedCrop.localCropId || '';
        this.cropImageId = preparedCrop.cropImageId || '';
        this.selectedCrop = preparedCrop.selectedCrop;
    }

    private clearCropPreparation(options: { resetImageContext?: boolean } = {}) {
        this.cropPreviewUrl = '';
        this.localCropId = '';
        this.cropImageId = '';
        this.cropRegionOriginal = null;
        this.selectedCrop = null;

        if (options.resetImageContext) {
            this.originalImageSize = null;
            this.uploadedImageSize = null;
            this.displayedImageSize = null;
            this.normalizedImageId = '';
        }
    }

    private currentCropContext(): CropRegionContext {
        const preparedCrop = this.selectedCrop
            ? {
                originalImageSize: this.originalImageSize,
                displayedImageSize: this.displayedImageSize,
                cropImageId: this.cropImageId || this.selectedCrop?.crop_image_id || null,
                selectedRegion: this.selectedRegion,
                cropRegionOriginal: this.cropRegionOriginal,
                cropPreviewUrl: this.cropPreviewUrl || null,
                localCropId: this.localCropId || null,
                selectedCrop: this.selectedCrop
            }
            : null;

        return cropContextToApiFields(preparedCrop);
    }

    private async ensureCropArtifactForSearch(): Promise<boolean> {
        const request = this.currentImageSearchRequest;
        if (!request || !this.selectedRegion || !this.selectedCategoryId) return false;

        this.refreshSelectedCropPreparation();
        if (!this.selectedCrop || !this.cropRegionOriginal || !this.originalImageSize || !this.displayedImageSize) {
            this.uploadFailureMessage = '선택 영역 crop을 만들 수 없습니다. 영역을 조금 넓히거나 다시 조정해 주세요.';
            this.uploadState = 'crop 생성 필요';
            return false;
        }

        if (!request.upload_image_id) {
            return true;
        }

        if (!this.normalizedImageId) {
            this.uploadFailureMessage = '업로드 이미지의 정규화 ID를 확인할 수 없습니다. 영역을 다시 조정하거나 다른 사진으로 재시도해 주세요.';
            this.uploadState = 'crop 생성 실패';
            return false;
        }

        try {
            const crop = await cropArtifactService.createApiCropArtifactService().createCropArtifact({
                uploadImageId: request.upload_image_id,
                selectedRegion: this.selectedRegion,
                displayedImageSize: this.displayedImageSize,
                originalImageSize: this.originalImageSize,
                normalizedImageId: this.normalizedImageId,
                selectedCategoryId: this.selectedCategoryId,
                regionSource: this.selectionRegionSource,
                localPreviewId: this.localCropId || null,
                cropBasis: this.cropBasisForRegionSource(this.selectionRegionSource),
                mimeType: 'image/webp'
            });
            this.applyCropArtifactResponse(crop);
            return true;
        } catch (error) {
            this.uploadFailureMessage = '선택 영역 crop 생성에 실패했습니다. 영역을 다시 조정하거나 검색 버튼을 다시 눌러 주세요.';
            this.uploadState = 'crop 생성 실패';
            return false;
        }
    }

    private applyCropArtifactResponse(crop: CreateCropArtifactApiResponse) {
        const existingPreviewUrl = this.cropPreviewUrl || crop.previewUrl || '';
        const selectedCrop: SelectedRegionCrop = {
            ...crop.selectedCrop,
            crop_image_id: crop.cropImageId,
            crop_preview_url: crop.selectedCrop.crop_preview_url || existingPreviewUrl || null,
            local_crop_id: crop.selectedCrop.local_crop_id || crop.localPreviewId || this.localCropId || null,
            selected_region: crop.selectedCrop.selected_region || crop.selectedRegion,
            original_image_size: crop.selectedCrop.original_image_size || crop.originalImageSize,
            displayed_image_size: crop.selectedCrop.displayed_image_size || crop.displayedImageSize,
            crop_region_original: crop.selectedCrop.crop_region_original || crop.cropRegionOriginal,
            width: crop.selectedCrop.width || crop.cropWidth,
            height: crop.selectedCrop.height || crop.cropHeight,
            expires_at: crop.selectedCrop.expires_at || crop.expiresAt,
            crop_basis: crop.selectedCrop.crop_basis || crop.cropBasis
        };

        this.cropImageId = crop.cropImageId;
        this.selectedRegion = { ...crop.selectedRegion };
        this.originalImageSize = { ...crop.originalImageSize };
        this.displayedImageSize = { ...crop.displayedImageSize };
        this.cropRegionOriginal = { ...crop.cropRegionOriginal };
        this.localCropId = crop.localPreviewId || selectedCrop.local_crop_id || this.localCropId || '';
        this.cropPreviewUrl = existingPreviewUrl;
        this.selectedCrop = selectedCrop;
        this.syncCurrentImageSearchRequestSelection();
    }

    private imageNaturalSize(image: HTMLImageElement): ImageSize {
        return {
            width: Math.max(1, Math.round(image.naturalWidth || image.width || 1)),
            height: Math.max(1, Math.round(image.naturalHeight || image.height || 1))
        };
    }

    private imageDisplayedSize(image: HTMLImageElement): ImageSize {
        const rect = image.getBoundingClientRect();

        return {
            width: Math.max(1, Math.round(rect.width || image.clientWidth || image.width || image.naturalWidth || 1)),
            height: Math.max(1, Math.round(rect.height || image.clientHeight || image.height || image.naturalHeight || 1))
        };
    }

    private cropBasisForRegionSource(source: SelectionRegionSource): SelectedRegionCropBasis {
        if (source === 'candidate') return 'bounding_box';
        if (source === 'full_image') return 'full_image';
        if (source === 'category_default') return 'bounding_box';
        return 'user_adjusted_region';
    }

    private createLocalCropId(requestId: string, categoryId: string, cropRegionOriginal: CroppedRegion): string {
        const parts = [
            requestId,
            categoryId || 'unknown',
            cropRegionOriginal.x,
            cropRegionOriginal.y,
            cropRegionOriginal.width,
            cropRegionOriginal.height
        ];

        return `local-crop-${parts.join('-')}`;
    }

    private createCanvasCropPreview(image: HTMLImageElement, cropRegionOriginal: CroppedRegion): string {
        if (typeof document === 'undefined') return '';

        const sourceWidth = Math.max(1, Math.round(cropRegionOriginal.width));
        const sourceHeight = Math.max(1, Math.round(cropRegionOriginal.height));
        const previewMaxSide = 112;
        const aspectRatio = sourceWidth / sourceHeight;
        const canvas = document.createElement('canvas');

        if (aspectRatio >= 1) {
            canvas.width = previewMaxSide;
            canvas.height = Math.max(48, Math.round(previewMaxSide / aspectRatio));
        } else {
            canvas.height = previewMaxSide;
            canvas.width = Math.max(48, Math.round(previewMaxSide * aspectRatio));
        }

        const context = canvas.getContext('2d');
        if (!context) return '';

        try {
            context.drawImage(
                image,
                cropRegionOriginal.x,
                cropRegionOriginal.y,
                sourceWidth,
                sourceHeight,
                0,
                0,
                canvas.width,
                canvas.height
            );
            return canvas.toDataURL('image/webp', 0.82);
        } catch (error) {
            return '';
        }
    }

    private normalizeRegion(region: CroppedRegion): CroppedRegion {
        const values = [region.x, region.y, region.width, region.height];
        const isRatioRegion = values.every((value) => Math.abs(value) <= 1);
        const scale = isRatioRegion ? 100 : 1;
        const minSize = 12;
        const width = this.clamp(region.width * scale, minSize, 100);
        const height = this.clamp(region.height * scale, minSize, 100);
        const x = this.clamp(region.x * scale, 0, 100 - width);
        const y = this.clamp(region.y * scale, 0, 100 - height);

        return {
            x: this.roundRegionValue(x),
            y: this.roundRegionValue(y),
            width: this.roundRegionValue(width),
            height: this.roundRegionValue(height)
        };
    }

    private moveRegion(region: CroppedRegion, deltaX: number, deltaY: number): CroppedRegion {
        return this.normalizeRegion({
            ...region,
            x: region.x + deltaX,
            y: region.y + deltaY
        });
    }

    private resizeRegion(region: CroppedRegion, deltaX: number, deltaY: number, handle: SelectionRegionHandle | null): CroppedRegion {
        if (!handle) return this.normalizeRegion(region);

        const minSize = 12;
        let x = region.x;
        let y = region.y;
        let width = region.width;
        let height = region.height;

        if (handle.includes('e')) {
            width = this.clamp(region.width + deltaX, minSize, 100 - region.x);
        }

        if (handle.includes('s')) {
            height = this.clamp(region.height + deltaY, minSize, 100 - region.y);
        }

        if (handle.includes('w')) {
            const maxX = region.x + region.width - minSize;
            x = this.clamp(region.x + deltaX, 0, maxX);
            width = region.x + region.width - x;
        }

        if (handle.includes('n')) {
            const maxY = region.y + region.height - minSize;
            y = this.clamp(region.y + deltaY, 0, maxY);
            height = region.y + region.height - y;
        }

        return this.normalizeRegion({ x, y, width, height });
    }

    private scaleRegion(region: CroppedRegion, scale: number): CroppedRegion {
        const nextWidth = region.width * scale;
        const nextHeight = region.height * scale;
        const centerX = region.x + region.width / 2;
        const centerY = region.y + region.height / 2;

        return this.normalizeRegion({
            x: centerX - nextWidth / 2,
            y: centerY - nextHeight / 2,
            width: nextWidth,
            height: nextHeight
        });
    }

    private defaultRegionForCategory(categoryId: LookitItemCategoryId | string | null | undefined): CroppedRegion {
        return defaultDisplayRegionForCategory(categoryId) || defaultDisplayRegionForCategory('other')!;
    }

    private clamp(value: number, min: number, max: number): number {
        return Math.max(min, Math.min(max, value));
    }

    private roundRegionValue(value: number): number {
        return Math.round(value * 10) / 10;
    }

    private formatRegionNumber(value: number): string {
        return Number.isInteger(value) ? `${value}` : value.toFixed(1);
    }

    private defaultResultFilters(): ResultFilterState {
        return {
            exactOnly: false,
            includeWholesale: true,
            includeSimilar: true,
            inStockOnly: false,
            freeShippingOnly: false,
            publicCouponOnly: false,
            platformIds: []
        };
    }

    private isItemCategoryId(value: string): value is LookitItemCategoryId {
        return this.itemCategoryOptions.some((option) => option.id === value);
    }

    private itemCategoryLabel(categoryId: string | null | undefined): string {
        return this.itemCategoryOptions.find((option) => option.id === categoryId)?.label || '';
    }

    private selectedSearchContextLabel(): string {
        const detectedSubcategory = this.subcategoryLabelFromCategory(this.selectedDetectedItem?.label);
        if (detectedSubcategory) return detectedSubcategory;
        return this.selectedItemCategoryLabel || '선택한 아이템';
    }

    private categorySearchStateLabel(categoryId: string | null | undefined): string {
        const label = this.itemCategoryLabel(categoryId);
        if (!label) return '영역을 맞춰주세요 · 카테고리 선택 필요';
        if (this.isUserControlledRegionSource(this.selectionRegionSource)) return `사용자 지정 영역 · ${label} 검색 준비`;
        if (this.selectionRegionSource === 'candidate') return `추천 영역 · ${label} 검색 준비`;
        return `${label} 기본 영역 확인 중`;
    }

    private categorySelectionStateLabel(categoryId: string | null | undefined, preservedRegion: boolean): string {
        const label = this.itemCategoryLabel(categoryId);
        if (!label) return '카테고리 선택 필요';
        if (preservedRegion && this.selectionRegionSource === 'candidate') return `추천 영역 · ${label} 검색 준비`;
        if (preservedRegion) return `사용자 지정 영역 · ${label} 검색 준비`;
        return `${label} 기본 영역 확인 중`;
    }

    private subcategoryLabelFromCategory(category: string | null | undefined): string {
        const normalizedCategory = (category || '').trim();
        const [rootCategory, subcategory] = normalizedCategory.split('>').map((part) => part.trim());
        if (!subcategory) return '';

        const rootCategoryId = this.itemCategoryIdFromCategory(rootCategory);
        if (rootCategoryId && rootCategoryId !== this.selectedCategoryId) return '';
        return subcategory === '슬랙스' ? '팬츠' : subcategory;
    }

    private itemCategoryIdFromCategory(category: string | null | undefined): LookitItemCategoryId | null {
        const normalizedCategory = (category || '').trim();
        if (this.isItemCategoryId(normalizedCategory)) return normalizedCategory;

        const rootCategory = normalizedCategory.split('>')[0].trim();
        if (rootCategory === '상의') return 'top';
        if (rootCategory === '하의') return 'bottom';
        if (rootCategory === '아우터') return 'outer';
        if (rootCategory === '원피스') return 'dress';
        if (rootCategory === '신발') return 'shoes';
        if (rootCategory === '가방') return 'bag';
        if (rootCategory === '액세서리') return 'accessory';
        if (rootCategory === '확인 필요') return 'unknown';
        if (rootCategory === '기타') return 'other';
        return null;
    }

    private clusterCategoryLabel(cluster: ProductCluster): string {
        return this.itemCategoryLabel(this.itemCategoryIdFromCategory(cluster.category)) || cluster.category.split('>')[0].trim() || '카테고리 확인 필요';
    }

    private savedItemCategoryLabel(item: UserSavedItem, cluster: ProductCluster): string {
        return item.selected_category_label ||
            this.itemCategoryLabel(item.selected_category_id || '') ||
            this.clusterCategoryLabel(cluster);
    }

    private withCategoryContext(categoryLabel: string, label: string): string {
        return categoryLabel ? `${categoryLabel} 기준 · ${label}` : label;
    }

    private withSelectionContext(categoryLabel: string, regionLabel: string, label: string): string {
        return [categoryLabel ? `${categoryLabel} 기준` : '', regionLabel, label].filter(Boolean).join(' · ');
    }

    private currentSelectionRegionLabel(): string {
        if (!this.selectedRegion) return '';
        if (this.selectionRegionSource === 'full_image') return '전체 사진 기준';
        if (this.selectionRegionSource === 'category_default') return this.selectedItemCategoryLabel ? `${this.selectedItemCategoryLabel} 기본 영역` : '카테고리 기본 영역';
        if (this.selectionRegionSource === 'candidate') return '추천 영역';
        return '사용자 지정 영역';
    }

    private savedItemRegionLabel(item: UserSavedItem): string {
        return item.selected_region_label ||
            item.selected_item_label ||
            (item.selected_region ? '검색 영역 기준' : '');
    }

    private sharedFilterChips(): FilterChip[] {
        const chips: FilterChip[] = [];

        if (this.resultFilters.inStockOnly) chips.push({ id: 'inStockOnly', label: '재고 있음' });
        if (this.resultFilters.freeShippingOnly) chips.push({ id: 'freeShippingOnly', label: '무료배송' });
        if (this.resultFilters.publicCouponOnly) chips.push({ id: 'publicCouponOnly', label: '공개 쿠폰' });

        this.resultFilters.platformIds.forEach((platformId) => {
            chips.push({
                id: platformId,
                label: this.platformName(platformId)
            });
        });

        return chips;
    }

    private sortLabel(mode: SortMode): string {
        return this.sortOptions.find((option) => option.id === mode)?.label || '추천순';
    }

    private async loadLookitServiceState() {
        const [
            productClusters,
            listings,
            priceSnapshots,
            platforms,
            sellers,
            savedItems,
            priceAlerts,
            recentImageSearchRequest
        ] = await Promise.all([
            productService.getProductClusters(),
            priceService.getListings(),
            priceService.getSnapshotsByRange({ days: 90 }),
            productService.getPlatforms(),
            productService.getSellers(),
            savedItemService.listSavedItems({ userId: this.currentUserId, anonymousSessionId: this.anonymousSessionId, ownerType: 'anonymous' }),
            alertService.listPriceAlerts({ userId: this.currentUserId }),
            searchService.getRecentImageSearchRequest()
        ]);

        this.productClusters = productClusters;
        this.listings = listings;
        this.priceSnapshots = priceSnapshots;
        this.platforms = platforms;
        this.sellers = sellers;
        this.savedItems = savedItems;
        this.priceAlerts = priceAlerts;
        this.recentImageSearchRequest = recentImageSearchRequest;
    }

    private async loadProductClusterDetail(clusterId: string, trendWindowDays: PriceTrendWindow = this.selectedPriceTrendWindow) {
        const [cluster, listings, priceTrendSummary] = await Promise.all([
            productService.getProductCluster(clusterId),
            priceService.getListingsByProductCluster(clusterId),
            priceService.getPriceTrendByRange({
                productClusterId: clusterId,
                days: trendWindowDays
            })
        ]);

        this.productClusters = this.mergeById(this.productClusters, cluster ? [cluster] : []);
        this.listings = this.mergeById(this.listings, listings);
        this.replacePriceSnapshotsForCluster(clusterId, priceTrendSummary.snapshots);
        this.refreshLookitData();
        this.priceTrendRangeSummary = priceTrendSummary;
    }

    private async loadPriceTrendSnapshotsForSelectedProduct() {
        if (!this.selectedResultProduct) return;

        const clusterId = this.selectedResultProduct.clusterId;
        const priceTrendSummary = await priceService.getPriceTrendByRange({
            productClusterId: clusterId,
            days: this.selectedPriceTrendWindow
        });

        this.priceTrendRangeSummary = priceTrendSummary;
        this.replacePriceSnapshotsForCluster(clusterId, priceTrendSummary.snapshots);
    }

    private replacePriceSnapshotsForCluster(clusterId: string, nextSnapshots: PriceSnapshot[]) {
        const retainedSnapshots = this.priceSnapshots.filter((snapshot) => snapshot.productClusterId !== clusterId);
        this.priceSnapshots = this.mergeById(retainedSnapshots, nextSnapshots);
    }

    private mergeById<T extends { id: string }>(currentItems: T[], nextItems: T[]): T[] {
        const merged = new Map<string, T>();
        currentItems.forEach((item) => merged.set(item.id, item));
        nextItems.forEach((item) => merged.set(item.id, item));
        return Array.from(merged.values());
    }

    private async persistSavedItems() {
        this.savedItems = await savedItemService.saveSavedItems(this.savedItems, {
            userId: this.currentUserId,
            anonymousSessionId: this.anonymousSessionId,
            ownerType: 'anonymous'
        });
    }

    private async upsertSavedItem(savedItem: UserSavedItem) {
        const nextItem = await savedItemService.saveItem(savedItem, {
            userId: this.currentUserId,
            anonymousSessionId: this.anonymousSessionId,
            ownerType: 'anonymous'
        });
        const nextKey = this.savedKey(nextItem.product_cluster_id, nextItem.listing_id, nextItem.listing_option_id || null);
        this.savedItems = [
            nextItem,
            ...this.savedItems.filter((item) => (
                item.id !== nextItem.id &&
                this.savedKey(item.product_cluster_id, item.listing_id, item.listing_option_id || null) !== nextKey
            ))
        ];
    }

    private async removeSavedItemByIdentity(clusterId: string, listingId: string | null, listingOptionId: string | null = null) {
        this.savedItems = await savedItemService.removeSavedItem({
            userId: this.currentUserId,
            anonymousSessionId: this.anonymousSessionId,
            productClusterId: clusterId,
            listingId,
            listingOptionId
        }, {
            userId: this.currentUserId,
            anonymousSessionId: this.anonymousSessionId,
            ownerType: 'anonymous'
        });
    }

    private async persistPriceAlerts() {
        this.priceAlerts = await alertService.savePriceAlerts(this.priceAlerts, { userId: this.currentUserId });
    }

    private alertPreferenceFromAlert(alert: PriceAlert): AlertPreference {
        return {
            id: alert.id,
            saved_item_id: alert.saved_item_id || null,
            alert_scope: alert.alert_scope,
            alert_type: alert.alert_type,
            threshold_price: alert.threshold_price ?? null,
            threshold_percent: alert.threshold_percent ?? null,
            channels: [...alert.channels],
            enabled: alert.enabled
        };
    }

    private async updateSavedItemAlertPreferenceFromAlert(alert: PriceAlert): Promise<void> {
        await this.updateSavedItemAlertPreferenceByIdentity(
            alert.product_cluster_id,
            alert.listing_id,
            alert.listing_option_id || null,
            this.alertPreferenceFromAlert(alert),
            alert.saved_item_id || null
        );
    }

    private async updateSavedItemAlertPreferenceByIdentity(
        clusterId: string,
        listingId: string | null,
        listingOptionId: string | null,
        alertPreference: AlertPreference | null,
        savedItemId: string | null = null
    ): Promise<void> {
        const updatedItem = await savedItemService.updateAlertPreference({
            id: savedItemId,
            userId: this.currentUserId,
            anonymousSessionId: this.anonymousSessionId,
            productClusterId: clusterId,
            listingId,
            listingOptionId,
            alertPreference
        }, {
            userId: this.currentUserId,
            anonymousSessionId: this.anonymousSessionId,
            ownerType: 'anonymous'
        });

        if (!updatedItem) return;

        const updatedKey = this.savedKey(
            updatedItem.product_cluster_id,
            updatedItem.listing_id,
            updatedItem.listing_option_id || null
        );
        this.savedItems = [
            updatedItem,
            ...this.savedItems.filter((item) => (
                item.id !== updatedItem.id &&
                this.savedKey(item.product_cluster_id, item.listing_id, item.listing_option_id || null) !== updatedKey
            ))
        ];
    }

    private refreshLookitData() {
        this.resultSections = this.createResultSections();
        this.savedProductCards = this.createSavedProductCards();
        this.alertListItems = this.createAlertListItems();
        this.homeEntries = this.createHomeEntries();
        this.alertBadge = this.enabledAlertCount() || '';
        this.tabs = this.createTabs();

        if (this.selectedResultProduct) {
            const updatedProduct = this.findResultProduct(this.selectedResultProduct.clusterId);
            if (updatedProduct) {
                this.selectedResultProduct = updatedProduct;
            }
            this.refreshPriceInsights();
        } else {
            this.clearPriceInsights();
        }
    }

    private createTabs(): LookitTab[] {
        return [
            { id: 'search', label: '검색', icon: 'search' },
            { id: 'saved', label: '저장', icon: 'saved', badge: this.savedProductCards.length || '' },
            { id: 'alerts', label: '알림', icon: 'alerts', badge: this.alertBadge },
            { id: 'profile', label: '마이', icon: 'profile' }
        ];
    }

    private enabledAlertCount(): number {
        return this.alertListItems.filter((alert) => alert.enabled).length;
    }

    private createSavedProductCards(): SavedProductCard[] {
        return this.savedItems
            .map((item) => this.toSavedProductCard(item))
            .filter((item): item is SavedProductCard => Boolean(item));
    }

    private createAlertListItems(): AlertListItem[] {
        return this.priceAlerts
            .map((alert) => this.toAlertListItem(alert))
            .filter((item): item is AlertListItem => Boolean(item))
            .sort((a, b) => {
                if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
                return a.title.localeCompare(b.title, 'ko');
            });
    }

    private toAlertListItem(alert: PriceAlert): AlertListItem | null {
        const listingOptionId = alert.listing_option_id || null;
        const savedItem = this.findSavedItem(alert.product_cluster_id, alert.listing_id, listingOptionId);
        const cluster = this.findCluster(alert.product_cluster_id);
        if (!savedItem || !cluster) return null;

        const listing = alert.listing_id ? this.findListing(alert.listing_id) : null;
        const option = listingOptionId && listing ? this.findListingOption(listing.id, listingOptionId) : null;
        const currentPrice = this.currentEffectivePriceForSaved(savedItem);
        const thresholdLabel = this.alertThresholdLabel(alert);
        const isRestock = alert.alert_type === 'restock';
        const optionLabel = this.savedOptionLabel(savedItem);
        const categoryLabel = alert.selected_category_label || alert.baseline_category_label || this.savedItemCategoryLabel(savedItem, cluster);
        const regionLabel = alert.selected_region_label || alert.baseline_region_label || this.savedItemRegionLabel(savedItem);

        return {
            id: alert.id,
            category: isRestock ? 'restock' : 'price',
            clusterId: alert.product_cluster_id,
            listingId: alert.listing_id,
            listingOptionId,
            image: alert.baseline_image_url || cluster.representative_image_url,
            imageAlt: alert.baseline_product_name || cluster.canonical_name,
            title: alert.baseline_product_name || cluster.canonical_name,
            brand: cluster.brand_name || cluster.wholesale_tag_name || '브랜드/도매택 확인 필요',
            scopeLabel: this.withSelectionContext(categoryLabel, regionLabel, this.alertScopeLabel(alert, listing, optionLabel)),
            conditionLabel: this.alertConditionLabel(alert),
            categoryLabel: isRestock ? '재입고 알림' : '가격 알림',
            thresholdLabel,
            baselineTitle: isRestock ? '추적 옵션' : '저장 당시',
            savedPriceLabel: isRestock ? optionLabel : (savedItem.saved_price > 0 ? this.formatWon(savedItem.saved_price) : '저장가 확인 필요'),
            currentTitle: isRestock ? '현재 재고' : '현재 기준',
            currentPriceLabel: isRestock ? this.currentOptionStatusLabel(savedItem, option) : (currentPrice !== null ? this.formatWon(currentPrice) : '현재가 확인 필요'),
            channelsLabel: this.alertChannels(alert).map((channel) => this.alertChannelLabel(channel)).join(', '),
            basisLabel: this.withSelectionContext(categoryLabel, regionLabel, isRestock ? '옵션 재고 기준' : '실구매가 기준'),
            statusLabel: alert.enabled ? '켜짐' : '꺼짐',
            enabled: alert.enabled,
            lastSentLabel: alert.last_sent_at ? `마지막 발송 ${this.formatCheckedAt(alert.last_sent_at)}` : '아직 발송 없음'
        };
    }

    private toSavedProductCard(item: UserSavedItem): SavedProductCard | null {
        const cluster = this.findCluster(item.product_cluster_id);
        if (!cluster) return null;

        const clusterListings = this.getListingsForCluster(cluster.id);
        const lowestOffer = this.lowestOfferForListings(clusterListings, cluster.id);
        const lowestListing = lowestOffer.lowestCandidate?.listing || null;
        const currentLowestPrice = lowestOffer.lowestCandidate ? lowestOffer.lowestCandidate.publicEffectivePrice : null;
        const listing = item.listing_id ? this.findListing(item.listing_id) : null;
        const priceChange = this.priceChange(item.saved_price, currentLowestPrice);
        const optionLabel = this.savedOptionLabel(item);
        const alert = this.findAlertForSaved(cluster.id, item.listing_id, item.listing_option_id, item.id);
        const alertConditionLabel = alert ? this.alertConditionLabel(alert) : '설정 안 됨';
        const alertEnabled = Boolean(alert?.enabled);
        const categoryLabel = this.savedItemCategoryLabel(item, cluster);
        const regionLabel = this.savedItemRegionLabel(item);
        const scopeLabel = item.listing_option_id
            ? `옵션 저장 · ${listing ? this.platformAndSellerLabel(listing) : '판매처 확인 필요'}`
            : (item.listing_id ? `판매처 상품 · ${listing ? this.platformAndSellerLabel(listing) : '판매처 확인 필요'}` : '상품군 저장');

        return {
            id: item.id,
            clusterId: cluster.id,
            listingId: item.listing_id,
            listingOptionId: item.listing_option_id || null,
            image: item.image_url || cluster.representative_image_url,
            imageAlt: item.product_name || cluster.canonical_name,
            title: item.product_name || cluster.canonical_name,
            brand: cluster.brand_name || cluster.wholesale_tag_name || '브랜드/도매택 확인 필요',
            saveScopeLabel: this.withSelectionContext(categoryLabel, regionLabel, scopeLabel),
            savedPriceLabel: item.saved_price > 0 ? this.formatWon(item.saved_price) : '저장가 확인 필요',
            currentLowestPriceLabel: currentLowestPrice !== null ? this.formatWon(currentLowestPrice) : '현재가 확인 필요',
            priceChangeAmountLabel: priceChange.amountLabel,
            priceChangePercentLabel: priceChange.percentLabel,
            priceChangeTone: priceChange.tone,
            storeCountLabel: `${clusterListings.length}곳`,
            alertStatusLabel: alert ? (alertEnabled ? alertConditionLabel : `${alertConditionLabel} 꺼짐`) : '설정 안 됨',
            alertConditionLabel,
            alertEnabled,
            savedAtLabel: `저장 ${this.formatCheckedAt(item.created_at)}`,
            currentLowestSellerLabel: lowestListing ? `현재 최저 ${this.platformAndSellerLabel(lowestListing)}` : '현재 최저 판매처 확인 필요',
            optionLabel: optionLabel || '옵션 전체'
        };
    }

    private priceChange(savedPrice: number, currentPrice: number | null): {
        amountLabel: string;
        percentLabel: string;
        tone: SavedPriceChangeTone;
    } {
        if (!savedPrice || currentPrice === null) {
            return {
                amountLabel: '변화 확인 필요',
                percentLabel: '',
                tone: 'unknown'
            };
        }

        const diff = currentPrice - savedPrice;
        const absDiffLabel = this.formatWon(Math.abs(diff));
        const percent = Math.abs((diff / savedPrice) * 100);
        const percentLabel = `${diff > 0 ? '+' : diff < 0 ? '-' : ''}${percent.toFixed(1)}%`;

        if (diff < 0) {
            return {
                amountLabel: `${absDiffLabel} 하락`,
                percentLabel,
                tone: 'drop'
            };
        }

        if (diff > 0) {
            return {
                amountLabel: `${absDiffLabel} 상승`,
                percentLabel,
                tone: 'rise'
            };
        }

        return {
            amountLabel: '변동 없음',
            percentLabel: '0.0%',
            tone: 'same'
        };
    }

    private createSavedItem(clusterId: string, listingId: string | null, listingOption?: ListingOption | ListingOptionStockRow | null): UserSavedItem | null {
        const cluster = this.findCluster(clusterId);
        if (!cluster) return null;

        const listing = listingId ? this.findListing(listingId) : this.lowestOfferForListings(this.getListingsForCluster(clusterId), clusterId).lowestCandidate?.listing || null;
        const optionTargeted = Boolean(listingOption);
        const selectedOption = optionTargeted ? listingOption || null : listing?.options?.[0] || null;
        const savedOffer = listing ? this.lowestOfferForListings([listing], clusterId).lowestCandidate : null;
        const savedPrice = savedOffer ? savedOffer.publicEffectivePrice : 0;
        const listingOptionId = optionTargeted ? selectedOption?.id || null : null;
        const selectedCategoryId = this.selectedCategoryId || this.itemCategoryIdFromCategory(cluster.category);
        const selectedCategoryLabel = this.itemCategoryLabel(selectedCategoryId) || this.clusterCategoryLabel(cluster);
        const selectedRegionLabel = this.currentSelectionRegionLabel();
        const cropContext = this.currentCropContext();
        const now = new Date().toISOString();
        const searchContext = {
            source_request_id: this.currentImageSearchRequest?.id || null,
            selected_category_id: selectedCategoryId,
            selected_category_label: selectedCategoryLabel,
            selected_item_label: selectedRegionLabel || this.selectedDetectedItem?.label || null,
            selected_region_label: selectedRegionLabel || null,
            selected_region: this.selectedRegion ? { ...this.selectedRegion } : null,
            selected_crop: this.selectedCrop ? { ...this.selectedCrop } : null,
            ...cropContext
        };
        const savedPriceSnapshot = {
            amount: savedPrice,
            currency: 'KRW' as const,
            basis: savedPrice > 0 ? 'public_effective_price' as const : 'restock_only' as const,
            listing_id: listing?.id || listingId,
            listing_option_id: listingOptionId,
            listed_price: listing?.listed_price ?? null,
            instant_discount: listing?.instant_discount ?? null,
            public_coupon_discount: listing?.public_coupon_discount ?? null,
            shipping_fee: listing?.shipping_fee ?? null,
            free_shipping_condition: listing?.free_shipping_condition ?? null,
            checked_at: listing?.checked_at || listing?.last_checked_at || null,
            saved_at: now
        };

        return {
            id: `saved-local-${Date.now()}-${clusterId}${listingId ? `-${listingId}` : ''}${listingOptionId ? `-${listingOptionId}` : ''}`,
            user_id: this.currentUserId,
            owner_type: 'anonymous',
            anonymous_session_id: this.anonymousSessionId,
            product_cluster_id: clusterId,
            listing_id: listingId,
            listing_option_id: listingOptionId,
            product_name: cluster.canonical_name,
            image_url: cluster.representative_image_url,
            target_color: selectedOption?.color || cluster.colors[0] || null,
            target_size: selectedOption?.size || null,
            selected_category_id: searchContext.selected_category_id,
            selected_category_label: searchContext.selected_category_label,
            selected_item_label: searchContext.selected_item_label,
            selected_region_label: searchContext.selected_region_label,
            selected_region: searchContext.selected_region,
            ...cropContext,
            source_request_id: searchContext.source_request_id,
            saved_price: savedPrice,
            saved_price_snapshot: savedPriceSnapshot,
            search_context: searchContext,
            source_context: searchContext,
            alert_preference: null,
            note: null,
            saved_at: now,
            created_at: now,
            updated_at: now
        };
    }

    private ensureSavedItem(clusterId: string, listingId: string | null, listingOption?: ListingOption | ListingOptionStockRow | null): UserSavedItem | null {
        const listingOptionId = listingOption?.id || null;
        const existing = this.findSavedItem(clusterId, listingId, listingOptionId);
        if (existing) return existing;

        const savedItem = this.createSavedItem(clusterId, listingId, listingOption);
        if (!savedItem) return null;

        this.savedItems = [savedItem, ...this.savedItems];
        return savedItem;
    }

    private isProductSaved(clusterId: string): boolean {
        return this.savedItems.some((item) => this.savedKey(item.product_cluster_id, item.listing_id, item.listing_option_id || null) === this.savedKey(clusterId, null));
    }

    private savedKey(clusterId: string, listingId: string | null, listingOptionId: string | null = null): string {
        return `${clusterId}::${listingId || 'cluster'}::${listingOptionId || 'all-options'}`;
    }

    private findSavedItem(clusterId: string, listingId: string | null, listingOptionId: string | null = null): UserSavedItem | null {
        return this.savedItems.find((item) => this.savedKey(item.product_cluster_id, item.listing_id, item.listing_option_id || null) === this.savedKey(clusterId, listingId, listingOptionId)) || null;
    }

    private findListing(listingId: string): Listing | null {
        return this.listings.find((listing) => listing.id === listingId) || null;
    }

    private findAlertForSaved(clusterId: string, listingId: string | null, listingOptionId: string | null = null, savedItemId: string | null = null): PriceAlert | null {
        if (savedItemId) {
            const alert = this.priceAlerts.find((item) => item.saved_item_id === savedItemId);
            if (alert) return alert;
        }

        return this.priceAlerts.find((alert) => this.savedKey(alert.product_cluster_id, alert.listing_id, alert.listing_option_id || null) === this.savedKey(clusterId, listingId, listingOptionId)) || null;
    }

    private removeAlertsForSaved(clusterId: string, listingId: string | null, listingOptionId: string | null = null): boolean {
        const targetKey = this.savedKey(clusterId, listingId, listingOptionId);
        const beforeCount = this.priceAlerts.length;
        this.priceAlerts = this.priceAlerts.filter((alert) => this.savedKey(alert.product_cluster_id, alert.listing_id, alert.listing_option_id || null) !== targetKey);
        return beforeCount !== this.priceAlerts.length;
    }

    private openAlertEditor(savedItem: UserSavedItem) {
        const cluster = this.findCluster(savedItem.product_cluster_id);
        if (!cluster) return;

        const listing = savedItem.listing_id ? this.findListing(savedItem.listing_id) : null;
        const listingOption = savedItem.listing_option_id && listing ? this.findListingOption(listing.id, savedItem.listing_option_id) : null;
        const alert = this.findAlertForSaved(savedItem.product_cluster_id, savedItem.listing_id, savedItem.listing_option_id || null, savedItem.id);
        const currentPrice = this.currentEffectivePriceForSaved(savedItem);
        const isRestock = Boolean(savedItem.listing_option_id || alert?.alert_type === 'restock');
        const optionLabel = this.savedOptionLabel(savedItem);
        const categoryLabel = this.savedItemCategoryLabel(savedItem, cluster);
        const regionLabel = this.savedItemRegionLabel(savedItem);
        const contextLabel = isRestock
            ? `재입고 추적 · ${listing ? this.platformAndSellerLabel(listing) : '판매처 확인 필요'} · ${optionLabel}`
            : (savedItem.listing_id ? `판매처 상품 · ${listing ? this.platformAndSellerLabel(listing) : '판매처 확인 필요'}` : '상품군 전체');

        this.alertEditor = {
            mode: isRestock ? 'restock' : 'price',
            savedItemId: savedItem.id,
            existingAlertId: alert?.id || null,
            clusterId: savedItem.product_cluster_id,
            listingId: savedItem.listing_id,
            listingOptionId: savedItem.listing_option_id || null,
            image: savedItem.image_url || cluster.representative_image_url,
            imageAlt: savedItem.product_name || cluster.canonical_name,
            title: savedItem.product_name || cluster.canonical_name,
            brand: cluster.brand_name || cluster.wholesale_tag_name || '브랜드/도매택 확인 필요',
            contextLabel: this.withSelectionContext(categoryLabel, regionLabel, contextLabel),
            alertCategoryLabel: isRestock ? '재입고 알림' : '가격 하락 알림',
            basisText: isRestock ? `${this.withSelectionContext(categoryLabel, regionLabel, '특정 판매처 옵션 재고 기준')}` : `${this.withSelectionContext(categoryLabel, regionLabel, '누구나 적용 가능한 실구매가 기준')}`,
            baselineTitle: isRestock ? '추적 옵션' : '저장 당시',
            currentTitle: isRestock ? '현재 재고' : '현재 기준',
            savedPrice: savedItem.saved_price,
            savedPriceLabel: isRestock ? optionLabel : (savedItem.saved_price > 0 ? this.formatWon(savedItem.saved_price) : '저장가 확인 필요'),
            currentPrice,
            currentPriceLabel: isRestock ? this.currentOptionStatusLabel(savedItem, listingOption) : (currentPrice !== null ? this.formatWon(currentPrice) : '현재가 확인 필요'),
            targetColor: savedItem.target_color || alert?.target_color || null,
            targetSize: savedItem.target_size || alert?.target_size || null,
            optionStatusLabel: this.currentOptionStatusLabel(savedItem, listingOption),
            conditionId: alert ? this.alertConditionId(alert) : (isRestock ? 'restock' : 'saved_price_drop'),
            targetPriceInput: alert?.threshold_price ? `${alert.threshold_price}` : '',
            channels: this.alertChannels(alert),
            error: ''
        };
    }

    private createPriceAlertFromEditor(editor: AlertEditorState): PriceAlert | null {
        const condition = this.findAlertConditionOption(editor.conditionId);
        if (!condition) {
            editor.error = '알림 조건을 다시 선택해 주세요.';
            return null;
        }

        if (editor.channels.length === 0) {
            editor.error = '알림 채널을 하나 이상 선택해 주세요.';
            return null;
        }

        if (condition.alertType === 'restock' && (!editor.listingId || !editor.listingOptionId)) {
            editor.error = '재입고 알림은 판매처와 색상/사이즈 옵션을 선택해야 합니다.';
            return null;
        }

        const targetPrice = condition.requiresTargetPrice ? this.parseAlertTargetPrice(editor.targetPriceInput) : null;
        if (condition.requiresTargetPrice && !targetPrice) {
            editor.error = '목표 금액을 입력해 주세요.';
            return null;
        }

        const existingAlert = this.findAlertForSaved(editor.clusterId, editor.listingId, editor.listingOptionId, editor.savedItemId);
        const savedItem = this.savedItems.find((item) => item.id === editor.savedItemId) || null;
        const cluster = this.findCluster(editor.clusterId);
        const now = new Date().toISOString();
        const cropContext: CropRegionContext = {
            crop_image_id: savedItem?.crop_image_id || savedItem?.search_context?.crop_image_id || savedItem?.search_context?.selected_crop?.crop_image_id || null,
            original_image_size: savedItem?.original_image_size || null,
            displayed_image_size: savedItem?.displayed_image_size || null,
            crop_region_original: savedItem?.crop_region_original || null,
            crop_preview_url: savedItem?.crop_preview_url || null,
            local_crop_id: savedItem?.local_crop_id || null
        };
        const alertScope: AlertTrackingScope = condition.alertType === 'restock'
            ? 'listing_option'
            : editor.listingId ? 'listing' : 'product_cluster';

        return {
            id: editor.existingAlertId || `alert-local-${Date.now()}-${editor.clusterId}${editor.listingId ? `-${editor.listingId}` : ''}${editor.listingOptionId ? `-${editor.listingOptionId}` : ''}`,
            user_id: this.currentUserId,
            owner_type: 'anonymous',
            anonymous_session_id: this.anonymousSessionId,
            product_cluster_id: editor.clusterId,
            listing_id: editor.listingId,
            listing_option_id: editor.listingOptionId,
            alert_scope: alertScope,
            alert_type: condition.alertType,
            saved_item_id: editor.savedItemId,
            selected_category_id: savedItem?.selected_category_id || null,
            selected_category_label: savedItem?.selected_category_label || null,
            selected_region_label: savedItem?.selected_region_label || null,
            selected_region: savedItem?.selected_region ? { ...savedItem.selected_region } : null,
            ...cropContext,
            source_request_id: savedItem?.source_request_id || null,
            baseline_product_name: savedItem?.product_name || cluster?.canonical_name || editor.title,
            baseline_image_url: savedItem?.image_url || cluster?.representative_image_url || editor.image,
            baseline_category_label: savedItem?.selected_category_label || null,
            baseline_region_label: savedItem?.selected_region_label || null,
            target_color: editor.targetColor,
            target_size: editor.targetSize,
            baseline_price: editor.savedPrice || null,
            threshold_price: targetPrice,
            threshold_percent: condition.thresholdPercent,
            channels: [...editor.channels],
            enabled: true,
            last_sent_at: existingAlert?.last_sent_at || null,
            created_at: existingAlert?.created_at || now,
            updated_at: now
        };
    }

    private createRestockAlert(savedItem: UserSavedItem): PriceAlert | null {
        if (!savedItem.listing_id || !savedItem.listing_option_id) return null;

        const existingAlert = this.findAlertForSaved(savedItem.product_cluster_id, savedItem.listing_id, savedItem.listing_option_id, savedItem.id);
        const now = new Date().toISOString();
        const cropContext: CropRegionContext = {
            crop_image_id: savedItem.crop_image_id || savedItem.search_context?.crop_image_id || savedItem.search_context?.selected_crop?.crop_image_id || null,
            original_image_size: savedItem.original_image_size || null,
            displayed_image_size: savedItem.displayed_image_size || null,
            crop_region_original: savedItem.crop_region_original || null,
            crop_preview_url: savedItem.crop_preview_url || null,
            local_crop_id: savedItem.local_crop_id || null
        };

        return {
            id: existingAlert?.id || `alert-local-${Date.now()}-${savedItem.product_cluster_id}-${savedItem.listing_option_id}`,
            user_id: this.currentUserId,
            owner_type: 'anonymous',
            anonymous_session_id: this.anonymousSessionId,
            product_cluster_id: savedItem.product_cluster_id,
            listing_id: savedItem.listing_id,
            listing_option_id: savedItem.listing_option_id,
            alert_scope: 'listing_option',
            alert_type: 'restock',
            saved_item_id: savedItem.id,
            selected_category_id: savedItem.selected_category_id || null,
            selected_category_label: savedItem.selected_category_label || null,
            selected_region_label: savedItem.selected_region_label || null,
            selected_region: savedItem.selected_region ? { ...savedItem.selected_region } : null,
            ...cropContext,
            source_request_id: savedItem.source_request_id || null,
            baseline_product_name: savedItem.product_name || this.findCluster(savedItem.product_cluster_id)?.canonical_name || null,
            baseline_image_url: savedItem.image_url || this.findCluster(savedItem.product_cluster_id)?.representative_image_url || null,
            baseline_category_label: savedItem.selected_category_label || null,
            baseline_region_label: savedItem.selected_region_label || null,
            target_color: savedItem.target_color,
            target_size: savedItem.target_size,
            baseline_price: null,
            threshold_price: null,
            threshold_percent: null,
            channels: existingAlert?.channels || ['in_app'],
            enabled: true,
            last_sent_at: existingAlert?.last_sent_at || null,
            created_at: existingAlert?.created_at || now,
            updated_at: now
        };
    }

    private upsertPriceAlert(nextAlert: PriceAlert) {
        const nextKey = nextAlert.saved_item_id || this.savedKey(nextAlert.product_cluster_id, nextAlert.listing_id, nextAlert.listing_option_id || null);
        const index = this.priceAlerts.findIndex((alert) => {
            const alertKey = alert.saved_item_id || this.savedKey(alert.product_cluster_id, alert.listing_id, alert.listing_option_id || null);
            return alertKey === nextKey;
        });

        if (index >= 0) {
            this.priceAlerts = this.priceAlerts.map((alert, alertIndex) => alertIndex === index ? nextAlert : alert);
        } else {
            this.priceAlerts = [nextAlert, ...this.priceAlerts];
        }
    }

    private currentEffectivePriceForSaved(item: UserSavedItem): number | null {
        if (item.listing_id) {
            const listing = this.findListing(item.listing_id);
            if (!listing) return null;
            const offer = this.lowestOfferForListings([listing], item.product_cluster_id).lowestCandidate;
            return offer ? offer.publicEffectivePrice : null;
        }

        const lowestOffer = this.lowestOfferForListings(this.getListingsForCluster(item.product_cluster_id), item.product_cluster_id);
        return lowestOffer.lowestCandidate ? lowestOffer.lowestCandidate.publicEffectivePrice : null;
    }

    private findAlertConditionOption(conditionId: AlertConditionId): AlertConditionOption | null {
        return this.alertConditionOptions.find((condition) => condition.id === conditionId) || null;
    }

    private alertConditionId(alert: PriceAlert): AlertConditionId {
        if (alert.alert_type === 'restock') return 'restock';
        if (alert.alert_type === 'target_price') return 'target_price';
        if (alert.alert_type === 'lowest_30d') return 'lowest_30d';
        if (alert.alert_type === 'lowest_90d') return 'lowest_90d';
        if (alert.alert_type === 'price_drop' && (alert.threshold_percent || 0) >= 10) return 'drop_10';
        if (alert.alert_type === 'price_drop' && (alert.threshold_percent || 0) >= 5) return 'drop_5';
        return 'saved_price_drop';
    }

    private alertConditionLabel(alert: PriceAlert): string {
        const conditionId = this.alertConditionId(alert);
        if (conditionId === 'restock') {
            return `${[alert.target_color, alert.target_size].filter(Boolean).join(' · ') || '선택 옵션'} 재입고`;
        }

        if (conditionId === 'target_price') {
            return alert.threshold_price ? `${this.formatWon(alert.threshold_price)} 이하` : '내가 정한 금액 이하';
        }

        return this.findAlertConditionOption(conditionId)?.label || '가격 알림';
    }

    private alertThresholdLabel(alert: PriceAlert): string {
        if (alert.alert_type === 'restock') {
            return alert.listing_option_id ? '옵션 단위 추적' : '옵션 추적 키 확인 필요';
        }

        if (alert.alert_type === 'target_price') {
            return alert.threshold_price ? `목표 ${this.formatWon(alert.threshold_price)}` : '목표 금액 미입력';
        }

        if (alert.alert_type === 'price_drop') {
            return alert.threshold_percent ? `저장가 대비 ${alert.threshold_percent}% 이상 하락` : '저장가보다 하락';
        }

        if (alert.alert_type === 'lowest_30d') return '최근 30일 최저가 갱신';
        if (alert.alert_type === 'lowest_90d') return '최근 90일 최저가 갱신';
        return '가격 조건';
    }

    private alertChannels(alert?: PriceAlert | null): PriceAlertChannel[] {
        const channels = alert?.channels || ['in_app'];
        const validChannelIds = new Set(this.alertChannelOptions.map((channel) => channel.id));
        const safeChannels = channels.filter((channel): channel is PriceAlertChannel => validChannelIds.has(channel));
        return safeChannels.length > 0 ? safeChannels : ['in_app'];
    }

    private alertChannelLabel(channelId: PriceAlertChannel): string {
        return this.alertChannelOptions.find((channel) => channel.id === channelId)?.label || channelId;
    }

    private parseAlertTargetPrice(value: string): number {
        const parsed = Number((value || '').replace(/[^0-9]/g, ''));
        return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
    }

    private createHomeEntries(): HomeEntry[] {
        const recentRequest = this.recentImageSearchRequest;
        const recentCluster = this.findCluster('cluster-muse-crop-cardigan') || this.productClusters[0];
        const savedItem = this.savedItems[0];
        const savedCluster = this.findCluster(savedItem?.product_cluster_id || '') || this.productClusters[1];
        const recentDetectedItem = recentRequest?.detected_items[0] || null;
        const recentDetectedCategory = this.itemCategoryIdFromCategory(recentDetectedItem?.category);

        return [
            this.toHomeEntry({
                label: '최근 검색',
                cluster: recentCluster,
                meta: recentDetectedItem?.label || this.itemCategoryLabel(recentDetectedCategory) || '이미지 검색',
                targetTab: 'search'
            }),
            this.toHomeEntry({
                label: '저장 상품',
                cluster: savedCluster,
                meta: savedItem ? [savedItem.target_color, savedItem.target_size].filter(Boolean).join(' · ') || '가격 알림 대기' : '가격 알림 대기',
                targetTab: 'saved'
            })
        ].filter((entry): entry is HomeEntry => Boolean(entry && (entry.label !== '저장 상품' || Boolean(savedItem))));
    }

    private toHomeEntry(params: {
        label: string;
        cluster: ProductCluster | undefined;
        meta: string;
        targetTab: LookitTabId;
    }): HomeEntry | null {
        if (!params.cluster) return null;

        const clusterListings = this.getListingsForCluster(params.cluster.id);
        const lowestOffer = this.lowestOfferForListings(clusterListings, params.cluster.id);
        const lowestPrice = lowestOffer.lowestCandidate?.publicEffectivePrice || 0;

        return {
            label: params.label,
            title: params.cluster.canonical_name,
            meta: params.meta,
            image: params.cluster.representative_image_url,
            priceLabel: lowestPrice > 0 ? this.formatWon(lowestPrice) : '가격 확인',
            targetTab: params.targetTab
        };
    }

    private createResultSections(): ResultSection[] {
        const categoryContext = this.selectedSearchContextLabel();
        const regionContext = this.currentSelectionRegionLabel() || '검색 영역';
        const configs: Array<Omit<ResultSection, 'products' | 'meta' | 'emptyTitle' | 'emptyMessage' | 'emptyActionId' | 'emptyActionLabel'>> = [
            {
                id: 'exact',
                title: '동일 상품 가능성 높음',
                subtitle: `${categoryContext} 기준 · ${regionContext}에서 브랜드, 이미지, 옵션, 판매처 신호가 가까운 후보입니다.`,
                clusterType: 'brand_exact',
                secondary: false
            },
            {
                id: 'wholesale',
                title: '같은 도매택/동일 공급 상품 추정',
                subtitle: `${categoryContext} 기준 · ${regionContext}에서 도매택, 소재, 색상, 판매처 패턴이 가까운 후보입니다.`,
                clusterType: 'wholesale_same',
                secondary: false
            },
            {
                id: 'similar',
                title: '유사 디자인 상품',
                subtitle: `${categoryContext} 동일/도매택 후보를 확인한 뒤 참고로 보는 검색 영역 기준 후보입니다.`,
                clusterType: 'similar_style',
                secondary: true
            }
        ];
        const resultIdSet = this.resultClusterIds === null ? null : new Set(this.resultClusterIds);
        const visibleClusters = resultIdSet === null
            ? this.productClusters
            : this.productClusters.filter((cluster) => resultIdSet.has(cluster.id));
        const similarTotalCount = visibleClusters.filter((cluster) => this.resultClusterTypeFor(cluster) === 'similar_style').length;

        return configs.map((config) => {
            const totalCount = visibleClusters.filter((cluster) => this.resultClusterTypeFor(cluster) === config.clusterType).length;
            const products = this.isClusterTypeIncluded(config.clusterType)
                ? visibleClusters
                    .filter((cluster) => this.resultClusterTypeFor(cluster) === config.clusterType)
                    .map((cluster) => {
                        const filteredListings = this.getMarketFilteredListingsForCluster(cluster.id);
                        if (this.hasActiveMarketFilters() && filteredListings.length === 0) return null;
                        return this.toResultProduct(cluster, filteredListings);
                    })
                    .filter((product): product is ResultProduct => Boolean(product))
                    .sort((a, b) => this.compareResultProducts(a, b))
                : [];
            const emptyState = this.resultSectionEmptyState(config.id, totalCount, products.length, similarTotalCount);

            return {
                ...config,
                meta: products.length === totalCount ? `${products.length}개` : `${products.length}/${totalCount}개`,
                products,
                ...emptyState
            };
        });
    }

    private resultSectionEmptyState(sectionId: ResultSectionId, totalCount: number, productCount: number, similarTotalCount: number): Pick<ResultSection, 'emptyTitle' | 'emptyMessage' | 'emptyActionId' | 'emptyActionLabel'> {
        if (productCount > 0) {
            return {
                emptyTitle: '',
                emptyMessage: '',
                emptyActionId: null,
                emptyActionLabel: ''
            };
        }

        if (totalCount > 0 && (this.hasActiveMarketFilters() || !this.isResultSectionIdIncluded(sectionId))) {
            return this.resultSectionEmptyAction(
                '필터에 맞는 후보 없음',
                '현재 필터 조건으로는 이 구분의 후보가 보이지 않습니다.',
                sectionId === 'similar' ? 'adjust_region' : 'view_similar_designs'
            );
        }

        if (sectionId === 'exact') {
            return this.resultSectionEmptyAction(
                '동일 상품 없음',
                similarTotalCount > 0
                    ? '동일 상품으로 볼 근거가 부족합니다. 아래 유사 디자인은 같은 상품이 아니라 참고 후보입니다.'
                    : '동일 상품으로 볼 후보를 찾지 못했습니다. 영역을 다시 맞추거나 카테고리를 바꿔 보세요.',
                similarTotalCount > 0 ? 'view_similar_designs' : 'adjust_region'
            );
        }

        if (sectionId === 'wholesale') {
            return this.resultSectionEmptyAction(
                '같은 도매택 추정 없음',
                similarTotalCount > 0
                    ? '도매택/동일 공급 상품으로 묶을 근거가 부족합니다. 유사 디자인과 섞지 않고 분리했습니다.'
                    : '같은 도매택 또는 동일 공급 상품으로 추정할 후보가 없습니다.',
                similarTotalCount > 0 ? 'view_similar_designs' : 'change_category'
            );
        }

        return this.resultSectionEmptyAction(
            '유사 디자인 후보 없음',
            '동일 상품이나 도매택 후보도 없다면 영역을 넓히거나 전체 사진으로 다시 검색해 주세요.',
            'search_full_image'
        );
    }

    private resultSectionEmptyAction(title: string, message: string, actionId: ProductSearchRecoveryAction): Pick<ResultSection, 'emptyTitle' | 'emptyMessage' | 'emptyActionId' | 'emptyActionLabel'> {
        return {
            emptyTitle: title,
            emptyMessage: message,
            emptyActionId: actionId,
            emptyActionLabel: this.recoveryActionCatalog[actionId].label
        };
    }

    private resultProductCount(sectionId: ResultSectionId): number {
        return this.resultSections.find((section) => section.id === sectionId)?.products.length || 0;
    }

    private isResultSectionIdIncluded(sectionId: ResultSectionId): boolean {
        if (sectionId === 'exact') return true;
        if (sectionId === 'wholesale') return this.resultFilters.includeWholesale && !this.resultFilters.exactOnly;
        if (sectionId === 'similar') return this.resultFilters.includeSimilar && !this.resultFilters.exactOnly;
        return true;
    }

    private recoveryActions(ids: ProductSearchRecoveryAction[]): RecoveryAction[] {
        const uniqueIds = Array.from(new Set(ids));
        return uniqueIds
            .map((id) => this.recoveryActionCatalog[id])
            .filter((action): action is RecoveryAction => Boolean(action));
    }

    private primarySearchIssue(): ProductSearchUiState['issues'][number] | null {
        return this.searchUiState.issues[0] || null;
    }

    private defaultRecoveryActionIds(code: ProductSearchIssueCode | string): ProductSearchRecoveryAction[] {
        if (code === 'no_item_detected') return ['adjust_region', 'change_category', 'search_full_image', 'upload_new_photo'];
        if (code === 'low_confidence') return ['adjust_region', 'change_category', 'search_full_image'];
        if (code === 'no_exact_match') return ['view_similar_designs', 'adjust_region', 'change_category'];
        if (code === 'no_listing_available') return ['adjust_region', 'change_category', 'search_full_image', 'upload_new_photo'];
        if (code === 'stale_price') return ['view_similar_designs', 'upload_new_photo'];
        if (code === 'platform_error') return ['adjust_region', 'change_category', 'upload_new_photo'];
        return ['adjust_region', 'change_category', 'upload_new_photo'];
    }

    private toResultProduct(cluster: ProductCluster, filteredListings?: Listing[]): ResultProduct {
        const clusterListings = filteredListings || this.getListingsForCluster(cluster.id);
        const lowestOffer = this.lowestOfferForListings(clusterListings, cluster.id);
        const lowestCandidate = lowestOffer.lowestCandidate;
        const lowestListing = lowestCandidate?.listing || null;
        const lowestBreakdown = lowestCandidate?.breakdown || null;
        const lowestPrice = lowestCandidate ? lowestCandidate.publicEffectivePrice : '';
        const freshness = lowestCandidate?.freshness || (lowestListing ? this.listingFreshness(lowestListing) : null);
        const checkedAt = lowestCandidate?.checkedAt ? `가격 확인 시점 ${this.formatCheckedAt(lowestCandidate.checkedAt)}` : '';
        const saved = this.isProductSaved(cluster.id);
        const stockSummary = clusterListings.length > 0 ? this.clusterStockSummary(clusterListings) : '재고 확인 필요';
        const categoryContextLabel = this.selectedSearchContextLabel() || this.clusterCategoryLabel(cluster);
        const regionLabel = this.currentSelectionRegionLabel();
        const sourceContext = this.resultSourceContext(cluster, clusterListings, lowestListing, lowestBreakdown, freshness);

        return {
            clusterId: cluster.id,
            image: cluster.representative_image_url,
            imageAlt: cluster.canonical_name,
            title: cluster.canonical_name,
            brand: cluster.brand_name || cluster.wholesale_tag_name || '브랜드/도매택 확인 필요',
            matchType: this.matchTypeForCluster(cluster),
            matchLabel: this.matchLabelForCluster(cluster),
            confidence: cluster.confidence_level,
            effectivePrice: lowestPrice,
            listedPrice: lowestBreakdown ? lowestBreakdown.basePrice : '',
            storeCount: clusterListings.length,
            shippingIncluded: Boolean(lowestListing),
            couponApplied: Boolean(lowestBreakdown && lowestBreakdown.publicCoupon > 0),
            lowest: Boolean(lowestListing),
            statusLabel: stockSummary,
            checkedAt,
            freshnessStatus: freshness?.status || '',
            freshnessLabel: freshness ? this.freshnessBadgeLabel(freshness) : '가격 확인 필요',
            freshnessMetaLabel: freshness ? this.freshnessMetaLabel(freshness) : '확인된 판매처 가격 없음',
            freeShippingCondition: lowestListing?.free_shipping_condition || '',
            labels: [
                `${categoryContextLabel} 기준`,
                regionLabel,
                ...(lowestCandidate ? this.safeLowestOfferChipLabels(lowestCandidate, lowestListing) : ['최저가 확인 필요']),
                this.clusterOptionShortageLabel(clusterListings),
                lowestListing ? this.platformAndSellerLabel(lowestListing) : ''
            ].filter(Boolean),
            sourceStatus: cluster.source_status,
            sourceLabel: sourceContext.label,
            sourceMetaLabel: sourceContext.metaLabel,
            priceLabel: sourceContext.priceLabel,
            actionLabel: saved ? '저장됨' : '저장',
            saved
        };
    }

    private createPriceCompareRows(clusterId: string): PriceCompareRow[] {
        const clusterListings = this.getMarketFilteredListingsForCluster(clusterId);
        const lowestOffer = this.lowestOfferForListings(clusterListings, clusterId);
        const offerByListingId = new Map(lowestOffer.candidates.map((candidate) => [candidate.listingId, candidate]));
        const sortedListings = this.sortListings(clusterListings);

        return sortedListings.map((listing) => {
            const offer = offerByListingId.get(listing.id) || this.lowestOfferForListings([listing], clusterId).candidates[0];
            const breakdown = offer?.breakdown || this.publicPriceBreakdown(listing);
            const freshness = offer?.freshness || this.listingFreshness(listing);
            const excludedFromLowest = offer?.excludedFromLowest ?? true;
            const effectivePrice = offer?.publicEffectivePrice ?? breakdown.public_effective_price;
            const sourceLabel = this.sourceLabelForListing(listing);
            const comparisonLabels = offer ? this.safeLowestOfferChipLabels(offer, listing) : [
                '배송비 포함',
                breakdown.publicCoupon > 0 ? '공개 쿠폰 적용' : '',
                this.availabilityLabel(listing.availability_status),
                this.optionShortageLabel(listing),
                this.freshnessBadgeLabel(freshness),
                excludedFromLowest ? '최저가 제외' : ''
            ].filter(Boolean);

            return {
                id: listing.id,
                title: listing.title,
                sellerName: this.sellerNameForListing(listing),
                platformName: this.platformNameForListing(listing),
                sellerPlatformLabel: this.platformAndSellerLabel(listing),
                sourceLabel,
                sourceMetaLabel: this.sourceMetaLabelForListing(listing),
                productUrl: listing.product_url,
                linkButtonLabel: this.linkButtonLabelForListing(listing),
                linkAriaLabel: `${this.platformAndSellerLabel(listing)} ${this.linkButtonLabelForListing(listing)}`,
                linkSourceLabel: this.linkSourceLabelForListing(listing),
                listedPriceLabel: this.formatWon(breakdown.basePrice),
                instantDiscountLabel: this.formatDiscount(breakdown.instantDiscount),
                publicCouponLabel: this.formatDiscount(breakdown.publicCoupon),
                shippingFeeLabel: this.formatShipping(breakdown.shippingFee),
                optionPriceDeltaLabel: breakdown.optionPriceDelta !== 0 ? this.formatSignedWon(breakdown.optionPriceDelta) : '',
                freeShippingConditionLabel: listing.free_shipping_condition || '무료배송 조건 없음',
                effectivePrice,
                effectivePriceLabel: this.priceRowEffectivePriceLabel(listing, breakdown),
                availabilityLabel: this.availabilityLabel(listing.availability_status),
                optionAvailabilityLabel: this.optionAvailabilityLabel(listing),
                optionRows: this.createListingOptionRows(listing),
                checkedAtLabel: offer?.checkedAt ? `가격 확인 시점 ${this.formatCheckedAt(offer.checkedAt)}` : `가격 확인 시점 ${this.formatCheckedAt(freshness.checkedAt || listing.last_checked_at)}`,
                collectedAtLabel: freshness.collectedAt ? `수집 ${this.formatCheckedAt(freshness.collectedAt)}` : '수집 시점 확인 필요',
                nextRefreshLabel: freshness.nextRefreshAt ? `다음 갱신 ${this.formatCheckedAt(freshness.nextRefreshAt)}` : '다음 갱신 대기',
                freshnessStatus: freshness.status,
                freshnessLabel: this.freshnessBadgeLabel(freshness),
                freshnessMetaLabel: this.freshnessMetaLabel(freshness),
                failureReasonLabel: freshness.failureReason || '',
                publicBenefitSummary: breakdown.formulaLabel,
                publicBenefitLabels: this.publicBenefitLabels(listing, breakdown),
                referenceBenefitSummary: this.referenceBenefitSummary(listing),
                referenceBenefitLabels: this.referenceBenefitLabels(listing),
                comparisonLabels: Array.from(new Set(comparisonLabels)),
                excludedFromLowest,
                isLowest: Boolean(lowestOffer.lowestCandidate && listing.id === lowestOffer.lowestCandidate.listingId),
                priceBasisLabel: this.priceBasisLabelForListing(listing, breakdown, excludedFromLowest)
            };
        });
    }

    private refreshPriceInsights() {
        this.priceCompareRows = this.selectedResultProduct
            ? this.createPriceCompareRows(this.selectedResultProduct.clusterId)
            : [];
        this.priceCompareSummary = this.createPriceCompareSummary();
        this.priceTrendPoints = this.createPriceTrendPoints();
        this.priceTrendSummary = this.createPriceTrendSummary(this.priceTrendPoints);
        this.priceDistributionRows = this.createPriceDistributionRows();
        this.priceTrendAriaLabel = `${this.priceTrendSummary.windowLabel} 날짜별 최저 실구매가 추이`;

        if (!this.priceTrendSummary.hasData) {
            this.destroyPriceTrendChart();
        }
    }

    private clearPriceInsights() {
        this.priceCompareRows = [];
        this.priceCompareSummary = null;
        this.priceTrendRangeSummary = null;
        this.priceTrendPoints = [];
        this.priceDistributionRows = [];
        this.priceTrendSummary = {
            windowLabel: `최근 ${this.selectedPriceTrendWindow}일`,
            currentLowestLabel: '현재 최저가 확인 필요',
            recentLowestLabel: '가격 확인 데이터 없음',
            latestCheckedLabel: '스냅샷 없음',
            missingLabel: '확인된 날짜만 표시합니다.',
            hasData: false
        };
        this.priceTrendAriaLabel = `${this.priceTrendSummary.windowLabel} 날짜별 최저 실구매가 추이`;
    }

    private createPriceTrendPoints(): PriceTrendPoint[] {
        if (!this.selectedResultProduct) return [];

        const rangeSummary = this.currentPriceTrendRangeSummary();
        if (rangeSummary) {
            return this.createPriceTrendPointsFromDailyLowest(rangeSummary);
        }

        const clusterListings = this.getMarketFilteredListingsForCluster(this.selectedResultProduct.clusterId);
        const listingIds = new Set(clusterListings.map((listing) => listing.id));
        if (listingIds.size === 0) return [];

        const clusterSnapshots = this.priceSnapshots.filter((snapshot) => listingIds.has(snapshot.listing_id));
        if (clusterSnapshots.length === 0) return [];

        const endDateKey = this.priceTrendEndDateKey(clusterSnapshots, listingIds);
        const endTime = this.dateKeyToTime(endDateKey);
        const startTime = endTime - ((this.selectedPriceTrendWindow - 1) * this.dayMs);
        const dailyLowestSnapshots = getDailyLowestEffectivePriceSnapshots(clusterSnapshots.filter((snapshot) => {
            const dateKey = this.dateKeyFromIso(snapshot.checked_at);
            const dateTime = this.dateKeyToTime(dateKey);
            return dateTime >= startTime && dateTime <= endTime;
        }));
        const points: PriceTrendPoint[] = [];
        let previousTime: number | null = null;

        dailyLowestSnapshots.forEach((dailyLowest) => {
            const dateKey = dailyLowest.dateKey;
            const dateTime = this.dateKeyToTime(dateKey);

            if (previousTime !== null && dateTime - previousTime > this.missingGapDays * this.dayMs) {
                points.push(this.createMissingTrendPoint(previousTime, dateTime));
            }

            points.push({
                dateKey,
                label: this.formatMonthDay(dateKey),
                price: dailyLowest.public_effective_price,
                sourceCount: dailyLowest.sourceCount,
                checkedAt: dailyLowest.snapshot.checked_at,
                collectedAt: dailyLowest.snapshot.collected_at,
                isMissing: false,
                isRecentLowest: false
            });

            previousTime = dateTime;
        });

        return this.markRecentLowestPoint(points);
    }

    private createPriceTrendPointsFromDailyLowest(rangeSummary: PriceSnapshotRangeSummary): PriceTrendPoint[] {
        const points: PriceTrendPoint[] = [];
        let previousTime: number | null = null;

        rangeSummary.dailyLowest.forEach((dailyLowest) => {
            const dateKey = dailyLowest.dateKey;
            const dateTime = this.dateKeyToTime(dateKey);

            if (previousTime !== null && dateTime - previousTime > this.missingGapDays * this.dayMs) {
                points.push(this.createMissingTrendPoint(previousTime, dateTime));
            }

            points.push({
                dateKey,
                label: this.formatMonthDay(dateKey),
                price: dailyLowest.effectivePrice,
                sourceCount: dailyLowest.sourceCount,
                checkedAt: dailyLowest.checkedAt,
                collectedAt: dailyLowest.collectedAt,
                isMissing: false,
                isRecentLowest: false
            });

            previousTime = dateTime;
        });

        return this.markRecentLowestPoint(points);
    }

    private currentPriceTrendRangeSummary(): PriceSnapshotRangeSummary | null {
        if (!this.selectedResultProduct || !this.priceTrendRangeSummary) return null;
        if (this.priceTrendRangeSummary.productClusterId !== this.selectedResultProduct.clusterId) return null;
        if (this.priceTrendRangeSummary.days !== this.selectedPriceTrendWindow) return null;
        return this.priceTrendRangeSummary;
    }

    private createPriceTrendSummary(points: PriceTrendPoint[]): PriceTrendSummary {
        const windowLabel = `최근 ${this.selectedPriceTrendWindow}일`;
        const currentLowestLabel = this.priceCompareSummary?.lowestEffectivePriceLabel || '현재 최저가 확인 필요';
        const dataPoints = points.filter((point) => point.price !== null);
        const rangeSummary = this.currentPriceTrendRangeSummary();
        const latestCheckedLabel = rangeSummary?.latestCheckedAt
            ? `마지막 확인 ${this.formatCheckedAt(rangeSummary.latestCheckedAt)}`
            : '확인된 가격 스냅샷 없음';

        if (dataPoints.length === 0) {
            const hasRawSnapshots = (rangeSummary?.snapshotCount || 0) > 0;

            return {
                windowLabel,
                currentLowestLabel,
                recentLowestLabel: hasRawSnapshots ? '현재 최저가로 표시할 스냅샷 없음' : '해당 기간 스냅샷 없음',
                latestCheckedLabel,
                missingLabel: hasRawSnapshots
                    ? '기간 내 확인 기록은 있지만 재고/검증 상태 때문에 최저가로 표시하지 않았습니다.'
                    : '데이터가 없는 기간은 추정선 없이 비워둡니다.',
                hasData: false
            };
        }

        const recentLowest = dataPoints.find((point) => point.isRecentLowest) || dataPoints[0];
        const latestChecked = [...dataPoints].sort((a, b) => this.dateKeyToTime(b.dateKey) - this.dateKeyToTime(a.dateKey))[0];
        const missingCount = points.filter((point) => point.isMissing).length;

        return {
            windowLabel,
            currentLowestLabel,
            recentLowestLabel: `${this.formatWon(recentLowest.price || 0)} · ${this.formatMonthDay(recentLowest.dateKey)}`,
            latestCheckedLabel: rangeSummary?.latestCheckedAt
                ? `마지막 확인 ${this.formatCheckedAt(rangeSummary.latestCheckedAt)}`
                : `마지막 확인 ${this.formatMonthDay(latestChecked.dateKey)}`,
            missingLabel: missingCount > 0 ? `${missingCount}개 확인 누락 구간은 선을 끊어 표시` : `확인된 ${dataPoints.length}개 날짜 기준`,
            hasData: true
        };
    }

    private createPriceDistributionRows(): PriceDistributionRow[] {
        const rows = [...this.priceCompareRows].sort((a, b) => {
            const eligibilityDiff = Number(a.excludedFromLowest) - Number(b.excludedFromLowest);
            if (eligibilityDiff !== 0) return eligibilityDiff;
            return a.effectivePrice - b.effectivePrice;
        });
        if (rows.length === 0) return [];

        const prices = rows.map((row) => row.effectivePrice);
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        const range = maxPrice - minPrice;

        return rows.map((row) => {
            const width = range === 0 ? 100 : 32 + Math.round(((row.effectivePrice - minPrice) / range) * 68);

            return {
                id: row.id,
                sellerPlatformLabel: row.sellerPlatformLabel,
                priceLabel: row.effectivePriceLabel,
                widthPercent: `${width}%`,
                isLowest: row.isLowest
            };
        });
    }

    private schedulePriceTrendChartRender() {
        this.cancelPriceTrendChartRender();

        const render = () => {
            this.priceTrendRenderHandle = null;
            this.priceTrendRenderHandleType = null;
            this.renderPriceTrendChart();
        };

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            this.priceTrendRenderHandle = window.requestAnimationFrame(render);
            this.priceTrendRenderHandleType = 'animationFrame';
            return;
        }

        this.priceTrendRenderHandle = setTimeout(render, 0) as unknown as number;
        this.priceTrendRenderHandleType = 'timeout';
    }

    private renderPriceTrendChart() {
        if (!this.priceTrendSummary.hasData) {
            this.destroyPriceTrendChart();
            return;
        }

        if (typeof window === 'undefined') return;

        const canvas = this.priceTrendCanvas?.nativeElement;
        const context = canvas?.getContext('2d');
        if (!canvas || !context) return;

        const points = this.priceTrendPoints;
        const labels = points.map((point) => point.label);
        const signature = [
            this.selectedResultProduct?.clusterId || '',
            this.selectedPriceTrendWindow,
            points.map((point) => `${point.dateKey}:${point.price ?? 'x'}:${point.checkedAt || ''}:${point.isMissing ? 1 : 0}`).join('|')
        ].join('::');

        if (this.priceTrendChart && this.priceTrendChartSignature === signature) {
            this.priceTrendChart.resize();
            return;
        }

        const accent = this.chartColor('--lookit-color-price-drop', '#667c8c');
        const textMuted = this.chartColor('--lookit-color-text-muted', '#71717a');
        const grid = this.chartColor('--lookit-border-subtle', '#e4e4e7');

        if (this.priceTrendChart) {
            this.priceTrendChart.destroy();
            this.priceTrendChart = null;
        }

        this.priceTrendChart = new Chart(context, {
            type: 'line',
            data: {
                labels,
                datasets: [{
                    data: points.map((point) => point.price),
                    borderColor: accent,
                    backgroundColor: 'rgba(240, 248, 255, 0.72)',
                    borderWidth: 2,
                    fill: true,
                    spanGaps: false,
                    tension: 0.32,
                    pointRadius: (chartContext: any) => {
                        const point = points[chartContext.dataIndex];
                        if (!point || point.isMissing) return 0;
                        return point.isRecentLowest ? 5 : 3;
                    },
                    pointHoverRadius: (chartContext: any) => {
                        const point = points[chartContext.dataIndex];
                        if (!point || point.isMissing) return 0;
                        return point.isRecentLowest ? 7 : 5;
                    },
                    pointBackgroundColor: (chartContext: any) => {
                        const point = points[chartContext.dataIndex];
                        return point?.isRecentLowest ? accent : '#ffffff';
                    },
                    pointBorderColor: accent,
                    pointBorderWidth: 2
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: {
                    padding: { top: 8, right: 4, bottom: 0, left: 0 }
                },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        displayColors: false,
                        callbacks: {
                            title: (items: any[]) => {
                                const point = points[items?.[0]?.dataIndex || 0];
                                if (!point || point.isMissing) return '가격 확인 누락';
                                return point.dateKey;
                            },
                            label: (tooltipContext: any) => {
                                const point = points[tooltipContext.dataIndex];
                                if (!point || point.price === null) return '가격 확인 없음';
                                return `최저 실구매가 ${this.formatWon(point.price)}`;
                            },
                            afterLabel: (tooltipContext: any) => {
                                const point = points[tooltipContext.dataIndex];
                                if (!point || point.price === null) return '';
                                const labels = [];
                                if (point.sourceCount > 1) labels.push(`${point.sourceCount}개 판매처 스냅샷 중 최저`);
                                if (point.checkedAt) labels.push(`확인 ${this.formatCheckedAt(point.checkedAt)}`);
                                if (point.collectedAt && point.collectedAt !== point.checkedAt) labels.push(`수집 ${this.formatCheckedAt(point.collectedAt)}`);
                                return labels;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: {
                            autoSkip: true,
                            color: textMuted,
                            font: { size: 11, weight: '600' },
                            maxRotation: 0,
                            maxTicksLimit: this.selectedPriceTrendWindow === 90 ? 4 : 5,
                            minRotation: 0,
                            callback: (value: any) => labels[Number(value)] || ''
                        }
                    },
                    y: {
                        beginAtZero: false,
                        grace: '8%',
                        grid: { color: grid },
                        ticks: {
                            color: textMuted,
                            font: { size: 11, weight: '600' },
                            maxTicksLimit: 4,
                            callback: (value: any) => this.compactWon(Number(value))
                        }
                    }
                }
            }
        } as any);
        this.priceTrendChartSignature = signature;
    }

    private destroyPriceTrendChart() {
        this.cancelPriceTrendChartRender();

        if (this.priceTrendChart) {
            this.priceTrendChart.destroy();
            this.priceTrendChart = null;
        }

        this.priceTrendChartSignature = '';
    }

    private cancelPriceTrendChartRender() {
        if (this.priceTrendRenderHandle === null) return;

        if (this.priceTrendRenderHandleType === 'animationFrame' && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
            window.cancelAnimationFrame(this.priceTrendRenderHandle);
        } else {
            clearTimeout(this.priceTrendRenderHandle as unknown as ReturnType<typeof setTimeout>);
        }

        this.priceTrendRenderHandle = null;
        this.priceTrendRenderHandleType = null;
    }

    private getListingsForCluster(clusterId: string): Listing[] {
        return this.listings.filter((listing) => listing.product_cluster_id === clusterId);
    }

    private getMarketFilteredListingsForCluster(clusterId: string): Listing[] {
        return this.getListingsForCluster(clusterId).filter((listing) => this.listingMatchesMarketFilters(listing));
    }

    private listingMatchesMarketFilters(listing: Listing): boolean {
        if (this.resultFilters.platformIds.length > 0 && !this.resultFilters.platformIds.includes(listing.platform_id)) return false;
        if (this.resultFilters.inStockOnly && !this.listingHasStock(listing)) return false;
        if (this.resultFilters.freeShippingOnly && listing.shipping_fee > 0) return false;
        if (this.resultFilters.publicCouponOnly && listing.public_coupon_discount <= 0) return false;
        return true;
    }

    private hasActiveMarketFilters(): boolean {
        return Boolean(
            this.resultFilters.inStockOnly ||
            this.resultFilters.freeShippingOnly ||
            this.resultFilters.publicCouponOnly ||
            this.resultFilters.platformIds.length > 0
        );
    }

    private isCurrentlyStocked(listing: Listing): boolean {
        return listing.availability_status === 'in_stock' || listing.availability_status === 'low_stock';
    }

    private listingHasStock(listing: Listing): boolean {
        if (this.isCurrentlyStocked(listing)) return true;
        return (listing.options || []).some((option) => option.availability_status === 'in_stock' || option.availability_status === 'low_stock');
    }

    private isClusterTypeIncluded(clusterType: ProductClusterType): boolean {
        if (this.resultFilters.exactOnly) return clusterType === 'brand_exact';
        if (clusterType === 'brand_exact') return true;
        if (clusterType === 'wholesale_same') return this.resultFilters.includeWholesale;
        if (clusterType === 'similar_style') return this.resultFilters.includeSimilar;
        return true;
    }

    private findCluster(clusterId: string): ProductCluster | undefined {
        return this.productClusters.find((cluster) => cluster.id === clusterId);
    }

    private findResultProduct(clusterId: string): ResultProduct | null {
        for (const section of this.resultSections) {
            const product = section.products.find((item) => item.clusterId === clusterId);
            if (product) return product;
        }
        return null;
    }

    private platformName(platformId: string): string {
        const platform: Platform | undefined = this.platforms.find((item) => item.id === platformId);
        return platform?.name || '플랫폼 확인 필요';
    }

    private sellerName(sellerId: string): string {
        const seller: Seller | undefined = this.sellers.find((item) => item.id === sellerId);
        return seller?.name || '판매처 확인 필요';
    }

    private platformNameForListing(listing: Listing): string {
        return listing.platform_display_name || this.platformName(listing.platform_id);
    }

    private sellerNameForListing(listing: Listing): string {
        return listing.seller_display_name || this.sellerName(listing.seller_id);
    }

    private platformAndSellerLabel(listing: Listing): string {
        return `${this.sellerNameForListing(listing)} · ${this.platformNameForListing(listing)}`;
    }

    private compareResultProducts(a: ResultProduct, b: ResultProduct): number {
        if (this.resultSortMode === 'effective_price_asc') {
            const priceDiff = this.priceValue(a.effectivePrice) - this.priceValue(b.effectivePrice);
            if (priceDiff !== 0) return priceDiff;
            return b.confidence - a.confidence;
        }

        if (this.resultSortMode === 'match_confidence_desc') {
            const confidenceDiff = b.confidence - a.confidence;
            if (confidenceDiff !== 0) return confidenceDiff;
            return this.priceValue(a.effectivePrice) - this.priceValue(b.effectivePrice);
        }

        if (this.resultSortMode === 'recent_checked_desc') {
            const recentDiff = this.resultProductCheckedTime(b) - this.resultProductCheckedTime(a);
            if (recentDiff !== 0) return recentDiff;
            return b.confidence - a.confidence;
        }

        const confidenceDiff = b.confidence - a.confidence;
        if (confidenceDiff !== 0) return confidenceDiff;

        const priceDiff = this.priceValue(a.effectivePrice) - this.priceValue(b.effectivePrice);
        if (priceDiff !== 0) return priceDiff;

        return this.resultProductCheckedTime(b) - this.resultProductCheckedTime(a);
    }

    private resultProductCheckedTime(product: ResultProduct): number {
        const listings = this.getMarketFilteredListingsForCluster(product.clusterId);
        const times = listings.map((listing) => new Date(this.listingFreshness(listing).checkedAt || listing.last_checked_at).getTime()).filter((time) => Number.isFinite(time));
        return times.length > 0 ? Math.max(...times) : 0;
    }

    private sortListings(listings: Listing[]): Listing[] {
        return [...listings].sort((a, b) => {
            if (this.listingSortMode === 'effective_price_asc') return this.compareListingsForPriceRows(a, b);
            if (this.listingSortMode === 'match_confidence_desc') return this.compareListingsByTrust(a, b);
            if (this.listingSortMode === 'recent_checked_desc') return this.compareListingsByRecent(a, b);
            return this.compareListingsRecommended(a, b);
        });
    }

    private compareListingsRecommended(a: Listing, b: Listing): number {
        const eligibilityDiff = Number(this.listingExcludedFromLowest(a)) - Number(this.listingExcludedFromLowest(b));
        if (eligibilityDiff !== 0) return eligibilityDiff;

        const availabilityDiff = this.availabilityRank(a.availability_status) - this.availabilityRank(b.availability_status);
        if (availabilityDiff !== 0) return availabilityDiff;

        const priceDiff = this.publicPriceBreakdown(a).public_effective_price - this.publicPriceBreakdown(b).public_effective_price;
        if (priceDiff !== 0) return priceDiff;

        return this.compareListingsByRecent(a, b);
    }

    private compareListingsByTrust(a: Listing, b: Listing): number {
        const trustDiff = this.listingTrustScore(b) - this.listingTrustScore(a);
        if (trustDiff !== 0) return trustDiff;
        return this.compareListingsForPriceRows(a, b);
    }

    private compareListingsByRecent(a: Listing, b: Listing): number {
        const freshnessDiff = this.freshnessRank(this.listingFreshness(a).status) - this.freshnessRank(this.listingFreshness(b).status);
        if (freshnessDiff !== 0) return freshnessDiff;

        const recentDiff = new Date(this.listingFreshness(b).checkedAt || b.last_checked_at).getTime() - new Date(this.listingFreshness(a).checkedAt || a.last_checked_at).getTime();
        if (recentDiff !== 0) return recentDiff;
        return this.publicPriceBreakdown(a).public_effective_price - this.publicPriceBreakdown(b).public_effective_price;
    }

    private listingTrustScore(listing: Listing): number {
        const cluster = this.findCluster(listing.product_cluster_id);
        const platform = this.platforms.find((item) => item.id === listing.platform_id);
        const seller = this.sellers.find((item) => item.id === listing.seller_id);
        const freshness = this.listingFreshness(listing);
        const checkedTime = new Date(freshness.checkedAt || listing.last_checked_at).getTime();
        const recencyScore = Number.isFinite(checkedTime) ? Math.min(12, Math.max(0, 12 - Math.floor((Date.now() - checkedTime) / this.dayMs))) : 0;
        const freshnessScore = Math.max(0, 12 - (this.freshnessRank(freshness.status) * 3));
        const platformScore = platform?.terms_status === 'allowed' ? 10 : platform?.affiliate_available ? 7 : 3;
        const sellerScore = seller?.seller_type === 'brand' ? 8 : seller?.seller_type === 'wholesale_retailer' ? 6 : 4;
        const publicBenefitScore = this.publicPriceBreakdown(listing).publicCoupon > 0 ? 2 : 0;
        const stockScore = this.listingExcludedFromLowest(listing) ? 0 : 4;

        return ((cluster?.confidence_level || 0) * 100) + platformScore + sellerScore + recencyScore + freshnessScore + publicBenefitScore + stockScore;
    }

    private compareListingsForPriceRows(a: Listing, b: Listing): number {
        const eligibilityDiff = Number(this.listingExcludedFromLowest(a)) - Number(this.listingExcludedFromLowest(b));
        if (eligibilityDiff !== 0) return eligibilityDiff;

        const priceDiff = this.publicPriceBreakdown(a).public_effective_price - this.publicPriceBreakdown(b).public_effective_price;
        if (priceDiff !== 0) return priceDiff;

        const availabilityDiff = this.availabilityRank(a.availability_status) - this.availabilityRank(b.availability_status);
        if (availabilityDiff !== 0) return availabilityDiff;

        return new Date(this.listingFreshness(b).checkedAt || b.last_checked_at).getTime() - new Date(this.listingFreshness(a).checkedAt || a.last_checked_at).getTime();
    }

    private availabilityRank(status: string): number {
        if (status === 'in_stock') return 0;
        if (status === 'low_stock') return 1;
        if (status === 'restock_expected') return 2;
        if (status === 'checking') return 3;
        if (status === 'sold_out') return 4;
        return 5;
    }

    private referenceBenefitSummary(listing: Listing): string {
        const referenceBenefits = this.referenceBenefitLabels(listing);

        if (referenceBenefits.length === 0) return '개인 쿠폰/적립금은 최저가 계산에서 제외';
        return `참고 혜택 ${referenceBenefits.join(', ')} · 기본 최저가 계산 제외`;
    }

    private lowestOfferForListings(listings: Listing[], clusterId?: string | null): LowestOfferResult {
        return lowestOfferService.evaluateLowestOffer({
            productClusterId: clusterId || listings[0]?.product_cluster_id || null,
            listings
        });
    }

    private lowestOfferChipLabels(candidate: LowestOfferCandidate): string[] {
        return candidate.chips.map((chip) => chip.label).filter(Boolean);
    }

    private safeLowestOfferChipLabels(candidate: LowestOfferCandidate, listing: Listing | null): string[] {
        const labels = this.lowestOfferChipLabels(candidate)
            .filter((label) => !/^배송비 포함$/.test(label) || !candidate.breakdown.unknownComponents.includes('shipping_fee'))
            .map((label) => this.safeStatusText(label));

        const extraLabels = [
            listing && this.isNaverShoppingListing(listing) ? '네이버 쇼핑 후보' : '',
            listing ? this.sourceLabelForListing(listing) : '',
            candidate.breakdown.needsVerification ? '판매처 확인 후보' : ''
        ].filter(Boolean);

        return Array.from(new Set(labels.concat(extraLabels))).slice(0, 5);
    }

    private safeStatusText(value: string): string {
        if (value === '연동 대기') return '판매처 확인 대기';
        if (value === '확인 필요') return '확인 필요';
        return value;
    }

    private resultSourceContext(
        cluster: ProductCluster,
        listings: Listing[],
        lowestListing: Listing | null,
        breakdown: PublicPriceBreakdown | null,
        freshness: ListingFreshness | null
    ): { label: string; metaLabel: string; priceLabel: string } {
        const naverListing = listings.find((listing) => this.isNaverShoppingListing(listing)) || null;
        const sourceListing = naverListing || lowestListing || listings[0] || null;
        const label = sourceListing ? this.sourceLabelForListing(sourceListing) : this.sourceStatusLabel(cluster.source_status);
        const metaLabel = sourceListing ? this.sourceMetaLabelForListing(sourceListing) : this.sourceStatusMetaLabel(cluster.source_status);
        const needsVerification = Boolean(
            sourceListing && (
                this.isNaverShoppingListing(sourceListing) ||
                sourceListing.source_status === 'needs_verification' ||
                breakdown?.needsVerification ||
                freshness?.sourceStatus === 'needs_verification'
            )
        );

        return {
            label,
            metaLabel,
            priceLabel: needsVerification ? '가격 확인 후보' : lowestListing ? '실구매가' : '가격 확인 필요'
        };
    }

    private sourceStatusLabel(status: SourceStatus | 'live_api' | string): string {
        if (status === 'mock') return '검증용 후보';
        if (status === 'api_pending') return '판매처 확인 대기';
        if (status === 'needs_verification') return '판매처 확인 후보';
        if (status === 'image_match_ready') return '이미지 매칭 후보';
        if (status === 'live_api') return '실시간 연동 후보';
        return '확인 필요 후보';
    }

    private sourceStatusMetaLabel(status: SourceStatus | 'live_api' | string): string {
        if (status === 'mock') return '실제 연동 전 검증용 데이터';
        if (status === 'api_pending') return '판매처 연동 준비 중';
        if (status === 'needs_verification') return '판매처와 가격 확인 필요';
        if (status === 'image_match_ready') return '이미지와 카테고리 근거 후보';
        if (status === 'live_api') return '외부 연동 확인 후보';
        return '상세 확인 필요';
    }

    private isNaverShoppingListing(listing: Listing | null): boolean {
        if (!listing) return false;
        return listing.platform_id === 'platform-naver-shopping' ||
            listing.platform_display_name === '네이버 쇼핑 검색' ||
            this.productUrlSource(listing) === 'naver_api_link';
    }

    private sourceLabelForListing(listing: Listing): string {
        if (this.isNaverShoppingListing(listing)) return '네이버 쇼핑 후보';
        return this.sourceStatusLabel(listing.source_status);
    }

    private sourceMetaLabelForListing(listing: Listing): string {
        if (this.isNaverShoppingListing(listing)) return '판매처 확인 후보 · 배송비/쿠폰/재고 확인 필요';
        return this.sourceStatusMetaLabel(listing.source_status);
    }

    private productUrlSource(listing: Listing): ListingProductUrlSource {
        if (listing.product_url_source) return listing.product_url_source;
        if (listing.platform_id === 'platform-naver-shopping' || listing.platform_display_name === '네이버 쇼핑 검색') return 'naver_api_link';
        return listing.product_url ? 'seller_product_url' : 'unknown';
    }

    private linkButtonLabelForListing(listing: Listing): string {
        if (this.productUrlSource(listing) === 'naver_api_link') return '네이버에서 확인';
        return '판매처로 이동';
    }

    private linkSourceLabelForListing(listing: Listing): string {
        if (this.productUrlSource(listing) === 'naver_api_link') return '네이버 검색 링크';
        if (this.productUrlSource(listing) === 'affiliate_link') return '제휴 링크';
        if (this.productUrlSource(listing) === 'seller_product_url') return '판매처 링크';
        return '링크 확인 필요';
    }

    private priceBasisLabelForListing(
        listing: Listing,
        breakdown: PublicPriceBreakdown,
        excludedFromLowest: boolean
    ): string {
        if (this.isNaverShoppingListing(listing) || breakdown.needsVerification || listing.source_status === 'needs_verification') {
            return '가격 확인 후보';
        }
        const freshness = this.listingFreshness(listing);
        if (freshness.status === 'failed' || freshness.status === 'api_pending') return '가격 확인 필요';
        if (excludedFromLowest) return '참고 가격';
        return '실구매가';
    }

    private lowestOfferCandidateForListing(listing: Listing): LowestOfferCandidate | null {
        return this.lowestOfferForListings([listing], listing.product_cluster_id).candidates[0] || null;
    }

    private listingExcludedFromLowest(listing: Listing): boolean {
        return this.lowestOfferCandidateForListing(listing)?.excludedFromLowest ?? true;
    }

    private publicPriceBreakdown(listing: Listing): PublicPriceBreakdown {
        const pricedListing = listing as Listing & { price_breakdown?: PublicPriceBreakdown };
        return pricedListing.price_breakdown || getPublicPriceBreakdown(listing);
    }

    private listingFreshness(listing: Listing): ListingFreshness {
        return listing.freshness || normalizeListingFreshness({
            checkedAt: listing.checked_at || listing.last_checked_at,
            collectedAt: listing.collected_at || listing.checked_at || listing.last_checked_at,
            confidenceScore: listing.confidence ?? 0.5,
            sourceStatus: listing.source_status
        });
    }

    private freshnessRank(status: ListingFreshnessStatus): number {
        if (status === 'fresh') return 0;
        if (status === 'stale') return 1;
        if (status === 'expired') return 2;
        if (status === 'failed') return 3;
        if (status === 'api_pending') return 4;
        return 5;
    }

    private freshnessBadgeLabel(freshness: ListingFreshness): string {
        if (freshness.status === 'fresh') return '최근 확인';
        if (freshness.status === 'stale') return '확인 필요';
        if (freshness.status === 'expired') return '재확인 필요';
        if (freshness.status === 'failed') return '확인 실패';
        if (freshness.status === 'api_pending') return '판매처 확인 대기';
        return '확인 필요';
    }

    private freshnessMetaLabel(freshness: ListingFreshness): string {
        const checked = freshness.checkedAt ? `확인 ${this.formatCheckedAt(freshness.checkedAt)}` : '확인 시점 없음';
        const confidence = this.freshnessConfidenceLabel(freshness);
        const nextRefresh = freshness.nextRefreshAt ? `다음 ${this.formatCheckedAt(freshness.nextRefreshAt)}` : '다음 갱신 대기';
        if (freshness.status === 'failed' && freshness.failureReason) return `${checked} · ${confidence} · ${freshness.failureReason}`;
        if (freshness.status === 'api_pending') return `${confidence} · 판매처 확인 대기`;
        return `${checked} · ${confidence} · ${nextRefresh}`;
    }

    private freshnessConfidenceLabel(freshness: ListingFreshness): string {
        if (freshness.confidence === 'high') return `신뢰 높음 ${this.scoreLabel(freshness.confidenceScore)}`;
        if (freshness.confidence === 'medium') return `신뢰 보통 ${this.scoreLabel(freshness.confidenceScore)}`;
        if (freshness.confidence === 'low') return `신뢰 낮음 ${this.scoreLabel(freshness.confidenceScore)}`;
        return '신뢰 확인 필요';
    }

    private priceRowEffectivePriceLabel(listing: Listing, breakdown: PublicPriceBreakdown): string {
        const freshness = this.listingFreshness(listing);
        if (freshness.status === 'failed' || freshness.status === 'api_pending') return '가격 확인 필요';
        return this.formatWon(breakdown.public_effective_price);
    }

    private publicBenefitLabels(listing: Listing, breakdown: PublicPriceBreakdown = this.publicPriceBreakdown(listing)): string[] {
        if (breakdown.needsVerification) {
            const verificationLabels = breakdown.chips
                .filter((chip) => !chip.includedInEffectivePrice && /확인 필요/.test(chip.label))
                .map((chip) => chip.label);
            if (verificationLabels.length > 0) return Array.from(new Set(verificationLabels)).slice(0, 3);
        }

        const labels = breakdown.chips
            .filter((chip) => chip.includedInEffectivePrice && chip.id !== 'basePrice')
            .map((chip) => chip.label);

        return labels.length > 0 ? labels : ['공개 혜택 없음'];
    }

    private referenceBenefitLabels(listing: Listing): string[] {
        return listing.benefits
            .filter((benefit) => !benefit.is_public_default)
            .map((benefit) => benefit.name);
    }

    private createListingOptionRows(listing: Listing): ListingOptionStockRow[] {
        return (listing.options || []).map((option) => {
            const canRequestRestock = option.restock_alert_available ?? (option.availability_status === 'sold_out' || option.availability_status === 'restock_expected');
            const restockEnabled = this.isRestockAlertEnabled(listing.product_cluster_id, listing.id, option.id);

            return {
                id: option.id,
                listingId: listing.id,
                color: option.color,
                size: option.size,
                label: `${option.color} · ${option.size}`,
                availabilityStatus: option.availability_status,
                statusLabel: this.optionStatusLabel(option.availability_status),
                stockLabel: this.optionStockLabel(option),
                restockExpectedLabel: option.restock_expected_at ? `예상 ${this.formatCheckedAt(option.restock_expected_at)}` : '',
                canRequestRestock,
                restockEnabled,
                restockButtonLabel: restockEnabled ? '알림 해제' : '재입고 알림'
            };
        });
    }

    private clusterStockSummary(listings: Listing[]): string {
        const options = listings.flatMap((listing) => listing.options || []);
        if (options.length === 0) return '옵션 재고 확인 필요';

        const purchasableCount = options.filter((option) => option.availability_status === 'in_stock' || option.availability_status === 'low_stock').length;
        const soldOutCount = options.filter((option) => option.availability_status === 'sold_out').length;
        const lowStockCount = options.filter((option) => option.availability_status === 'low_stock').length;
        const statusLabel = soldOutCount === options.length
            ? '전체 품절'
            : soldOutCount > 0
                ? '일부 사이즈 품절'
                : lowStockCount > 0
                    ? '일부 사이즈 재고 적음'
                    : '확인 기준 재고 있음';

        return `${statusLabel} · ${purchasableCount}/${options.length} 옵션 구매 가능`;
    }

    private clusterOptionShortageLabel(listings: Listing[]): string {
        const options = listings.flatMap((listing) => listing.options || []);
        if (options.length === 0) return '';

        const soldOutCount = options.filter((option) => option.availability_status === 'sold_out').length;
        if (soldOutCount === options.length) return '전체 품절';
        if (soldOutCount > 0) return '일부 사이즈 품절';

        const lowStockCount = options.filter((option) => option.availability_status === 'low_stock').length;
        if (lowStockCount > 0) return '일부 사이즈 재고 적음';

        return '확인 기준 재고 있음';
    }

    private isRestockAlertEnabled(clusterId: string, listingId: string, listingOptionId: string): boolean {
        const alert = this.findAlertForSaved(clusterId, listingId, listingOptionId);
        return Boolean(alert && alert.alert_type === 'restock' && alert.enabled);
    }

    private findListingOption(listingId: string, listingOptionId: string): ListingOption | null {
        const listing = this.findListing(listingId);
        return listing?.options.find((option) => option.id === listingOptionId) || null;
    }

    private savedOptionLabel(item: UserSavedItem): string {
        return [item.target_color, item.target_size].filter(Boolean).join(' · ') || '옵션 전체';
    }

    private currentOptionStatusLabel(item: UserSavedItem, option?: ListingOption | null): string {
        const resolvedOption = option || (item.listing_id && item.listing_option_id ? this.findListingOption(item.listing_id, item.listing_option_id) : null);
        if (!resolvedOption) return '옵션 재고 확인 필요';

        const stockLabel = this.optionStockLabel(resolvedOption);
        const expectedLabel = resolvedOption.restock_expected_at ? ` · 예상 ${this.formatCheckedAt(resolvedOption.restock_expected_at)}` : '';
        return `${this.optionStatusLabel(resolvedOption.availability_status)} · ${stockLabel}${expectedLabel}`;
    }

    private alertScopeLabel(alert: PriceAlert, listing: Listing | null, optionLabel: string): string {
        if (alert.alert_type === 'restock') {
            return `옵션 단위 · ${listing ? this.platformAndSellerLabel(listing) : '판매처 확인 필요'} · ${optionLabel}`;
        }

        return alert.listing_id ? `판매처 상품 · ${listing ? this.platformAndSellerLabel(listing) : '판매처 확인 필요'}` : '상품군 전체';
    }

    private inferAlertScope(alert: PriceAlert): AlertTrackingScope {
        if (alert.listing_option_id || alert.alert_type === 'restock') return 'listing_option';
        if (alert.listing_id) return 'listing';
        return 'product_cluster';
    }

    private optionAvailabilityLabel(listing: Listing): string {
        const options = listing.options || [];
        if (options.length === 0) return '옵션 재고 확인 필요';

        const availableCount = options.filter((option) => option.availability_status === 'in_stock' || option.availability_status === 'low_stock').length;
        const soldOutOption = options.find((option) => option.availability_status === 'sold_out');
        const lowStockOption = options.find((option) => option.availability_status === 'low_stock');
        const labels = [`확인 기준 ${availableCount}/${options.length} 옵션 구매 가능`];

        if (soldOutOption) labels.push(`${soldOutOption.color} ${soldOutOption.size} 품절`);
        if (lowStockOption) labels.push(`${lowStockOption.color} ${lowStockOption.size} 재고 적음`);

        return labels.join(' · ');
    }

    private optionShortageLabel(listing: Listing): string {
        const options = listing.options || [];
        if (options.length === 0) return '';

        const soldOutCount = options.filter((option) => option.availability_status === 'sold_out').length;
        if (soldOutCount === options.length) return '전체 품절';
        if (soldOutCount > 0) return '일부 사이즈 품절';

        const lowStockCount = options.filter((option) => option.availability_status === 'low_stock').length;
        if (lowStockCount > 0) return '일부 사이즈 재고 적음';

        return '';
    }

    private isComparableSnapshot(snapshot: PriceSnapshot): boolean {
        const stockComparable = snapshot.availability_status === 'in_stock' || snapshot.availability_status === 'low_stock';
        const freshnessStatus = snapshot.freshness?.status || snapshot.listing_freshness?.status || 'fresh';
        return stockComparable && (freshnessStatus === 'fresh' || freshnessStatus === 'stale');
    }

    private priceTrendEndDateKey(snapshots: PriceSnapshot[], listingIds: Set<string>): string {
        const times = snapshots.map((snapshot) => this.dateKeyToTime(this.dateKeyFromIso(snapshot.checked_at)));

        this.listings
            .filter((listing) => listingIds.has(listing.id))
            .forEach((listing) => times.push(this.dateKeyToTime(this.dateKeyFromIso(listing.last_checked_at))));

        if (times.length === 0) return this.dateKeyFromTime(Date.now());
        return this.dateKeyFromTime(Math.max(...times));
    }

    private createMissingTrendPoint(previousTime: number, nextTime: number): PriceTrendPoint {
        const missingTime = previousTime + Math.round((nextTime - previousTime) / 2);
        const dateKey = this.dateKeyFromTime(missingTime);

        return {
            dateKey,
            label: '확인 없음',
            price: null,
            sourceCount: 0,
            checkedAt: null,
            collectedAt: null,
            isMissing: true,
            isRecentLowest: false
        };
    }

    private markRecentLowestPoint(points: PriceTrendPoint[]): PriceTrendPoint[] {
        const lowestPoint = points
            .filter((point) => point.price !== null)
            .sort((a, b) => {
                const priceDiff = (a.price || 0) - (b.price || 0);
                if (priceDiff !== 0) return priceDiff;
                return this.dateKeyToTime(b.dateKey) - this.dateKeyToTime(a.dateKey);
            })[0];

        if (!lowestPoint) return points;

        return points.map((point) => ({
            ...point,
            isRecentLowest: point.dateKey === lowestPoint.dateKey && point.price === lowestPoint.price
        }));
    }

    private dateKeyFromIso(value: string): string {
        const matched = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (matched) return `${matched[1]}-${matched[2]}-${matched[3]}`;
        return this.dateKeyFromTime(new Date(value).getTime());
    }

    private dateKeyToTime(dateKey: string): number {
        return new Date(`${dateKey}T00:00:00+09:00`).getTime();
    }

    private dateKeyFromTime(time: number): string {
        const seoulDate = new Date(time + (9 * 60 * 60 * 1000));
        const year = seoulDate.getUTCFullYear();
        const month = `${seoulDate.getUTCMonth() + 1}`.padStart(2, '0');
        const day = `${seoulDate.getUTCDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private formatMonthDay(dateKey: string): string {
        const matched = dateKey.match(/^\d{4}-(\d{2})-(\d{2})$/);
        if (!matched) return dateKey;
        return `${matched[1]}.${matched[2]}`;
    }

    private compactWon(value: number): string {
        if (!Number.isFinite(value)) return '';
        return `${Math.round(value / 1000)}천`;
    }

    private chartColor(variableName: string, fallback: string): string {
        if (typeof window === 'undefined' || typeof document === 'undefined') return fallback;

        const host = this.priceTrendCanvas?.nativeElement.closest('.lookit-price-trend') || document.documentElement;
        const value = window.getComputedStyle(host).getPropertyValue(variableName).trim();
        return value || fallback;
    }

    private getPhotoInput(): HTMLInputElement | null {
        if (typeof document === 'undefined') return null;
        return document.getElementById('lookit-photo-input') as HTMLInputElement | null;
    }

    private configurePhotoInput(input: HTMLInputElement, source: UploadSource) {
        this.photoInputAccept = source === 'camera' ? this.cameraImageAccept : this.albumImageAccept;
        this.photoInputCapture = source === 'camera' ? 'environment' : null;
        input.accept = this.photoInputAccept;

        if (this.photoInputCapture) {
            input.setAttribute('capture', this.photoInputCapture);
        } else {
            input.removeAttribute('capture');
        }
    }

    private isSupportedImageFile(file: File): boolean {
        if (this.supportedImageTypes.includes(file.type)) return true;

        return /\.(jpe?g|png|webp)$/i.test(file.name);
    }

    private isApiImageUploadEnabled(): boolean {
        try {
            return imageUploadService.runtimeImageUploadConfig().adapter === 'api';
        } catch (error) {
            return false;
        }
    }

    private async uploadOriginalImageIfEnabled(file: File, previewAsset: PreparedPreviewImage): Promise<UploadOriginalImageResponse | null> {
        if (!this.isApiImageUploadEnabled()) return null;

        try {
            const uploadResult = await imageUploadService.uploadOriginalImage({
                file,
                imageSize: previewAsset.imageSize,
                mimeType: file.type || null,
                byteSize: file.size
            });

            if (!uploadResult) {
                this.uploadFailureMessage = this.apiUploadFallbackMessage();
                this.uploadState = '기기 미리보기로 계속';
            }

            return uploadResult;
        } catch (error) {
            this.uploadFailureMessage = this.apiUploadFallbackMessage();
            this.uploadState = '기기 미리보기로 계속';
            return null;
        }
    }

    private selectedUploadFileLabel(file: File): string {
        return `선택한 이미지 · ${this.formatUploadFileSize(file.size)}`;
    }

    private formatUploadFileSize(bytes: number): string {
        if (!Number.isFinite(bytes) || bytes <= 0) return '크기 확인 중';
        if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)}MB`;
        return `${Math.max(1, Math.round(bytes / 1024))}KB`;
    }

    private unsupportedUploadFileMessage(): string {
        return 'jpg, png, webp 이미지만 사용할 수 있습니다. HEIC/HEIF는 현재 지원하지 않습니다.';
    }

    private fileTooLargeMessage(): string {
        return '10MB 이하의 jpg, png, webp 이미지를 다시 선택해 주세요.';
    }

    private invalidUploadImageMessage(): string {
        return '이미지를 열 수 없습니다. 다른 사진을 다시 선택해 주세요.';
    }

    private apiUploadFallbackMessage(): string {
        return '서버 업로드를 완료하지 못했지만 기기 미리보기로 계속할 수 있습니다. 문제가 반복되면 다른 사진을 선택해 주세요.';
    }

    private previewPreparingState(file: File): string {
        return file.size > this.maxPreviewImageBytes ? '큰 이미지 최적화 중' : '이미지 준비 중';
    }

    private setLocalPreviewUrl(url: string) {
        this.revokePreviewUrl();
        this.previewImageUrl = url;
    }

    private revokePreviewUrl() {
        if (!this.previewImageUrl) return;
        URL.revokeObjectURL(this.previewImageUrl);
    }

    private async preparePreviewImageAsset(file: File): Promise<PreparedPreviewImage> {
        const sourceUrl = URL.createObjectURL(file);

        try {
            const decodedImage = await this.decodePreviewImage(file, sourceUrl);
            if (!decodedImage) {
                return {
                    url: sourceUrl,
                    fileSize: file.size,
                    imageSize: null,
                    optimized: false
                };
            }

            const sourceSize: ImageSize = {
                width: Math.max(1, Math.round(decodedImage.width)),
                height: Math.max(1, Math.round(decodedImage.height))
            };
            const shouldResize = (
                file.size > this.maxPreviewImageBytes ||
                Math.max(sourceSize.width, sourceSize.height) > this.maxPreviewImageLongEdge
            );

            if (!shouldResize) {
                decodedImage.close?.();
                return {
                    url: sourceUrl,
                    fileSize: file.size,
                    imageSize: sourceSize,
                    optimized: false
                };
            }

            const resizedImage = await this.createResizedPreviewImage(decodedImage, sourceSize);
            decodedImage.close?.();

            if (!resizedImage) {
                return {
                    url: sourceUrl,
                    fileSize: file.size,
                    imageSize: sourceSize,
                    optimized: false
                };
            }

            URL.revokeObjectURL(sourceUrl);
            return {
                url: URL.createObjectURL(resizedImage.blob),
                fileSize: resizedImage.blob.size,
                imageSize: resizedImage.imageSize,
                optimized: true
            };
        } catch (error) {
            return {
                url: sourceUrl,
                fileSize: file.size,
                imageSize: null,
                optimized: false
            };
        }
    }

    private async decodePreviewImage(file: File, objectUrl: string): Promise<DecodedPreviewImage | null> {
        if (typeof createImageBitmap === 'function') {
            try {
                const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
                return {
                    source: bitmap,
                    width: bitmap.width,
                    height: bitmap.height,
                    close: () => bitmap.close()
                };
            } catch (error) {
                // Fall through to the HTMLImageElement decoder for browsers without full ImageBitmap support.
            }
        }

        if (typeof Image === 'undefined') return null;

        return new Promise((resolve) => {
            const image = new Image();
            image.decoding = 'async';
            image.onload = () => {
                resolve({
                    source: image,
                    width: image.naturalWidth || image.width,
                    height: image.naturalHeight || image.height
                });
            };
            image.onerror = () => resolve(null);
            image.src = objectUrl;
        });
    }

    private async createResizedPreviewImage(decodedImage: DecodedPreviewImage, sourceSize: ImageSize): Promise<{ blob: Blob; imageSize: ImageSize } | null> {
        if (typeof document === 'undefined') return null;

        const longestSide = Math.max(sourceSize.width, sourceSize.height);
        const scale = Math.min(1, this.maxPreviewImageLongEdge / longestSide);
        const targetSize: ImageSize = {
            width: Math.max(1, Math.round(sourceSize.width * scale)),
            height: Math.max(1, Math.round(sourceSize.height * scale))
        };
        const canvas = document.createElement('canvas');
        canvas.width = targetSize.width;
        canvas.height = targetSize.height;

        const context = canvas.getContext('2d');
        if (!context) return null;

        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        context.drawImage(decodedImage.source, 0, 0, targetSize.width, targetSize.height);

        const blob = await new Promise<Blob | null>((resolve) => {
            canvas.toBlob(resolve, this.previewImageMimeType, this.previewImageQuality);
        });

        return blob ? { blob, imageSize: targetSize } : null;
    }

    private waitForNextFrame(): Promise<void> {
        if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            window.requestAnimationFrame(() => resolve());
        });
    }

    private resultClusterTypeFor(cluster: ProductCluster): ProductClusterType {
        return this.resultClusterTypes?.[cluster.id] || cluster.cluster_type;
    }

    private matchTypeForCluster(cluster: ProductCluster): ResultMatchType {
        const clusterType = this.resultClusterTypeFor(cluster);
        if (clusterType === 'brand_exact') return 'exact';
        if (clusterType === 'wholesale_same') return 'wholesale';
        return 'similar';
    }

    private matchLabelForCluster(cluster: ProductCluster): string {
        const clusterType = this.resultClusterTypeFor(cluster);
        if (clusterType === 'brand_exact') return '동일 상품 가능성 높음';
        if (clusterType === 'wholesale_same') return '같은 도매택 상품 추정';
        return '유사 디자인 상품';
    }

    private availabilityLabel(status: string): string {
        if (status === 'in_stock') return '확인 기준 재고 있음';
        if (status === 'low_stock') return '재고 적음';
        if (status === 'sold_out') return '품절';
        if (status === 'restock_expected') return '재입고 예정';
        return '재고 확인 필요';
    }

    private optionStatusLabel(status: AvailabilityStatus): string {
        if (status === 'in_stock') return '재고 있음';
        if (status === 'low_stock') return '재고 적음';
        if (status === 'sold_out') return '품절';
        if (status === 'restock_expected') return '재입고 예정';
        return '확인 필요';
    }

    private optionStockLabel(option: ListingOption | ListingOptionStockRow): string {
        const status = 'availability_status' in option ? option.availability_status : option.availabilityStatus;
        if (status === 'sold_out') return '0개';

        const stockCount = 'stock_count' in option ? option.stock_count : undefined;
        if (typeof stockCount === 'number') return `${stockCount}개`;
        return '수량 확인 필요';
    }

    private formatDiscount(value: number): string {
        if (!value) return '없음';
        return `-${this.formatWon(value)}`;
    }

    private formatShipping(value: number): string {
        if (!value) return '무료';
        return this.formatWon(value);
    }

    private formatSignedWon(value: number): string {
        if (!value) return '0원';
        return `${value > 0 ? '+' : '-'}${this.formatWon(Math.abs(value))}`;
    }

    private formatCheckedAt(value: string): string {
        const matched = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
        if (!matched) return value;
        return `${matched[2]}.${matched[3]} ${matched[4]}:${matched[5]}`;
    }

    private priceValue(value: number | string): number {
        if (typeof value === 'number') return value;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
    }

    private uploadStatus(source: UploadSource): string {
        if (source === 'camera') return '촬영 준비 중';
        if (source === 'capture') return '캡처 선택 중';
        return '앨범 선택 중';
    }

    private isTabId(tab: string): tab is LookitTabId {
        return tab === 'search' || tab === 'saved' || tab === 'alerts' || tab === 'profile';
    }

    private isSortMode(value: string): value is SortMode {
        return value === 'recommended' ||
            value === 'effective_price_asc' ||
            value === 'match_confidence_desc' ||
            value === 'recent_checked_desc';
    }
}

export default PageDashboardComponent;